import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./DomainSearchApp.css";
import { useLocation } from "react-router-dom";
import { AppFiltersSidebar } from "./components/AppFiltersSidebar";
import { AppResultsList } from "./components/AppReultsList";
import { AppSearchInterface } from "./components/AppSearchInterface";
import { AppSummary } from "./components/AppSummary";
import { AudienceFiltersSidebar } from "./components/AudienceFiltersSidebar";
import { AudienceResultsList } from "./components/AudienceResultsList";
import { AudienceSearchInterface } from "./components/AudienceSearchInterface";
import DomainSummary from "./components/DomainSummary";
import { FiltersSidebar } from "./components/FiltersSidebar";
import { PrebuiltAppSets } from "./components/PrebuiltAppSets";
import { PrebuiltSets } from "./components/PrebuiltSets";
import { PackageFiltersSidebar } from "./components/packageFilterSidebar";
import { PackageResultsList } from "./components/packageResultsList";
import { PackageSearchInterface } from "./components/packageSearchInterface";
import { ResultsList } from "./components/ResultsList";
import { ResultsTabs } from "./components/ResultsTabs";
import { SearchInterface } from "./components/SearchInterface";
import { SegmentsResultsList } from "./components/segmentResultList";
import { SegmentsSearchInterface } from "./components/segmentSearchInterface";
import { SegmentsFiltersSidebar } from "./components/segmentsFilterSidebar";
import { CategoryResultsList } from "./components/CategoryResultsList";
import { CategorySearchInterface } from "./components/CategorySearchInterface";
import { ToggleFiltersButton } from "./components/ToggleFiltersButton";
import { AudienceDetailModal } from "./components/tiledAudienceResults";
import { UploadModal } from "./components/UploadModal";
import { ViewControls } from "./components/ViewControls";
import { DEFAULT_QUERY_SCORE } from "./constants";
import { getDomainFilterDefaults } from "./context/envDefaults";
import { useAppFilters } from "./hooks/UseAppFilter";
import { useAppSearchInterface } from "./hooks/UseAppSearchInterface";
import { useAudienceSearchWithFilters } from "./hooks/useAudienceSearchWithFilters";
import { useCategorySearch } from "./hooks/useCategorySearch";
import { usePackageSearch } from "./hooks/usePackageSearch";
import { useSearchInterface } from "./hooks/useSearchInterface";
import { useSegments } from "./hooks/useSegments";
import appApiService from "./services/AppApiService";
import segmentsApiService from "./services/SegmentsApiService";
import {
	calculateRelevanceFromScore,
	getRecommendedThreshold,
	relevanceFromDistance,
} from "./utils";

// Fallback query thresholds, taken from the recommended-threshold sheet.
// Include terms use the default (Strong Match) tier; exclude terms keep the
// tighter Best Match distance they used before the model-2 migration.
const AUDIENCE_DEFAULT_THRESHOLD = getRecommendedThreshold("AUDIENCE");
const AUDIENCE_DEFAULT_RELEVANCE = Math.round(
	relevanceFromDistance(AUDIENCE_DEFAULT_THRESHOLD, "AUDIENCE"),
);
const PACKAGE_INCLUDE_THRESHOLD = getRecommendedThreshold(
	"PACKAGES",
	"strongMatch",
);
const PACKAGE_EXCLUDE_THRESHOLD = getRecommendedThreshold(
	"PACKAGES",
	"bestMatch",
);

const DomainSearchApp = ({ user, authToken }) => {
	const location = useLocation();

	// ✅ FIXED: Simplified initial audience filters - let the hook handle defaults
	const [initialAudienceFilters] = useState(() => {
		const basicFilters = {
			// Basic structure - the hook will fill in complete defaults
			sortBy: "compositeScore",
			sortDirection: "DESC",
			startIndex: 0,
			noOfResult: 200,
		};

		console.log("🔧 Initialized basic audience filters for combined hook");
		return basicFilters;
	});

	// =============================================
	// STATE DECLARATIONS
	// =============================================

	// Main Application State
	const [activeTab, setActiveTab] = useState("domains");
	const [showFilters, setShowFilters] = useState(true);
	const [selectedResults, setSelectedResults] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [activeView, setActiveView] = useState("performance");
	const [metricsDisplayMode, setMetricsDisplayMode] = useState("detailed");
	const [sortBy, setSortBy] = useState("impressions");
	const [sortOrder, setSortOrder] = useState("desc");
	const [isExporting, setIsExporting] = useState(false);
	const [pageSize, setPageSize] = useState(25);
	const [totalItems, setTotalItems] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [isAllSelected, setIsAllSelected] = useState(false);
	const [excludedItems, setExcludedItems] = useState([]);
	const [appError, setAppError] = useState(false);

	// ✅ ADD THESE STATE VARIABLES AT THE TOP WITH OTHER STATE
	const [llmSearchData, setLlmSearchData] = useState(null);
	const [isLlmResultsActive, setIsLlmResultsActive] = useState(false);

	// Upload State
	const [isUploading, setIsUploading] = useState(false);
	const [uploadedDomains, setUploadedDomains] = useState({
		includeList: [],
		excludeList: [],
	});
	const [showUploadModal, setShowUploadModal] = useState(false);

	// Domains & Keywords State
	const [domainResults, setDomainResults] = useState([]);
	const [keywordResults, setKeywordResults] = useState([]);
	const [selectedDomainSet, setSelectedDomainSet] = useState(null);
	const [domainSearchTerm, setDomainSearchTerm] = useState("");
	const [keywordSearchTerm, setKeywordSearchTerm] = useState("");
	const [domainSearchTerms, setDomainSearchTerms] = useState([]);
	const [persistedPrebuiltDomainSets, setPersistedPrebuiltDomainSets] =
		useState(null);
	const [domainSortBy, setDomainSortBy] = useState("totalImpressions");
	const [domainSortOrder, setDomainSortOrder] = useState("desc");

	// Apps State
	const [appData, setAppData] = useState([]);
	const [appResults, setAppResults] = useState([]);
	const [appSelectedResults, setAppSelectedResults] = useState([]);
	const [appSearchTerm, setAppSearchTerm] = useState("");
	const [appCurrentPage, setAppCurrentPage] = useState(1);
	const [appTotalPages, setAppTotalPages] = useState(1);
	const [appTotalItems, setAppTotalItems] = useState(0);
	const [selectedAppSet, setSelectedAppSet] = useState(null);
	const [excludedApps, setExcludedApps] = useState([]);
	const [appSearchTerms, setAppSearchTerms] = useState([]);
	const [persistedPrebuiltAppSets, setPersistedPrebuiltAppSets] =
		useState(null);
	const [prebuiltSetsLoading, setPrebuiltSetsLoading] = useState(false);
	const [prebuiltSetsTimestamp, setPrebuiltSetsTimestamp] = useState(null);
	const [appSortBy, setAppSortBy] = useState("totalImpressions");
	const [appSortOrder, setAppSortOrder] = useState("desc");

	// Segments State
	const [segmentsActiveSubTab, setSegmentsActiveSubTab] = useState("OA");
	const [segmentsResults, setSegmentsResults] = useState([]);
	const [segmentsSelectedResults, setSegmentsSelectedResults] = useState([]);
	const [segmentsSearchTerm, setSegmentsSearchTerm] = useState("");
	const [segmentsCurrentPage, setSegmentsCurrentPage] = useState(1);
	const [segmentsTotalPages, setSegmentsTotalPages] = useState(1);
	const [segmentsTotalItems, setSegmentsTotalItems] = useState(0);
	const [selectedSegmentsSet, setSelectedSegmentsSet] = useState("Relevance");
	const [segmentsSummary, setSegmentsSummary] = useState(null);
	const [segmentsViewMode, setSegmentsViewMode] = useState("grid");
	const [segmentsSortBy, setSegmentsSortBy] = useState("compositeScore");
	const [segmentsSortOrder, setSegmentsSortOrder] = useState("DESC");
	const [selectedSets, setSelectedSets] = useState([]);

	// Audience State declaration
	const [audienceSelectedResults, setAudienceSelectedResults] = useState([]);
	const [audienceCurrentPage, setAudienceCurrentPage] = useState(1);
	const [audienceTotalPages, setAudienceTotalPages] = useState(1);
	const [audienceTotalItems, setAudienceTotalItems] = useState(0);
	const [selectedAudienceSet, setSelectedAudienceSet] = useState(null);
	const [audienceSummary, setAudienceSummary] = useState(null);
	const [audienceSearchTerm, setAudienceSearchTerm] = useState("");
	const [error, setError] = useState("");
	const [selectedAudienceForModal, setSelectedAudienceForModal] =
		useState(null);
	const [isAudienceModalOpen, setIsAudienceModalOpen] = useState(false);
	const [audienceSortBy, setAudienceSortBy] = useState("compositeScore");
	const [audienceSortOrder, setAudienceSortOrder] = useState("DESC");

	//Package State declaration
	const [packageResults, setPackageResults] = useState([]);
	const [packageSelectedResults, setPackageSelectedResults] = useState([]);
	const [packageCurrentPage, setPackageCurrentPage] = useState(1);
	const [packageTotalPages, setPackageTotalPages] = useState(1);
	const [packageTotalItems, setPackageTotalItems] = useState(0);
	const [packageSummary, setPackageSummary] = useState(null);
	const [packageLlmResults, setPackageLlmResults] = useState(null);
	const [packageSortColumn, setPackageSortColumn] = useState(null);
	const [packageSortDirection, setPackageSortDirection] = useState("desc");
	const [packageCurationFilter, setPackageCurationFilter] = useState("all");

	// Search Results State
	const [localSearchResults, setLocalSearchResults] = useState({
		domains: [],
		keywords: [],
		totalRecords: 0,
		searchSummary: null,
	});

	const [filters, setFilters] = useState(getDomainFilterDefaults());

	const [expandedSections, setExpandedSections] = useState({
		mediaSubtype: false,
		deviceTypes: false,
		distributionChannel: false,
		videoPlacement: false,
		dateRange: false,
		geography: false,
		contentStyle: false,
		content_style_1: false,
		content_style_2: false,
		content_style_3: false,
		safetyTier: false,
		safety_tier_1: false,
		safety_tier_2: false,
		safety_tier_3: false,
		directPublishers: false,
		publishers: false,
		relevance: false,
		performance: false,
	});

	// ✅ FIXED: Simplified upload handlers
	const handleUploadAudienceList = useCallback((audienceIds) => {
		console.log(
			"📤 Audience include list uploaded:",
			audienceIds.length,
			"IDs",
		);
	}, []);

	const handleUploadBlockedAudienceList = useCallback((audienceIds) => {
		console.log(
			"🚫 Audience exclude list uploaded:",
			audienceIds.length,
			"IDs",
		);
	}, []);

	// =============================================
	// CUSTOM HOOKS
	// =============================================

	// Authentication request hook
	const makeAuthenticatedRequest = useCallback(
		async (url, options = {}) => {
			const headers = {
				"Content-Type": "application/json",
				...options.headers,
			};

			if (authToken) {
				headers["Authorization"] = `Bearer ${authToken}`;
			}

			return fetch(url, {
				...options,
				headers,
			});
		},
		[authToken],
	);

	// App Filters Hook
	const {
		filters: appFilters,
		expandedSections: appExpandedSections,
		filterOptions: appFilterOptions,
		updateFilter: handleAppUpdateFilter,
		toggleArrayFilter: handleAppToggleArrayFilter,
		toggleSection: handleAppToggleSection,
		clearAllFilters: handleAppClearAllFilters,
		retryFetchFilterOptions: handleAppRetryFilters,
		debugAppFilters: handleAppDebugFilters,
		activeFilterCount: appActiveFilterCount,
		filterSummary: appFilterSummary,
		apiFilters: appApiFilters,
		validation: appFilterValidation,
		hasActiveFilters: appHasActiveFilters,
		isLoading: appFiltersLoading,
		hasError: appFiltersHasError,
		error: appFiltersError,
		applyPreset: applyAppFilterPreset,
	} = useAppFilters({}, authToken);

	// Segments Unified Hook
	const {
		// Filter state and actions
		filters: segmentsFilters,
		updateFilter: handleSegmentsUpdateFilter,
		toggleArrayFilter: handleSegmentsToggleArrayFilter,
		clearAllFilters: handleSegmentsClearAllFilters,

		// UI state
		expandedSections: segmentsExpandedSections,
		toggleSection: handleSegmentsToggleSection,
		filterOptions: segmentsFilterOptions,
		isLoadingFilterOptions: segmentsFiltersLoading,
		hasFilterError: segmentsFiltersHasError,
		filterError: segmentsFiltersError,
		retryFetchFilterOptions: handleSegmentsRetryFilters,
		fetchFilterOptions,

		// Search state and actions
		searchQuery: segmentsSearchQuery,
		onSearchInputChange: handleSegmentsSearchInputChange,
		searchTerms: segmentsSearchTerms,
		addSearchTerm: handleSegmentsAddSearchTerm,
		removeSearchTerm: handleSegmentsRemoveSearchTerm,
		logicChain: segmentsLogicChain,
		onLogicChainChange: handleSegmentsLogicChainChange,
		onSearch: handleSegmentsSearch, // Main unified search function
		isSearching: segmentsIsSearching,
		searchResults: segmentsSearchResults,
		error: segmentsSearchError,
		resetSearch: handleSegmentsResetSearch,

		injectSearchResults: segmentsInjectSearchResults,
		searchWithOverride: handleSegmentsSearchWithOverride,

		// Computed values
		activeFilterCount: segmentsActiveFilterCount,
		hasActiveFilters: segmentsHasActiveFilters,
		filterSummary: segmentsFilterSummary,
		validation: segmentsFilterValidation,
		getSearchSummary: getSegmentsSearchSummary,
		hasResults: segmentsHasResults,
		hasError: segmentsHasError,
		resultsCount: segmentsResultsCount,
	} = useSegments();

	// ✅ FIXED: Combined Audience Hook - Single source of truth
	const {
		// Filter state
		filters: audienceFilters,
		expandedCategories: audienceExpandedCategories,
		availableOptions: audienceFilterOptions,
		filterValuesLoading: audienceFilterValuesLoading,
		filterValuesError: audienceFilterValuesError,
		loadFilterValues: audienceLoadFilterValues,
		updateFilter: audienceUpdateFilter,
		toggleArrayFilter: audienceToggleArrayFilter,
		clearAllFilters: audienceClearAllFilters,
		toggleCategory: audienceToggleCategory,
		activeFilterCount: audienceActiveFilterCount,
		hasActiveFilters: audienceHasActiveFilters,
		filtersContentRef: audienceFiltersContentRef,

		// Upload states
		uploadStates: audienceUploadStates,
		showUploadTooltip: audienceShowUploadTooltip,
		setShowUploadTooltip: audienceSetShowUploadTooltip,
		handleFileUpload: audienceHandleFileUpload,
		clearUploadedList: audienceClearUploadedList,

		// Search input states
		searchInputs: audienceSearchInputs,
		debouncedSearchInputs: audienceDebouncedSearchInputs,

		// Search state
		searchQuery: audienceSearchQuery,
		searchTerms: audienceSearchTerms,
		excludeTerms: audienceExcludeTerms,
		isSearching: audienceIsSearching,
		searchResults: audienceSearchResults,
		error: audienceSearchError,
		hasSearched: audienceHasSearched,

		// Search handlers
		onSearchInputChange: audienceOnSearchInputChange,
		addSearchTerm: audienceAddSearchTerm,
		addExcludeTerm: audienceAddExcludeTerm,
		removeSearchTerm: audienceRemoveSearchTerm,
		clearSearchTerms: audienceClearSearchTerms,

		// CRITICAL: The main search trigger function (now available early in hook)
		triggerSearch: audienceTriggerSearch,

		// NEW: LLM support functions from the fixed hook
		injectSearchResults: audienceInjectSearchResults,
		triggerSearchWithOverride: audienceTriggerSearchWithOverride,

		// Additional handlers
		handleSearchInputChange: audienceHandleSearchInputChange,
		handleSearchInputEnter: audienceHandleSearchInputEnter,
		handleRemoveSearchTag: audienceHandleRemoveSearchTag,

		// Computed values
		hasResults: audienceHasResults,
		hasError: audienceHasError,
		showEmptyState: audienceShowEmptyState,
	} = useAudienceSearchWithFilters(initialAudienceFilters, {
		onFiltersChange: (updatedFilters) => {
			console.log("Audience filters changed:", updatedFilters);
		},
		onUploadAudienceList: handleUploadAudienceList,
		onUploadBlockedAudienceList: handleUploadBlockedAudienceList,
	});

	// Category Search Hook
	const categorySearch = useCategorySearch();

	// Search Interface Hooks (for other tabs)
	const domainSearchHook = useSearchInterface(filters, "domains", filters);
	const appSearchHook = useAppSearchInterface(appApiFilters, appApiFilters);
	// Current Search Hook (depends on active tab)
	const currentSearchHook =
		activeTab === "app" ? appSearchHook : domainSearchHook;

	// Extract search interface properties
	const {
		searchQuery,
		refinedQuery,
		isSearching,
		showSuggestions,
		searchSuggestions,
		searchResults,
		error: searchError,
		performanceMetric,
		minReachThreshold,
		onSearchInputChange,
		prebuiltDomainSets,
		onRefinedQueryChange,
		onSearch,
		onSuggestionSelect,
		onClearRefinement,
		onHideSuggestions,
		onPerformanceMetricChange,
		onMinReachChange,
		resetSearch,
		getSearchSummary,
		hasResults,
		hasError,
	} = currentSearchHook;

	const { prebuiltAppSets: hookPrebuiltAppSets } = appSearchHook;
	const { prebuiltDomainSets: hookPrebuiltDomainSets } = domainSearchHook;

	const packageSearchHook = usePackageSearch();

	// Extract package search properties
	const {
		// Filter state
		filters: packageFilters,
		expandedSections: packageExpandedSections,
		filterOptions: packageFilterOptions,
		isLoadingFilters: packageIsLoadingFilters,
		filterError: packageFilterError,
		updateFilter: handlePackageUpdateFilter,
		toggleArrayFilter: handlePackageToggleArrayFilter,
		toggleSection: handlePackageToggleSection,
		clearAllFilters: handlePackageClearAllFilters,
		loadFilterOptions: handlePackageLoadFilterOptions,

		// Search state
		searchQuery: packageSearchQuery,
		searchTerms: packageSearchTerms,
		excludeTerms: packageExcludeTerms,
		isSearching: packageIsSearching,
		searchResults: packageSearchResults,
		searchError: packageSearchError,
		hasSearched: packageHasSearched,

		// Search handlers
		onSearchInputChange: handlePackageSearchInputChange,
		addSearchTerm: handlePackageAddSearchTerm,
		removeSearchTerm: handlePackageRemoveSearchTerm,
		clearSearchTerms: handlePackageClearSearchTerms,
		triggerSearch: handlePackageTriggerSearch,
		resetSearch: handlePackageResetSearch,

		// LLM integration
		injectSearchResults: packageInjectSearchResults,
		triggerSearchWithOverride: packageTriggerSearchWithOverride,

		// Computed values
		activeFilterCount: packageActiveFilterCount,
		hasActiveFilters: packageHasActiveFilters,
		hasResults: packageHasResults,
		hasError: packageHasError,
		resultsCount: packageResultsCount,
		excludedPackageIds, // Array of excluded package IDs
		excludedCount,
		hasExcludedResults,
	} = packageSearchHook;

	// =============================================
	// GENERAL HANDLER FUNCTIONS
	// =============================================

	const handleTabChange = useCallback((tab) => {
		try {
			setActiveTab(tab);
			setCurrentPage(1);
			setSelectedResults([]);
			setIsAllSelected(false);
			if (tab === "app") {
				setAppCurrentPage(1);
			} else if (tab === "segments") {
				setSegmentsCurrentPage(1);
				setSegmentsSelectedResults([]);
			} else if (tab === "audience") {
				setAudienceCurrentPage(1);
				setAudienceSelectedResults([]);
			} else if (tab === "packages") {
				setPackageCurrentPage(1);
				setPackageSelectedResults([]);
			}
		} catch (error) {
			console.error("Error in handleTabChange:", error);
		}
	}, []);

	const handlePageSizeChange = (newSize) => {
		setPageSize(newSize);
		setCurrentPage(1);
	};

	const handlePageChange = useCallback((page) => {
		try {
			setCurrentPage(page);
			const resultsContainer = document.querySelector(".results-container");
			if (resultsContainer) {
				resultsContainer.scrollTop = 0;
			}
		} catch (error) {
			console.error("Error in handlePageChange:", error);
		}
	}, []);

	const handleResultSelect = useCallback((id) => {
		try {
			setSelectedResults((prev) =>
				prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
			);
		} catch (error) {
			// Silent error handling
		}
	}, []);

	const handleExcludeSelected = (selectedIds) => {
		setExcludedItems((prev) => [...prev, ...selectedIds]);
		setSelectedResults([]);
	};

	const handleAddToSearch = (selectedItems) => {
		const keywords = selectedItems.map((item) => item.keyword);
		setSelectedResults([]);
	};

	// =============================================
	// DOMAINS & KEYWORDS HANDLER FUNCTIONS
	// =============================================

	const handleDomainSearchChange = (searchValue) => {
		if (activeTab === "domains") {
			setDomainSearchTerm(searchValue);
		} else if (activeTab === "keywords") {
			setKeywordSearchTerm(searchValue);
		}
		setCurrentPage(1);
	};

	// =============================================
	// APPS HANDLER FUNCTIONS
	// =============================================

	const handleAppResultSelect = (appId) => {
		setAppSelectedResults((prev) =>
			prev.includes(appId) ? prev.filter((r) => r !== appId) : [...prev, appId],
		);
	};

	const handleAppSelectAll = (selectAll) => {
		if (selectAll) {
			const currentPageAppIds = getAppCurrentPageData().map((item) => item.id);
			setAppSelectedResults((prev) => [
				...new Set([...prev, ...currentPageAppIds]),
			]);
		} else {
			const currentPageAppIds = getAppCurrentPageData().map((item) => item.id);
			setAppSelectedResults((prev) =>
				prev.filter((id) => !currentPageAppIds.includes(id)),
			);
		}
	};

	const handleAppSearchChange = (searchValue) => {
		setAppSearchTerm(searchValue);
		setAppCurrentPage(1);
	};

	const handleAppPageChange = (page) => {
		setAppCurrentPage(page);
	};

	const handleAppFiltersClose = useCallback(() => {
		setShowFilters(false);
	}, []);

	// =============================================
	// SEGMENTS HANDLER FUNCTIONS
	// =============================================

	const handleSegmentsResultSelect = (segmentId) => {
		setSegmentsSelectedResults((prev) =>
			prev.includes(segmentId)
				? prev.filter((r) => r !== segmentId)
				: [...prev, segmentId],
		);
	};

	const handleSegmentsSelectAll = (selectAll) => {
		if (selectAll) {
			const currentPageSegmentIds = getSegmentsCurrentPageData().map(
				(item) => item.segmentVector.segmentId,
			);
			setSegmentsSelectedResults((prev) => [
				...new Set([...prev, ...currentPageSegmentIds]),
			]);
		} else {
			const currentPageSegmentIds = getSegmentsCurrentPageData().map(
				(item) => item.segmentVector.segmentId,
			);
			setSegmentsSelectedResults((prev) =>
				prev.filter((id) => !currentPageSegmentIds.includes(id)),
			);
		}
	};

	const handleSegmentsSearchChange = (searchValue) => {
		setSegmentsSearchTerm(searchValue);
		setSegmentsCurrentPage(1);
	};

	const handleSegmentsPageChange = (page) => {
		setSegmentsCurrentPage(page);
	};

	const handleSegmentsViewModeChange = (mode) => {
		setSegmentsViewMode(mode);
	};

	const handleSegmentsSortChange = (sortBy, sortOrder) => {
		setSegmentsSortBy(sortBy);
		setSegmentsSortOrder(sortOrder);
	};

	// Add set selection handlers:
	const handleSetSelect = (setKey) => {
		setSelectedSets((prev) =>
			prev.includes(setKey)
				? prev.filter((key) => key !== setKey)
				: [...prev, setKey],
		);
	};

	const handleSetAdd = (setKeys, setData) => {
		if (Array.isArray(setKeys)) {
			// Multiple sets selected
			console.log("Adding multiple sets to campaign:", setKeys);
			// Your campaign addition logic here
		} else {
			// Single set
			console.log("Adding set to campaign:", setKeys, setData);
			// Your campaign addition logic here
		}

		// Clear selections after adding
		setSelectedSets([]);
	};

	// =============================================
	// ✅ FIXED: SIMPLIFIED AUDIENCE HANDLER FUNCTIONS
	// =============================================
	const filteredSegmentsDataForUI = useMemo(() => {
		if (!segmentsSearchResults?.data || !segmentsSearchTerms) {
			return segmentsSearchResults?.data || {};
		}

		// Get only include terms
		const includeTerms = segmentsSearchTerms.filter(
			(term) => term.type === "include",
		);
		const includeTermTexts = includeTerms.map((term) =>
			term.text.toLowerCase(),
		);

		console.log("🔍 Filtering segments data for UI:", {
			totalResults: Object.keys(segmentsSearchResults.data).length,
			includeTerms: includeTermTexts,
			allSearchTerms: segmentsSearchTerms.map((t) => ({
				text: t.text,
				type: t.type,
			})),
		});

		const filtered = {};
		Object.entries(segmentsSearchResults.data).forEach(([key, data]) => {
			// Always include demographic data
			if (key.startsWith("Demographic_")) {
				filtered[key] = data;
				return;
			}

			if (key.toLowerCase() === "default") {
				filtered[key] = data;
				console.log(`✅ Including "${key}" in UI (default results)`);
				return;
			}

			// Only include data if the key corresponds to an include term
			const isIncludeTerm = includeTerms.some(
				(term) =>
					key.toLowerCase() === term.text.toLowerCase() ||
					key.toLowerCase() === `searchterm_${term.text.toLowerCase()}` ||
					key.toLowerCase().includes(term.text.toLowerCase()),
			);

			if (isIncludeTerm) {
				filtered[key] = data;
				console.log(`✅ Including "${key}" in UI (matches include term)`);
			} else {
				console.log(`❌ Excluding "${key}" from UI (not an include term)`);
			}
		});

		return filtered;
	}, [segmentsSearchResults?.data, segmentsSearchTerms]);

	const handleAudienceSearchChange = (searchValue) => {
		console.log("🔍 Audience search change:", searchValue);
		setAudienceSearchTerm(searchValue);
		setAudienceCurrentPage(1); // Use audience-specific page state
	};

	const handleAudienceResultSelect = (audienceId) => {
		setAudienceSelectedResults((prev) =>
			prev.includes(audienceId)
				? prev.filter((r) => r !== audienceId)
				: [...prev, audienceId],
		);
	};

	const handleAudienceSelectAll = (selectAll) => {
		if (selectAll) {
			const currentPageAudienceIds = getAudienceCurrentPageData()
				.map(
					(item) =>
						item.audienceVector?.audienceId || item.audienceId || item.id,
				)
				.filter(Boolean);
			setAudienceSelectedResults((prev) => [
				...new Set([...prev, ...currentPageAudienceIds]),
			]);
		} else {
			const currentPageAudienceIds = getAudienceCurrentPageData()
				.map(
					(item) =>
						item.audienceVector?.audienceId || item.audienceId || item.id,
				)
				.filter(Boolean);
			setAudienceSelectedResults((prev) =>
				prev.filter((id) => !currentPageAudienceIds.includes(id)),
			);
		}
	};

	const handleAudienceExcludeSelected = (selectedIds) => {
		const excludedAudiences = [...(excludedItems || []), ...selectedIds];
		setExcludedItems(excludedAudiences);
		setAudienceSelectedResults([]);
	};

	const handleAudiencePageChange = (page) => {
		setAudienceCurrentPage(page);
	};

	// ✅ FIXED: Use the combined hook's data directly
	const getAudienceCurrentPageData = useCallback(() => {
		console.log(
			"🔍 getAudienceCurrentPageData - using combined hook results:",
			{
				hasSearchResults: !!audienceSearchResults,
				searchResultsSuccess: audienceSearchResults?.success,
				audiencesLength: audienceSearchResults?.audiences?.length || 0,
			},
		);

		if (!audienceSearchResults?.success || !audienceSearchResults?.audiences) {
			console.warn("⚠️ No valid audience search results");
			return [];
		}

		return audienceSearchResults.audiences || [];
	}, [audienceSearchResults]);

	const calculateAudienceSummary = (data) => {
		if (!data || data.length === 0) return null;

		console.log("📊 Calculating audience summary for:", data.length, "items");

		try {
			const summary = {
				totalCount: data.length,
				totalUniqueUsers: data.reduce((sum, item) => {
					const uniqueUsers =
						item.audienceMetrics?.uniqueUsers || item.metrics?.uniqueUsers || 0;
					return sum + uniqueUsers;
				}, 0),
				avgCompositeScore:
					(data.reduce((sum, item) => sum + (item.compositeScore || 0), 0) *
						100) /
					data.length,
				totalSpend: data.reduce((sum, item) => {
					const spend =
						item.audienceMetrics?.advertiserSpend ||
						item.metrics?.advertiserSpend ||
						0;
					return sum + spend;
				}, 0),
				totalImpressions: data.reduce((sum, item) => {
					const impressions =
						item.audienceMetrics?.weightedImpressions ||
						item.metrics?.weightedImpressions ||
						0;
					return sum + impressions;
				}, 0),
				regionsCount:
					new Set(
						data
							.map((item) => item.audienceVector?.region || item.region)
							.filter(Boolean),
					).size || 1,
				activeAudiences: data.filter(
					(item) =>
						(item.audienceVector?.status || item.status) === "PROCESSED",
				).length,
				avgEcpm:
					data.reduce((sum, item) => {
						const ecpm =
							item.audienceMetrics?.avgEcpm || item.metrics?.avgEcpm || 0;
						return sum + ecpm;
					}, 0) / data.length,
			};

			console.log("✅ Audience summary calculated:", summary);
			return summary;
		} catch (error) {
			console.error("❌ Error calculating audience summary:", error);
			return null;
		}
	};

	const handleOpenAudienceModal = useCallback((audience) => {
		console.log("🎯 Opening audience modal for:", audience);
		setSelectedAudienceForModal(audience);
		setIsAudienceModalOpen(true);
	}, []);

	const handleCloseAudienceModal = useCallback(() => {
		setIsAudienceModalOpen(false);
		setSelectedAudienceForModal(null);
	}, []);

	// =============================================
	// Package HANDLER FUNCTIONS
	// =============================================

	const handlePackageResultSelect = (packageId) => {
		setPackageSelectedResults((prev) =>
			prev.includes(packageId)
				? prev.filter((r) => r !== packageId)
				: [...prev, packageId],
		);
	};

	const handlePackageCurationFilterChange = useCallback((filter) => {
		console.log("📊 Package curation filter changed:", filter);
		setPackageCurationFilter(filter);
		setPackageCurrentPage(1); // Reset to first page when filter changes

		// Also clear sorting when filter changes
		setPackageSortColumn(null);
		setPackageSortDirection("desc");
	}, []);

	// Add this handler in your Package HANDLER FUNCTIONS section
	const handlePackageSortChange = useCallback((column, direction) => {
		console.log("📊 Package sort changed:", { column, direction });
		setPackageSortColumn(column);
		setPackageSortDirection(direction);
		setPackageCurrentPage(1); // Reset to first page when sorting changes
	}, []);

	// Add this new function in your Package HANDLER FUNCTIONS section
	const getSortedPackageData = useCallback(
		(data, sortColumn, sortDirection) => {
			if (!sortColumn || !Array.isArray(data)) return data;

			return [...data].sort((a, b) => {
				const packageVectorA = a.packageVector || {};
				const packageVectorB = b.packageVector || {};
				const packageMetricsA = a.packageMetrics || {};
				const packageMetricsB = b.packageMetrics || {};

				let aValue, bValue;

				switch (sortColumn) {
					case "packageName":
						aValue = packageVectorA.packageName?.toLowerCase() || "";
						bValue = packageVectorB.packageName?.toLowerCase() || "";
						break;
					case "category":
						aValue = packageVectorA.packageCategoryL1?.toLowerCase() || "";
						bValue = packageVectorB.packageCategoryL1?.toLowerCase() || "";
						break;
					case "vertical":
						aValue = packageVectorA.primaryVertical?.toLowerCase() || "";
						bValue = packageVectorB.primaryVertical?.toLowerCase() || "";
						break;
					case "status":
						aValue = packageVectorA.dealStatus?.toLowerCase() || "";
						bValue = packageVectorB.dealStatus?.toLowerCase() || "";
						break;
					case "relevance":
						// Calculate relevance score from the score field
						aValue = calculateRelevanceScore(a);
						bValue = calculateRelevanceScore(b);
						break;
					case "compositeScore":
						aValue = a.compositeScore || 0;
						bValue = b.compositeScore || 0;
						break;
					case "dealPrice":
						aValue = parseFloat(packageVectorA.dealPrice) || 0;
						bValue = parseFloat(packageVectorB.dealPrice) || 0;
						break;
					case "ctr":
						aValue = parseFloat(packageMetricsA.clickThroughRate) || 0;
						bValue = parseFloat(packageMetricsB.clickThroughRate) || 0;
						break;
					case "viewability":
						aValue = parseFloat(packageMetricsA.viewability) || 0;
						bValue = parseFloat(packageMetricsB.viewability) || 0;
						break;
					case "impressions":
						aValue = parseInt(packageMetricsA.impressions) || 0;
						bValue = parseInt(packageMetricsB.impressions) || 0;
						break;
					case "fills":
						aValue = parseInt(packageMetricsA.fills) || 0;
						bValue = parseInt(packageMetricsB.fills) || 0;
						break;
					case "clicks":
						aValue = parseInt(packageMetricsA.cprClicks) || 0;
						bValue = parseInt(packageMetricsB.cprClicks) || 0;
						break;
					case "bidCpm":
						aValue = parseFloat(packageMetricsA.bidCpm) || 0;
						bValue = parseFloat(packageMetricsB.bidCpm) || 0;
						break;
					case "winRate":
						aValue = parseFloat(packageMetricsA.winRate) || 0;
						bValue = parseFloat(packageMetricsB.winRate) || 0;
						break;
					case "spend":
						aValue = parseFloat(packageMetricsA.totalSpendInUSD) || 0;
						bValue = parseFloat(packageMetricsB.totalSpendInUSD) || 0;
						break;
					case "requests":
						aValue = parseInt(packageMetricsA.totalMarketRequests) || 0;
						bValue = parseInt(packageMetricsB.totalMarketRequests) || 0;
						break;
					case "curated":
						aValue = packageVectorA.isCurated === true ? 1 : 0;
						bValue = packageVectorB.isCurated === true ? 1 : 0;
						break;
					default:
						return 0;
				}

				let comparison = 0;
				if (typeof aValue === "string" && typeof bValue === "string") {
					comparison = aValue.localeCompare(bValue);
				} else {
					comparison = aValue - bValue;
				}

				return sortDirection === "desc" ? -comparison : comparison;
			});
		},
		[],
	);

	// Add the relevance score calculation function
	const calculateRelevanceScore = useCallback((pkg) => {
		if (pkg.relevanceScore && typeof pkg.relevanceScore === "number") {
			return pkg.relevanceScore;
		}

		if (typeof pkg.score === "number") {
			// Packages sigmoid relevance curve (see constants.js)
			return relevanceFromDistance(parseFloat(pkg.score), "PACKAGES");
		}

		return 0;
	}, []);

	const getPackageDataSource = useCallback(() => {
		console.log("📦 getPackageDataSource - checking all sources...");

		// 1. Check packageResults state first (for LLM)
		if (packageResults && packageResults.length > 0) {
			console.log("✅ Using packageResults state:", packageResults.length);
			return packageResults;
		}

		// 2. Check LLM stored results
		if (packageLlmResults?.packages && packageLlmResults.packages.length > 0) {
			console.log(
				"✅ Using LLM stored packages:",
				packageLlmResults.packages.length,
			);
			return packageLlmResults.packages;
		}

		// 3. Check hook's packageSearchResults (from usePackageSearch hook)
		if (
			packageSearchResults?.packages &&
			packageSearchResults.packages.length > 0
		) {
			console.log(
				"✅ Using hook packageSearchResults.packages:",
				packageSearchResults.packages.length,
			);
			return packageSearchResults.packages;
		}

		if (packageSearchResults?.queryResults) {
			const allPackages = [];
			Object.values(packageSearchResults.queryResults).forEach(
				(queryResult) => {
					if (queryResult?.data && Array.isArray(queryResult.data)) {
						allPackages.push(...queryResult.data);
					}
				},
			);
			if (allPackages.length > 0) {
				console.log("✅ Extracted from hook queryResults:", allPackages.length);
				return allPackages;
			}
		}

		console.log("📊 DATA SOURCE DEBUG:", {
			packageResults: packageResults?.length || 0,
			llmResults: packageLlmResults?.packages?.length || 0,
			hookResults: packageSearchResults?.packages?.length || 0,
			hookQueryResults: packageSearchResults?.queryResults
				? Object.keys(packageSearchResults.queryResults).length
				: 0,
		});

		console.log("❌ No packages found in any data source");
		return [];
	}, [packageResults, packageLlmResults, packageSearchResults]);

	const aggregatePackagesByDealId = useCallback((packages) => {
		const packageMap = new Map();

		packages.forEach((pkg) => {
			const packageId = pkg.packageVector?.packageId || pkg.packageId;

			if (!packageMap.has(packageId)) {
				packageMap.set(packageId, {
					...pkg,
					dealIds: [pkg.packageVector?.dealId].filter(Boolean),
					dealCount: 1,
				});
			} else {
				const existing = packageMap.get(packageId);
				const dealId = pkg.packageVector?.dealId;
				if (dealId && !existing.dealIds.includes(dealId)) {
					existing.dealIds.push(dealId);
				}
				existing.dealCount++;
			}
		});

		return Array.from(packageMap.values());
	}, []);

	const getPackageFilteredDataCount = useCallback(() => {
		const allPackages = getPackageDataSource();
		let aggregatedPackages = aggregatePackagesByDealId(allPackages);

		// Apply curation filtering
		if (packageCurationFilter !== "all") {
			switch (packageCurationFilter) {
				case "curated":
					aggregatedPackages = aggregatedPackages.filter(
						(pkg) => pkg.packageVector?.isCurated === true,
					);
					break;
				case "non-curated":
					aggregatedPackages = aggregatedPackages.filter(
						(pkg) => pkg.packageVector?.isCurated !== true,
					);
					break;
			}
		}

		// Apply search filtering
		if (packageSearchQuery && packageSearchQuery.trim()) {
			const searchLower = packageSearchQuery.toLowerCase();
			aggregatedPackages = aggregatedPackages.filter((item) => {
				const packageVector = item.packageVector || {};
				return (
					packageVector.dealName?.toLowerCase().includes(searchLower) ||
					packageVector.packageName?.toLowerCase().includes(searchLower) ||
					packageVector.audienceName?.toLowerCase().includes(searchLower) ||
					(Array.isArray(packageVector.primaryVertical)
						? packageVector.primaryVertical.some((v) =>
								v.toLowerCase().includes(searchLower),
							)
						: packageVector.primaryVertical
								?.toLowerCase()
								.includes(searchLower)) ||
					(Array.isArray(packageVector.packageCategoryL1)
						? packageVector.packageCategoryL1.some((c) =>
								c.toLowerCase().includes(searchLower),
							)
						: packageVector.packageCategoryL1
								?.toLowerCase()
								.includes(searchLower))
				);
			});
		}

		return aggregatedPackages.length;
	}, [
		getPackageDataSource,
		aggregatePackagesByDealId,
		packageCurationFilter,
		packageSearchQuery,
	]);

	const getPackageCurationCounts = useCallback(() => {
		const allPackages = getPackageDataSource();
		const aggregatedPackages = aggregatePackagesByDealId(allPackages);

		const all = aggregatedPackages.length;
		const curated = aggregatedPackages.filter(
			(pkg) => pkg.packageVector?.isCurated === true,
		).length;
		const nonCurated = aggregatedPackages.filter(
			(pkg) => pkg.packageVector?.isCurated !== true,
		).length;

		return {
			all,
			curated,
			nonCurated,
		};
	}, [getPackageDataSource, aggregatePackagesByDealId]);

	const getPackageTotalPages = useCallback(() => {
		const totalItems = getPackageFilteredDataCount();
		const pages = Math.ceil(totalItems / pageSize) || 1;

		console.log("📄 Total pages calculation:", {
			totalItems,
			pageSize,
			totalPages: pages,
		});

		return pages;
	}, [getPackageFilteredDataCount, pageSize]);

	const handlePackageSelectAll = (selectAll) => {
		if (selectAll) {
			const currentPagePackageIds = getPackageCurrentPageData()
				.map((item) => item.packageId || item.id || item.packageVector?.dealId)
				.filter(Boolean);
			setPackageSelectedResults((prev) => [
				...new Set([...prev, ...currentPagePackageIds]),
			]);
		} else {
			const currentPagePackageIds = getPackageCurrentPageData()
				.map((item) => item.packageId || item.id || item.packageVector?.dealId)
				.filter(Boolean);
			setPackageSelectedResults((prev) =>
				prev.filter((id) => !currentPagePackageIds.includes(id)),
			);
		}
	};

	const handlePackagePageChange = (page) => {
		setPackageCurrentPage(page);
	};

	const getPackageCurrentPageData = useCallback(() => {
		console.log("📋 === PACKAGE getCurrentPageData DEBUG START ===");

		let allPackages = getPackageDataSource();

		if (allPackages.length === 0) {
			return [];
		}

		// Apply curation filtering FIRST
		if (packageCurationFilter !== "all") {
			const originalCount = allPackages.length;

			switch (packageCurationFilter) {
				case "curated":
					allPackages = allPackages.filter(
						(pkg) => pkg.packageVector?.isCurated === true,
					);
					break;
				case "non-curated":
					allPackages = allPackages.filter(
						(pkg) => pkg.packageVector?.isCurated !== true,
					);
					break;
				default:
					// 'all' - no filtering
					break;
			}

			console.log("🔍 After curation filtering:", {
				originalCount,
				filteredCount: allPackages.length,
				filter: packageCurationFilter,
			});
		}

		// Apply search filtering SECOND
		if (packageSearchQuery && packageSearchQuery.trim()) {
			const searchLower = packageSearchQuery.toLowerCase();
			const originalCount = allPackages.length;

			allPackages = allPackages.filter((item) => {
				const packageVector = item.packageVector || {};
				return (
					packageVector.dealName?.toLowerCase().includes(searchLower) ||
					packageVector.packageName?.toLowerCase().includes(searchLower) ||
					packageVector.audienceName?.toLowerCase().includes(searchLower) ||
					(Array.isArray(packageVector.primaryVertical)
						? packageVector.primaryVertical.some((v) =>
								v.toLowerCase().includes(searchLower),
							)
						: packageVector.primaryVertical
								?.toLowerCase()
								.includes(searchLower)) ||
					(Array.isArray(packageVector.packageCategoryL1)
						? packageVector.packageCategoryL1.some((c) =>
								c.toLowerCase().includes(searchLower),
							)
						: packageVector.packageCategoryL1
								?.toLowerCase()
								.includes(searchLower))
				);
			});

			console.log("🔍 After search filtering:", {
				originalCount,
				filteredCount: allPackages.length,
				searchQuery: packageSearchQuery,
			});
		}

		// Apply sorting THIRD
		if (packageSortColumn) {
			allPackages = getSortedPackageData(
				allPackages,
				packageSortColumn,
				packageSortDirection,
			);
			console.log(
				"🔄 Applied sorting by:",
				packageSortColumn,
				packageSortDirection,
			);
		}

		// Apply pagination LAST
		const startIndex = (packageCurrentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		const paginatedData = allPackages.slice(startIndex, endIndex);

		console.log("📊 Final Result:", {
			paginatedItemsCount: paginatedData.length,
			startIndex,
			endIndex,
			totalAfterAllFilters: allPackages.length,
		});

		return paginatedData;
	}, [
		getPackageDataSource,
		packageCurationFilter,
		packageSearchQuery,
		packageCurrentPage,
		pageSize,
		packageSortColumn,
		packageSortDirection,
		getSortedPackageData,
	]);

	// const calculatePackageSummary = (data) => {
	//     if (!data || data.length === 0) return null;

	//     try {
	//         const summary = {
	//             totalCount: data.length,
	//             totalImpressions: data.reduce((sum, item) => {
	//                 const impressions = item.packageMetrics?.impressions || 0;
	//                 return sum + impressions;
	//             }, 0),
	//             totalDealVolume: data.reduce((sum, item) => {
	//                 const volume = item.packageMetrics?.dealVolume || 0;
	//                 return sum + volume;
	//             }, 0),
	//             avgCtr: data.reduce((sum, item) => {
	//                 const ctr = item.packageMetrics?.clickThroughRate || 0;
	//                 return sum + ctr;
	//             }, 0) / data.length,
	//             avgViewability: data.reduce((sum, item) => {
	//                 const viewability = item.packageMetrics?.viewability || 0;
	//                 return sum + viewability;
	//             }, 0) / data.length,
	//             activeDeals: data.filter(item =>
	//                 item.packageVector?.dealStatus === 'active'
	//             ).length,
	//             countriesCount: new Set(data.flatMap(item =>
	//                 item.packageVector?.country || []
	//             )).size,
	//             categoriesCount: new Set(data.flatMap(item =>
	//                 item.packageVector?.packageCategoryL1 || []
	//             )).size
	//         };

	//         return summary;
	//     } catch (error) {
	//         console.error('Error calculating package summary:', error);
	//         return null;
	//     }
	// }, []);

	// Package export function
	const exportPackagesToCSV = useCallback(
		(exportType) => {
			try {
				setIsExporting(exportType);
				console.log("Starting package export:", exportType);

				// Use your existing data source function
				let fullPackageData = getPackageDataSource();

				if (!fullPackageData || fullPackageData.length === 0) {
					console.warn("No package data available for export");
					alert(
						"No package data available for export. Please search for packages first.",
					);
					setIsExporting(false);
					return;
				}

				// Apply exclusion filter if excludedPackageIds exists
				if (excludedPackageIds && excludedPackageIds.length > 0) {
					const originalLength = fullPackageData.length;
					fullPackageData = fullPackageData.filter((pkg) => {
						const packageId =
							pkg.packageVector?.packageId || pkg.packageId || pkg.id;
						return !excludedPackageIds.includes(packageId);
					});
					console.log(
						`Filtered ${originalLength} -> ${fullPackageData.length} packages after exclusions`,
					);
				}

				if (fullPackageData.length === 0) {
					console.warn("No package data available for export after filtering");
					alert("No package data available for export after applying filters.");
					setIsExporting(false);
					return;
				}

				console.log(`Exporting ${fullPackageData.length} packages`);

				const escapeCSV = (value) => {
					if (value === null || value === undefined) return "";
					const str = String(value);
					if (
						str.includes(",") ||
						str.includes('"') ||
						str.includes("\n") ||
						str.includes("\r")
					) {
						return `"${str.replace(/"/g, '""')}"`;
					}
					return str;
				};

				const formatNumber = (value) => {
					if (!value || value === "N/A") return "N/A";
					const num = typeof value === "string" ? parseFloat(value) : value;
					if (isNaN(num)) return "N/A";
					return num.toLocaleString();
				};

				let csvContent = "";
				let headers = [];
				let filename = "";

				if (exportType === "summarized") {
					headers = [
						"Package Name",
						"Deal ID",
						"Category L1",
						"Primary Vertical",
						"Country",
						"Deal Status",
						"Impressions",
						"Deal Volume",
						"CTR",
						"Viewability",
					];
					filename = `package-results-summarized-${new Date().toISOString().split("T")[0]}.csv`;
				} else {
					headers = [
						"Package Name",
						"Deal ID",
						"Deal Name",
						"Category L1",
						"Category L2",
						"Category L3",
						"Primary Vertical",
						"Ad Placement",
						"Distribution Channel",
						"Device Types",
						"Country",
						"Audience Name",
						"Audience Provider",
						"Deal Status",
						"Deal Source",
						"Account ID",
						"Publisher Account",
						"Associated Advertisers",
						"URL Targeting",
						"Impressions",
						"Fills",
						"Deal Volume",
						"CTR",
						"Viewability",
						"Bid CPM",
						"Rate Card CPM",
						"Discounted CPM",
						"Deal Price",
						"Deal Start Date",
						"Deal End Date",
					];
					filename = `package-results-detailed-${new Date().toISOString().split("T")[0]}.csv`;
				}

				csvContent = headers.join(",") + "\n";

				fullPackageData.forEach((pkg) => {
					const packageVector = pkg.packageVector || {};
					const packageMetrics = pkg.packageMetrics || {};

					if (exportType === "summarized") {
						const row = [
							escapeCSV(pkg.packageName || packageVector.dealName || "Unknown"),
							escapeCSV(packageVector.dealId || "N/A"),
							escapeCSV(
								Array.isArray(packageVector.packageCategoryL1)
									? packageVector.packageCategoryL1[0]
									: packageVector.packageCategoryL1 || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.primaryVertical)
									? packageVector.primaryVertical[0]
									: packageVector.primaryVertical || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.country)
									? packageVector.country.join("; ")
									: packageVector.country || "N/A",
							),
							escapeCSV(packageVector.dealStatus || "N/A"),
							escapeCSV(formatNumber(packageMetrics.impressions)),
							escapeCSV(formatNumber(packageMetrics.dealVolume)),
							escapeCSV(
								packageMetrics.clickThroughRate
									? `${(packageMetrics.clickThroughRate * 100).toFixed(2)}%`
									: "N/A",
							),
							escapeCSV(
								packageMetrics.viewability
									? `${(packageMetrics.viewability * 100).toFixed(2)}%`
									: "N/A",
							),
						];
						csvContent += row.join(",") + "\n";
					} else {
						const row = [
							escapeCSV(pkg.packageName || packageVector.dealName || "Unknown"),
							escapeCSV(packageVector.dealId || "N/A"),
							escapeCSV(
								Array.isArray(packageVector.dealName)
									? packageVector.dealName[0]
									: packageVector.dealName || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.packageCategoryL1)
									? packageVector.packageCategoryL1.join("; ")
									: packageVector.packageCategoryL1 || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.packageCategoryL2)
									? packageVector.packageCategoryL2.join("; ")
									: packageVector.packageCategoryL2 || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.packageCategoryL3)
									? packageVector.packageCategoryL3.join("; ")
									: packageVector.packageCategoryL3 || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.primaryVertical)
									? packageVector.primaryVertical.join("; ")
									: packageVector.primaryVertical || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.adPlacement)
									? packageVector.adPlacement.join("; ")
									: packageVector.adPlacement || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.distributionChannel)
									? packageVector.distributionChannel.join("; ")
									: packageVector.distributionChannel || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.deviceTypes)
									? packageVector.deviceTypes.join("; ")
									: packageVector.deviceTypes || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.country)
									? packageVector.country.join("; ")
									: packageVector.country || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.audienceName)
									? packageVector.audienceName.join("; ")
									: packageVector.audienceName || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.audienceProviderName)
									? packageVector.audienceProviderName.join("; ")
									: packageVector.audienceProviderName || "N/A",
							),
							escapeCSV(packageVector.dealStatus || "N/A"),
							escapeCSV(
								Array.isArray(packageVector.dealSource)
									? packageVector.dealSource.join("; ")
									: packageVector.dealSource || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.accountId)
									? packageVector.accountId.join("; ")
									: packageVector.accountId || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.publisherAccountNk)
									? packageVector.publisherAccountNk.join("; ")
									: packageVector.publisherAccountNk || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.associatedAdvertisers)
									? packageVector.associatedAdvertisers.join("; ")
									: packageVector.associatedAdvertisers || "N/A",
							),
							escapeCSV(
								Array.isArray(packageVector.urlTargetingType)
									? packageVector.urlTargetingType.join("; ")
									: packageVector.urlTargetingType || "N/A",
							),
							escapeCSV(formatNumber(packageMetrics.impressions)),
							escapeCSV(formatNumber(packageMetrics.fills)),
							escapeCSV(formatNumber(packageMetrics.dealVolume)),
							escapeCSV(
								packageMetrics.clickThroughRate
									? `${(packageMetrics.clickThroughRate * 100).toFixed(4)}%`
									: "N/A",
							),
							escapeCSV(
								packageMetrics.viewability
									? `${(packageMetrics.viewability * 100).toFixed(4)}%`
									: "N/A",
							),
							escapeCSV(packageMetrics.bidCpm || "N/A"),
							escapeCSV(packageVector.rateCardCpm || "N/A"),
							escapeCSV(packageVector.discountedCpm || "N/A"),
							escapeCSV(packageVector.dealPrice || "N/A"),
							escapeCSV(packageVector.dealStartDate || "N/A"),
							escapeCSV(packageVector.dealEndDate || "N/A"),
						];
						csvContent += row.join(",") + "\n";
					}
				});

				const blob = new Blob([csvContent], {
					type: "text/csv;charset=utf-8;",
				});
				const link = document.createElement("a");
				const url = URL.createObjectURL(blob);
				link.setAttribute("href", url);
				link.setAttribute("download", filename);
				link.style.visibility = "hidden";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);

				console.log(
					`Package ${exportType} export completed: ${fullPackageData.length} records`,
				);
				setTimeout(() => setIsExporting(false), 1000);
			} catch (error) {
				console.error("Package export failed:", error);
				setIsExporting(false);
				alert("Package export failed. Please try again.");
			}
		},
		[getPackageDataSource, excludedPackageIds],
	);

	// =============================================
	// FILTER HANDLER FUNCTIONS
	// =============================================

	const toggleSection = (section) => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	};

	const updateFilter = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
	};

	const toggleArrayFilter = (key, value) => {
		setFilters((prev) => {
			const currentArray = prev[key] || [];
			return {
				...prev,
				[key]: currentArray.includes(value)
					? currentArray.filter((item) => item !== value)
					: [...currentArray, value],
			};
		});
	};

	const clearAllFilters = () => {
		setFilters({
			mediaSubtype: [],
			deviceTypes: [],
			distributionChannel: [],
			videoPlacement: [],
			categories: [],
			dateRange: 30,
			geography: [],
			language: [],
			contentStyle: [],
			safetyTier: [],
			isDirect: false,
			publishers: [],
			minViewability: 0,
			minCTR: 0,
			minReach: 0,
			relevance: 0,
			relevanceThreshold: 0,
			minReachThreshold: 0,
			totalReach: "0",
			minViewabilityThreshold: 0,
			minCTRVTR: 0,
			customFilters: [
				{
					id: Date.now(),
					column: "cprmarketrequests",
					threshold: 0,
				},
				{
					id: Date.now() + 1,
					column: "impressions",
					threshold: 0,
				},
			],
		});
	};

	const handleToggleSection = useCallback((section) => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	}, []);

	const handleUpdateFilter = useCallback((key, value) => {
		setFilters((prev) => {
			const updated = { ...prev, [key]: value };
			return updated;
		});
	}, []);

	const handleToggleArrayFilter = useCallback((key, value) => {
		setFilters((prev) => {
			const currentArray = prev[key] || [];
			const updated = {
				...prev,
				[key]: currentArray.includes(value)
					? currentArray.filter((item) => item !== value)
					: [...currentArray, value],
			};
			return updated;
		});
	}, []);

	const handleClearAllFilters = useCallback(() => {
		const defaultFilters = {
			mediaSubtype: [],
			deviceTypes: [],
			distributionChannel: [],
			videoPlacement: [],
			dateRange: 30,
			geography: [],
			language: [],
			contentStyle: [],
			content_style_1: [],
			content_style_2: [],
			content_style_3: [],
			safetyTier: [],
			safety_tier_1: [],
			safety_tier_2: [],
			safety_tier_3: [],
			isDirect: false,
			publishers: [],
			selectedCountries: [],
			minViewability: 0,
			minCTR: 0,
			minReach: 0,
			relevance: 0,
			relevanceThreshold: 0,
			minReachThreshold: 1,
			totalReach: "0",
			minViewabilityThreshold: 0,
			minCTRVTR: 0,
			categories: [],
			limit: 50,
			customFilters: [
				{
					id: Date.now(),
					column: "cprmarketrequests",
					threshold: 0,
				},
				{
					id: Date.now() + 1,
					column: "impressions",
					threshold: 0,
				},
			],
		};

		setFilters(defaultFilters);
	}, []);

	// =============================================
	// UPLOAD HANDLER FUNCTIONS
	// =============================================

	const handleFileUpload = useCallback(
		(event) => {
			try {
				const file = event.target.files?.[0];
				if (!file) return;

				const reader = new FileReader();
				reader.onload = (e) => {
					try {
						const csv = e.target.result;
						const lines = csv.split("\n");
						const domains = [];

						for (let i = 1; i < lines.length; i++) {
							const domain = lines[i]?.trim();
							if (domain) {
								domains.push({
									id: (domainResults?.length || 0) + domains.length + 1,
									domain: domain,
									publisher: "Custom Upload",
									categories: ["Custom"],
									reach: `${(Math.random() * 10 + 1).toFixed(1)}M`,
									relevance: Math.floor(Math.random() * 20 + 60),
									metrics: {
										viewability: Math.floor(Math.random() * 20 + 70),
										ctr: (Math.random() * 2 + 1).toFixed(2),
										vtr: (Math.random() * 25 + 60).toFixed(1),
									},

									isDirect: false,
									supportedFormats: ["display"],
									supportedDevices: ["desktop", "mobile"],
									isCustom: true,
								});
							}
						}

						setUploadedDomains(domains);
					} catch (parseError) {
						alert("Error parsing file. Please check the format.");
					}
				};

				reader.onerror = () => {
					alert("Error reading file. Please try again.");
				};

				reader.readAsText(file);
			} catch (error) {
				alert("Error uploading file. Please try again.");
			}
		},
		[domainResults],
	);

	const processUploadedDomains = useCallback(() => {
		try {
			setIsUploading(true);

			setTimeout(() => {
				try {
					setDomainResults((prev) => [
						...(prev || []),
						...(uploadedDomains || []),
					]);
					setIsUploading(false);
					setShowUploadModal(false);
					setUploadedDomains([]);
					alert(
						`Successfully added ${uploadedDomains?.length || 0} custom domains!`,
					);
				} catch (processError) {
					setIsUploading(false);
					alert("Error processing uploaded domains. Please try again.");
				}
			}, 2000);
		} catch (error) {
			setIsUploading(false);
		}
	}, [uploadedDomains]);

	// =============================================
	// UTILITY & DATA HELPER FUNCTIONS
	// =============================================

	const transformApiDomains = useCallback((apiDomains) => {
		return apiDomains.map((domain, index) => {
			const totalImpressions = domain.totalImpressions || 0;
			const totalClicks = domain.totalCprClicks || 0;
			const totalSpend = domain.totalCprMarketDemandPartnerSpendInUsd || 0;

			const calculatedCTR =
				totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
			const calculatedCPM = domain.avgBidCpm || 0;

			let relevance = 0;
			if (typeof domain.score === "number") {
				relevance = calculateRelevanceFromScore(domain.score);
			} else if (typeof domain.relevance === "number") {
				relevance = Math.round(domain.relevance);
			} else if (typeof domain.relevanceScore === "number") {
				relevance = Math.round(domain.relevanceScore);
			} else {
				relevance = 75;
			}
			return {
				...domain,
				id: domain.id || index + 1,
				domain: domain.domainName || domain.domain || "Unknown",
				publisher: domain.publisher || "Unknown Publisher",
				publisherId: domain.publisheraccountid || `pub_${index + 1}`,
				categories: domain.categories || ["General"],
				reach: domain.domainReach || 0,
				relevance: Math.round((domain.score || 0) * 100),

				metrics: {
					viewability: Math.round(domain.avgViewability || 0),
					viewabilityTrend: "+",
					viewabilityChange: "2.1",
					ctr: calculatedCTR.toFixed(2),
					ctrTrend: "+",
					ctrChange: "0.15",
					clicks: totalClicks,
					clicksMonthly: Math.floor(totalClicks / 3),
					vtr: (domain.avgVtr || 0).toFixed(1),
					vtrTrend: "+",
					vtrChange: "1.5",
					videoCompletionRate:
						domain.totalCompleteFirstOccurrence &&
						domain.totalStartFirstOccurrence
							? (
									(domain.totalCompleteFirstOccurrence /
										domain.totalStartFirstOccurrence) *
									100
								).toFixed(1)
							: "0.0",
					bidRate: (domain.avgPubWinRate * 100 || 0).toFixed(1),
					winRate: (domain.avgOxWinRate * 100 || 0).toFixed(1),
					fillRate:
						domain.totalCprAllRequests > 0
							? (
									(domain.totalFills / domain.totalCprAllRequests) *
									100
								).toFixed(1)
							: "0.0",
					spend: Math.round(totalSpend),
					spendMonthly: Math.round(totalSpend / 3),
					avgBidCpm: domain.avgBidCpm,
					cpm: calculatedCPM,
					cpc: totalClicks > 0 ? (totalSpend / totalClicks).toFixed(2) : "0.00",
					cpa: "25.50",
					engagementRate: "3.2",
					bounceRate: "35.0",
					sessionDuration: 180,
					pagesPerSession: "2.1",
					brandSafetyScore: 85,
					fraudScore: 5,
					impressions: totalImpressions,
					qualityScore: Math.round((domain.score || 0) * 100),
				},

				supportedFormats: domain.iabmediasubtype || ["Video"],
				supportedDevices: domain.iabdevicetype || ["desktop"],
				videoOptions: {
					placements: domain.videoplcmt || ["NO_CONTENT_STANDALONE"],
					maxDuration: 30,
					skipEnabled: true,
				},
				targetingOptions: {
					geographic: domain.geography || ["US"],
					demographic: ["age", "gender"],
					behavioral: ["interests"],
					contextual: domain.content_styles || ["Factual"],
				},
				isDirect: domain.isDirect?.[0] === "true",
				safetyTier: "standard",
				lastActive: new Date(),
				lastUpdated: new Date(),
				trends: {
					reach: "up",
					engagement: "up",
					efficiency: "up",
				},
			};
		});
	}, []);

	const transformApiKeywords = useCallback((apiKeywords) => {
		return apiKeywords.map((keyword, index) => ({
			id: index + 1,
			keyword: keyword,
			type: "Discovered",
			frequency: "Medium",
			associatedDomains: 1,
			relevance: 75,
			metrics: {
				searchVolume: Math.floor(Math.random() * 100000 + 10000),
				competition: "Medium",
				cpc: "1.25",
			},
		}));
	}, []);

	const shouldShowFilters = useCallback(() => {
		return [
			"domains",
			"packages",
			"app",
			"audience",
			"segments",
			"category",
		].includes(activeTab);
	}, [activeTab]);

	const getFilteredDataCount = useCallback(() => {
		try {
			let data = activeTab === "domains" ? domainResults : keywordResults;

			if (
				activeTab === "domains" &&
				selectedDomainSet &&
				prebuiltDomainSets[selectedDomainSet]
			) {
				const setDomainIds = (
					prebuiltDomainSets[selectedDomainSet]?.domains || []
				).map((d) => d?.id);
				data = data.filter((domain) => setDomainIds.includes(domain?.id));
			}

			if (activeTab === "domains" && domainSearchTerm.trim()) {
				data = data.filter(
					(item) =>
						item.domain
							?.toLowerCase()
							.includes(domainSearchTerm.toLowerCase()) ||
						item.categories?.some((cat) =>
							cat.toLowerCase().includes(domainSearchTerm.toLowerCase()),
						) ||
						item.publisher
							?.toLowerCase()
							.includes(domainSearchTerm.toLowerCase()),
				);
			} else if (activeTab === "keywords" && keywordSearchTerm.trim()) {
				data = data.filter((item) =>
					item.keyword?.toLowerCase().includes(keywordSearchTerm.toLowerCase()),
				);
			}

			return data.length;
		} catch (error) {
			console.error("Error in getFilteredDataCount:", error);
			return 0;
		}
	}, [
		activeTab,
		domainResults,
		keywordResults,
		selectedDomainSet,
		prebuiltDomainSets,
		domainSearchTerm,
		keywordSearchTerm,
	]);

	const getSortedData = useCallback(
		(data) => {
			try {
				if (!Array.isArray(data)) return [];

				return [...data].sort((a, b) => {
					let aValue, bValue;

					switch (sortBy) {
						case "relevance":
							aValue = b?.score || 0;
							bValue = a?.score || 0;
							break;
						case "reach":
							aValue = a?.reach || a?.domainReach || 0;
							bValue = b?.reach || b?.domainReach || 0;

							if (typeof aValue === "string") {
								aValue = parseFloat(aValue.replace(/[^0-9.]/g, ""));
							}
							if (typeof bValue === "string") {
								bValue = parseFloat(bValue.replace(/[^0-9.]/g, ""));
							}

							aValue = Number(aValue) || 0;
							bValue = Number(bValue) || 0;
							break;
						case "viewability":
							aValue = a?.metrics?.viewability || 0;
							bValue = b?.metrics?.viewability || 0;
							break;
						case "ctr":
							aValue = parseFloat(a?.metrics?.ctr || 0);
							bValue = parseFloat(b?.metrics?.ctr || 0);
							break;
						case "vtr":
							aValue = parseFloat(a?.metrics?.vtr || 0);
							bValue = parseFloat(b?.metrics?.vtr || 0);
							break;
						case "compositeScore":
							aValue = parseFloat(a?.compositeScore || 0);
							bValue = parseFloat(b?.compositeScore || 0);
							break;
						case "domain":
							aValue = a?.domain || a?.keyword || "";
							bValue = b?.domain || b?.keyword || "";
							break;
						case "impressions":
							aValue =
								a?.metrics?.impressions ||
								a?.totalImpressions ||
								a?.impressions ||
								0;
							bValue =
								b?.metrics?.impressions ||
								b?.totalImpressions ||
								b?.impressions ||
								0;

							aValue = Number(aValue) || 0;
							bValue = Number(bValue) || 0;
							break;

						case "marketRequests":
							aValue =
								a?.metrics?.totalCprMarketRequests ||
								a?.totalCprMarketRequests ||
								0;
							bValue =
								b?.metrics?.totalCprMarketRequests ||
								b?.totalCprMarketRequests ||
								0;

							aValue = Number(aValue) || 0;
							bValue = Number(bValue) || 0;
							break;

						case "domain":
							aValue = a?.domain || a?.domainName || a?.keyword || "";
							bValue = b?.domain || b?.domainName || b?.keyword || "";
							return sortOrder === "asc"
								? aValue.localeCompare(bValue)
								: bValue.localeCompare(aValue);

						default:
							return 0;
					}

					return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
				});
			} catch (error) {
				return data || [];
			}
		},
		[sortBy, sortOrder],
	);

	const getCurrentPageData = useCallback(() => {
		try {
			let data = activeTab === "domains" ? domainResults : keywordResults;

			const domainSetsToUse =
				persistedPrebuiltDomainSets || hookPrebuiltDomainSets;
			if (
				activeTab === "domains" &&
				selectedDomainSet &&
				domainSetsToUse &&
				domainSetsToUse[selectedDomainSet]
			) {
				const setDomainIds = (
					domainSetsToUse[selectedDomainSet]?.domains || []
				).map((d) => d?.id);
				data = data.filter((domain) => setDomainIds.includes(domain?.id));
			}

			if (activeTab === "domains" && domainSearchTerm.trim()) {
				data = data.filter(
					(item) =>
						item.domain
							?.toLowerCase()
							.includes(domainSearchTerm.toLowerCase()) ||
						item.categories?.some((cat) =>
							cat.toLowerCase().includes(domainSearchTerm.toLowerCase()),
						) ||
						item.publisher
							?.toLowerCase()
							.includes(domainSearchTerm.toLowerCase()),
				);
			} else if (activeTab === "keywords" && keywordSearchTerm.trim()) {
				data = data.filter((item) =>
					item.keyword?.toLowerCase().includes(keywordSearchTerm.toLowerCase()),
				);
			}

			const sortedData = getSortedData(data);
			const startIndex = (currentPage - 1) * pageSize;
			const endIndex = startIndex + pageSize;

			return sortedData.slice(startIndex, endIndex);
		} catch (error) {
			console.error("Error in getCurrentPageData:", error);
			return [];
		}
	}, [
		activeTab,
		domainResults,
		keywordResults,
		selectedDomainSet,
		prebuiltDomainSets,
		getSortedData,
		currentPage,
		pageSize,
		domainSearchTerm,
		keywordSearchTerm,
	]);

	const handleSelectAll = useCallback(
		(selectAll) => {
			try {
				if (selectAll) {
					const currentPageIds = getCurrentPageData()
						.map((item) => item?.id)
						.filter(Boolean);
					setSelectedResults((prev) => [
						...new Set([...prev, ...currentPageIds]),
					]);
				} else {
					const currentPageIds = getCurrentPageData()
						.map((item) => item?.id)
						.filter(Boolean);
					setSelectedResults((prev) =>
						prev.filter((id) => !currentPageIds.includes(id)),
					);
				}
				setIsAllSelected(selectAll);
			} catch (error) {
				// Silent error handling
			}
		},
		[getCurrentPageData],
	);

	const getTotalPages = useCallback(() => {
		try {
			let data = activeTab === "domains" ? domainResults : keywordResults;

			if (
				activeTab === "domains" &&
				selectedDomainSet &&
				prebuiltDomainSets[selectedDomainSet]
			) {
				const setDomainIds = (
					prebuiltDomainSets[selectedDomainSet]?.domains || []
				).map((d) => d?.id);
				data = data.filter((domain) => setDomainIds.includes(domain?.id));
			}

			if (activeTab === "domains" && domainSearchTerm.trim()) {
				data = data.filter(
					(item) =>
						item.domain
							?.toLowerCase()
							.includes(domainSearchTerm.toLowerCase()) ||
						item.categories?.some((cat) =>
							cat.toLowerCase().includes(domainSearchTerm.toLowerCase()),
						) ||
						item.publisher
							?.toLowerCase()
							.includes(domainSearchTerm.toLowerCase()),
				);
			} else if (activeTab === "keywords" && keywordSearchTerm.trim()) {
				data = data.filter((item) =>
					item.keyword?.toLowerCase().includes(keywordSearchTerm.toLowerCase()),
				);
			}

			return Math.ceil((data?.length || 0) / pageSize);
		} catch (error) {
			console.error("Error in getTotalPages:", error);
			return 0;
		}
	}, [
		activeTab,
		domainResults,
		keywordResults,
		selectedDomainSet,
		prebuiltDomainSets,
		pageSize,
		domainSearchTerm,
		keywordSearchTerm,
	]);

	const getCompleteAppData = () => {
		let data = appResults;
		console.log("AppResultsList props:", {
			dataLength: data?.length,
			dataFirstItemKeys: data?.[0] ? Object.keys(data[0]).length : 0,
		});

		data = data.filter((item) => !excludedApps.includes(item.id));

		if (appSearchTerm.trim()) {
			data = data.filter(
				(item) =>
					(item.app_name || item.name)
						?.toLowerCase()
						.includes(appSearchTerm.toLowerCase()) ||
					item.developer?.toLowerCase().includes(appSearchTerm.toLowerCase()) ||
					item.category?.toLowerCase().includes(appSearchTerm.toLowerCase()),
			);
		}

		return getSortedAppData(data);
	};

	const getAppCurrentPageData = () => {
		let data = appResults;

		data = data.filter((item) => !excludedApps.includes(item.id));

		if (appSearchTerm.trim()) {
			data = data.filter(
				(item) =>
					(item.app_name || item.name)
						?.toLowerCase()
						.includes(appSearchTerm.toLowerCase()) ||
					item.developer?.toLowerCase().includes(appSearchTerm.toLowerCase()) ||
					item.category?.toLowerCase().includes(appSearchTerm.toLowerCase()),
			);
		}

		return data.map((item) => ({
			...item,
			_original: item._original || item,
		}));
	};

	const getAppTotalPages = () => {
		let data = appResults;
		data = data.filter((item) => !excludedApps.includes(item.id));

		if (appSearchTerm.trim()) {
			data = data.filter(
				(item) =>
					(item.app_name || item.name)
						?.toLowerCase()
						.includes(appSearchTerm.toLowerCase()) ||
					item.developer?.toLowerCase().includes(appSearchTerm.toLowerCase()) ||
					item.category?.toLowerCase().includes(appSearchTerm.toLowerCase()),
			);
		}

		return Math.ceil(data.length / pageSize);
	};

	const getAppFilteredDataCount = () => {
		let data = appResults;
		data = data.filter((item) => !excludedApps.includes(item.id));

		if (appSearchTerm.trim()) {
			data = data.filter(
				(item) =>
					(item.app_name || item.name)
						?.toLowerCase()
						.includes(appSearchTerm.toLowerCase()) ||
					item.developer?.toLowerCase().includes(appSearchTerm.toLowerCase()) ||
					item.category?.toLowerCase().includes(appSearchTerm.toLowerCase()),
			);
		}

		return data.length;
	};

	const getSortedAppData = (data) => {
		return [...data].sort((a, b) => {
			let aValue, bValue;

			switch (sortBy) {
				case "relevance":
					aValue = a.score || 0;
					bValue = b.score || 0;
					break;
				case "rating":
					aValue = a.rating || 0;
					bValue = b.rating || 0;
					break;
				case "downloads":
					aValue = a.downloads || 0;
					bValue = b.downloads || 0;
					break;
				case "viewability":
					aValue = a.avgViewability || 0;
					bValue = b.avgViewability || 0;
					break;
				case "ctr":
					aValue = a.calculated_ctr || 0;
					bValue = b.calculated_ctr || 0;
					break;
				case "compositeScore":
					aValue = a.compositeScore || 0;
					bValue = b.compositeScore || 0;
					break;
				case "name":
					aValue = a.app_name || a.name || "";
					bValue = b.app_name || b.name || "";
					return sortOrder === "asc"
						? aValue.localeCompare(bValue)
						: bValue.localeCompare(aValue);
				default:
					return 0;
			}

			return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
		});
	};

	const getSegmentsCurrentPageData = () => {
		let data = segmentsResults;

		if (segmentsSearchTerm.trim()) {
			data = data.filter(
				(item) =>
					item.segmentVector?.segmentName
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()) ||
					item.segmentVector?.description
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()) ||
					item.segmentVector?.attributeName
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()) ||
					item.segmentVector?.attributeValue
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()),
			);
		}

		if (selectedSegmentsSet && selectedSegmentsSet !== "All") {
			const setConfig = getSegmentsSetConfig(selectedSegmentsSet);
			if (setConfig) {
				data = applySegmentsSetFilter(data, setConfig);
			}
		}

		const sortedData = getSortedSegmentsData(data);
		const startIndex = (segmentsCurrentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return sortedData.slice(startIndex, endIndex);
	};

	const getSortedSegmentsData = (data) => {
		return [...data].sort((a, b) => {
			let aValue, bValue;

			switch (segmentsSortBy) {
				case "reach":
					aValue = a.segmentMetrics?.uniqueUsers || 0;
					bValue = b.segmentMetrics?.uniqueUsers || 0;
					break;
				case "avgEcpm":
					aValue = a.segmentMetrics?.avgEcpm || 0;
					bValue = b.segmentMetrics?.avgEcpm || 0;
					break;
				case "segment_name":
					aValue = a.segmentVector?.segmentName || "";
					bValue = b.segmentVector?.segmentName || "";
					return segmentsSortOrder === "ASC"
						? aValue.localeCompare(bValue)
						: bValue.localeCompare(aValue);
				case "sv.segment_id":
				default:
					aValue = a.segmentVector?.segmentId || "";
					bValue = b.segmentVector?.segmentId || "";
					return segmentsSortOrder === "ASC"
						? aValue.localeCompare(bValue)
						: bValue.localeCompare(aValue);
			}

			return segmentsSortOrder === "ASC" ? aValue - bValue : bValue - aValue;
		});
	};

	const getSegmentsTotalPages = () => {
		let data = segmentsResults;

		if (segmentsSearchTerm.trim()) {
			data = data.filter(
				(item) =>
					item.segmentVector?.segmentName
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()) ||
					item.segmentVector?.description
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()) ||
					item.segmentVector?.attributeName
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()) ||
					item.segmentVector?.attributeValue
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()),
			);
		}

		return Math.ceil(data.length / pageSize);
	};

	const getSegmentsFilteredDataCount = () => {
		let data = segmentsResults;

		if (segmentsSearchTerm.trim()) {
			data = data.filter(
				(item) =>
					item.segmentVector?.segmentName
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()) ||
					item.segmentVector?.description
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()) ||
					item.segmentVector?.attributeName
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()) ||
					item.segmentVector?.attributeValue
						?.toLowerCase()
						.includes(segmentsSearchTerm.toLowerCase()),
			);
		}

		return data.length;
	};

	const getSegmentsSetConfig = (setName) => {
		const prebuiltSetsConfig = {
			Relevance: {
				sortBy: "reach",
				isASC: true,
				filterBy: "reach",
				threshold: 1000,
			},
			Performance: {
				sortBy: "avgEcpm",
				isASC: false,
				filterBy: "avgEcpm",
				threshold: 5000.5,
			},
			"Cost Optimized": {
				sortBy: "reach",
				isASC: true,
				filterBy: "reach",
				threshold: 1000,
			},
		};

		return prebuiltSetsConfig[setName];
	};

	const applySegmentsSetFilter = (data, setConfig) => {
		if (!setConfig) return data;

		return data.filter((segment) => {
			const value =
				setConfig.filterBy === "reach"
					? segment.segmentMetrics?.uniqueUsers || 0
					: segment.segmentMetrics?.avgEcpm || 0;

			return value >= setConfig.threshold;
		});
	};

	const calculateSegmentsSummary = (data) => {
		if (!data || data.length === 0) return null;

		const summary = {
			totalCount: data.length,
			totalReach: data.reduce(
				(sum, item) => sum + (item.segmentMetrics?.uniqueUsers || 0),
				0,
			),
			avgEcpm:
				data.reduce(
					(sum, item) => sum + (item.segmentMetrics?.avgEcpm || 0),
					0,
				) / data.length,
			totalSpend: data.reduce(
				(sum, item) => sum + (item.segmentMetrics?.advertiserSpend || 0),
				0,
			),
			avgFrequency:
				data.reduce(
					(sum, item) => sum + (item.segmentMetrics?.impressionFrequency || 0),
					0,
				) / data.length,
			countriesCount:
				new Set(
					data
						.flatMap((item) => item.segmentVector?.allowedCountries)
						.filter(Boolean),
				).size || 1,
			activeCampaigns: data.filter(
				(item) => item.segmentVector?.status === "ready",
			).length,
			performanceScore:
				Math.round(
					data.reduce(
						(sum, item) => sum + (item.segmentVector?.reach || 0),
						0,
					) /
						data.length /
						1000000,
				) || 5,
		};

		return summary;
	};

	// ✅ REMOVED: All complex audience data manipulation functions
	// The combined hook handles all data processing internally

	const shouldShowPrebuiltDomainSets = () => {
		const dataToUse = persistedPrebuiltDomainSets || hookPrebuiltDomainSets;
		return (
			activeTab === "domains" &&
			dataToUse &&
			Object.keys(dataToUse).length > 0 &&
			domainResults.length > 0
		);
	};

	// ✅ ENHANCED: Update the shouldShowPrebuiltAppSets function to be more reliable
	const shouldShowPrebuiltAppSets = useCallback(() => {
		console.log("Checking shouldShowPrebuiltAppSets:", {
			activeTab,
			persistedSetsCount: persistedPrebuiltAppSets
				? Object.keys(persistedPrebuiltAppSets).length
				: 0,
			hookSetsCount: hookPrebuiltAppSets
				? Object.keys(hookPrebuiltAppSets).length
				: 0,
			appResultsCount: appResults?.length || 0,
			searchResultsAppsCount: searchResults?.apps?.length || 0,
		});

		// Only show for app tab
		if (activeTab !== "app") return false;

		// Must have app results to show prebuilt sets
		const hasAppResults =
			(appResults && appResults.length > 0) ||
			(searchResults?.apps && searchResults.apps.length > 0);
		if (!hasAppResults) return false;

		// Check for prebuilt sets - prioritize hook sets over persisted sets for real-time updates
		const currentSets =
			hookPrebuiltAppSets && Object.keys(hookPrebuiltAppSets).length > 0
				? hookPrebuiltAppSets
				: persistedPrebuiltAppSets;

		const hasPrebuiltSets = currentSets && Object.keys(currentSets).length > 0;

		console.log("shouldShowPrebuiltAppSets result:", {
			hasAppResults,
			hasPrebuiltSets,
			finalDecision: hasPrebuiltSets,
		});

		return hasPrebuiltSets;
	}, [
		activeTab,
		persistedPrebuiltAppSets,
		hookPrebuiltAppSets,
		appResults,
		searchResults?.apps,
	]);

	// =============================================
	// FIXED EXPORT FUNCTIONS - FULL DATA EXPORT
	// =============================================

	// 1. DOMAINS/KEYWORDS EXPORT - FIXED
	const exportToCSV = useCallback(() => {
		try {
			setIsExporting(true);

			// ✅ FIXED: Ensure we get ALL data, not just current page
			let fullData = [];

			if (activeTab === "domains") {
				// Use the complete domain results
				fullData = domainResults || [];

				// If using prebuilt sets, get all domains from the set
				if (
					selectedDomainSet &&
					(persistedPrebuiltDomainSets || hookPrebuiltDomainSets)
				) {
					const domainSetsToUse =
						persistedPrebuiltDomainSets || hookPrebuiltDomainSets;
					if (domainSetsToUse[selectedDomainSet]) {
						const setDomainIds = (
							domainSetsToUse[selectedDomainSet]?.domains || []
						).map((d) => d?.id);
						fullData = fullData.filter((domain) =>
							setDomainIds.includes(domain?.id),
						);
					}
				}

				// Apply search filter if exists
				if (domainSearchTerm.trim()) {
					fullData = fullData.filter(
						(item) =>
							item.domain
								?.toLowerCase()
								.includes(domainSearchTerm.toLowerCase()) ||
							item.categories?.some((cat) =>
								cat.toLowerCase().includes(domainSearchTerm.toLowerCase()),
							) ||
							item.publisher
								?.toLowerCase()
								.includes(domainSearchTerm.toLowerCase()),
					);
				}
			} else {
				// Keywords
				fullData = keywordResults || [];

				// Apply search filter if exists
				if (keywordSearchTerm.trim()) {
					fullData = fullData.filter((item) =>
						item.keyword
							?.toLowerCase()
							.includes(keywordSearchTerm.toLowerCase()),
					);
				}
			}

			// Apply sorting to full dataset
			const sortedData = getSortedData(fullData);

			console.log(
				`📊 Exporting ${activeTab} - Total records: ${sortedData.length}`,
			);

			let csvContent = "";
			let headers = [];

			if (activeTab === "domains") {
				headers = [
					"Domain",
					"Categories",
					"Relevance",
					"Avg.Reach/Day",
					"Market Request",
					"Viewability",
					"CTR",
					"VTR",
					"Impressions",
				];
				csvContent = headers.join(",") + "\n";

				sortedData.forEach((domain) => {
					const row = [
						domain?.domain || "",
						`"${(domain?.categories || []).join(", ")}"`,
						domain?.relevance || "",
						domain?.reach || "",
						domain?.totalCprMarketRequests || "",
						domain?.metrics?.viewability || "N/A",
						domain?.metrics?.ctr || "N/A",
						domain?.metrics?.vtr || "N/A",
						domain?.metrics?.impressions || "N/A",
					];
					csvContent += row.join(",") + "\n";
				});
			} else {
				headers = [
					"Keyword",
					"Type",
					"Frequency",
					"Associated Domains",
					"Relevance",
				];
				csvContent = headers.join(",") + "\n";

				sortedData.forEach((keyword) => {
					const row = [
						keyword?.keyword || "",
						keyword?.type || "",
						keyword?.frequency || "",
						keyword?.associatedDomains || "N/A",
						keyword?.relevance || "",
					];
					csvContent += row.join(",") + "\n";
				});
			}

			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute(
				"download",
				`${activeTab}-export-full-${new Date().toISOString().split("T")[0]}.csv`,
			);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);

			console.log(
				`✅ ${activeTab} export completed: ${sortedData.length} records`,
			);
			setTimeout(() => setIsExporting(false), 1000);
		} catch (error) {
			console.error("❌ Export failed:", error);
			setIsExporting(false);
			alert("Export failed. Please try again.");
		}
	}, [
		activeTab,
		domainResults,
		keywordResults,
		getSortedData,
		selectedDomainSet,
		persistedPrebuiltDomainSets,
		hookPrebuiltDomainSets,
		domainSearchTerm,
		keywordSearchTerm,
	]);

	// 2. APPS EXPORT - FIXED
	const exportAppToCSV = useCallback(
		(exportType) => {
			try {
				setIsExporting(exportType);

				// ✅ FIXED: Get ALL app data, with multiple fallbacks
				let fullAppData = [];

				// Priority order for data sources
				if (appResults && appResults.length > 0) {
					fullAppData = appResults.map((app) => app._original || app);
				} else if (searchResults?.apps && searchResults.apps.length > 0) {
					fullAppData = searchResults.apps.map((app) => app._original || app);
				} else if (appData && appData.length > 0) {
					fullAppData = appData.map((app) => app._original || app);
				} else {
					console.warn("⚠️ No app data available for export");
					alert(
						"No app data available for export. Please search for apps first.",
					);
					setIsExporting(false);
					return;
				}

				// Apply filters (excluding excluded apps)
				fullAppData = fullAppData.filter(
					(item) => !excludedApps.includes(item.id),
				);

				// Apply search filter if exists
				if (appSearchTerm.trim()) {
					fullAppData = fullAppData.filter(
						(item) =>
							(item.app_name || item.name)
								?.toLowerCase()
								.includes(appSearchTerm.toLowerCase()) ||
							item.developer
								?.toLowerCase()
								.includes(appSearchTerm.toLowerCase()) ||
							item.category
								?.toLowerCase()
								.includes(appSearchTerm.toLowerCase()),
					);
				}

				// Apply sorting to full dataset
				fullAppData = getSortedAppData(fullAppData);

				console.log(
					`📱 Exporting apps (${exportType}) - Total records: ${fullAppData.length}`,
				);

				const escapeCSV = (value) => {
					if (value === null || value === undefined) return "";
					const str = String(value);
					if (
						str.includes(",") ||
						str.includes('"') ||
						str.includes("\n") ||
						str.includes("\r")
					) {
						return `"${str.replace(/"/g, '""')}"`;
					}
					return str;
				};

				const formatNumber = (value) => {
					if (!value || value === "N/A") return "N/A";
					const num = typeof value === "string" ? parseFloat(value) : value;
					if (isNaN(num)) return "N/A";
					if (num >= 1000000000) return (num / 1000000000).toFixed(2) + "B";
					if (num >= 1000000) return (num / 1000000).toFixed(2) + "M";
					return num.toLocaleString();
				};

				const formatPercentage = (value) => {
					if (!value || value === "N/A") return "N/A";
					const num = typeof value === "string" ? parseFloat(value) : value;
					if (isNaN(num)) return "N/A";
					return `${num.toFixed(4)}%`;
				};

				const formatCurrency = (value) => {
					if (!value || value === "N/A") return "N/A";
					const num = typeof value === "string" ? parseFloat(value) : value;
					if (isNaN(num)) return "N/A";
					if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
					return `${num.toFixed(4)}`;
				};

				let csvContent = "";
				let headers = [];
				let filename = "";

				if (exportType === "summarized") {
					headers = [
						"App Name",
						"Bundle ID",
						"Category",
						"Developer",
						"Downloads",
						"Market Request",
						"Rating",
						"Rating Count",
						"Viewability",
						"CTR",
						"Impressions",
					];
					filename = `app-results-summarized-full-${new Date().toISOString().split("T")[0]}.csv`;
				} else {
					headers = [
						"App Name",
						"Bundle ID",
						"Developer",
						"Category",
						"Content Rating",
						"Market Requests",
						"Market Source",
						"Platform",
						"Downloads",
						"Rating",
						"Rating Count",
						"Release Date",
						"Last Update",
						"Total Impressions",
						"Total Clicks",
						"CTR",
						"Viewability",
						"VTR",
						"CPM",
						"Revenue Per Impression",
						"Cost Per Impression",
						"Description",
						"App Store URL",
						"Languages",
						"Keywords",
						"Domain",
					];
					filename = `app-results-detailed-full-${new Date().toISOString().split("T")[0]}.csv`;
				}

				csvContent = headers.join(",") + "\n";

				fullAppData.forEach((app) => {
					if (exportType === "summarized") {
						const row = [
							escapeCSV(app?.app_name || app?.name || "Unknown App"),
							escapeCSV(app?.bundle_id || "N/A"),
							escapeCSV(app?.category || app?.subcategory || "N/A"),
							escapeCSV(app?.developer || app?.creator || "N/A"),
							escapeCSV(formatNumber(app?.downloads)),
							escapeCSV(formatNumber(app?.totalCprMarketRequests)),
							escapeCSV(app?.rating || "N/A"),
							escapeCSV(formatNumber(app?.rating_count)),
							escapeCSV(
								formatPercentage(app?.avgViewability || app?.viewability),
							),
							escapeCSV(formatPercentage(app?.calculated_ctr || app?.ctr)),
							escapeCSV(
								formatNumber(app?.totalImpressions || app?.impressions),
							),
						];
						csvContent += row.join(",") + "\n";
					} else {
						const row = [
							escapeCSV(app?.app_name || app?.name || "Unknown App"),
							escapeCSV(app?.bundle_id || "N/A"),
							escapeCSV(app?.developer || app?.creator || "N/A"),
							escapeCSV(app?.category || app?.subcategory || "N/A"),
							escapeCSV(app?.content_rating || "N/A"),
							escapeCSV(formatNumber(app?.totalCprMarketRequests)),
							escapeCSV(app?.market_source || "N/A"),
							escapeCSV(app?.platform || "N/A"),
							escapeCSV(formatNumber(app?.downloads)),
							escapeCSV(app?.rating || "N/A"),
							escapeCSV(formatNumber(app?.rating_count)),
							escapeCSV(app?.release_date || "N/A"),
							escapeCSV(app?.update_date || "N/A"),
							escapeCSV(
								formatNumber(app?.totalImpressions || app?.impressions),
							),
							escapeCSV(formatNumber(app?.totalCprClicks || app?.clicks)),
							escapeCSV(formatPercentage(app?.calculated_ctr || app?.ctr)),
							escapeCSV(
								formatPercentage(app?.avgViewability || app?.viewability),
							),
							escapeCSV(formatPercentage(app?.avgVtr || app?.vtr)),
							escapeCSV(formatCurrency(app?.avgBidCpm || app?.cpm)),
							escapeCSV(
								formatCurrency(
									app?.calculated_revenueperimpression || app?.rpi,
								),
							),
							escapeCSV(
								formatCurrency(app?.calculated_costperimpression || app?.cpi),
							),
							escapeCSV(app?.short_description || app?.description || "N/A"),
							escapeCSV(app?.app_store_url || "N/A"),
							escapeCSV(
								Array.isArray(app?.languages)
									? app.languages.join("; ")
									: app?.languages || "N/A",
							),
							escapeCSV(
								Array.isArray(app?.keywords)
									? app.keywords.join("; ")
									: app?.keywords || "N/A",
							),
							escapeCSV(app?.domain || app?.app_domain || "N/A"),
						];
						csvContent += row.join(",") + "\n";
					}
				});

				const blob = new Blob([csvContent], {
					type: "text/csv;charset=utf-8;",
				});
				const link = document.createElement("a");
				const url = URL.createObjectURL(blob);
				link.setAttribute("href", url);
				link.setAttribute("download", filename);
				link.style.visibility = "hidden";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);

				console.log(
					`✅ App ${exportType} export completed: ${fullAppData.length} records`,
				);
				setTimeout(() => setIsExporting(false), 1000);
			} catch (error) {
				console.error("❌ App export failed:", error);
				setIsExporting(false);
				alert("App export failed. Please try again.");
			}
		},
		[
			appResults,
			searchResults?.apps,
			appData,
			excludedApps,
			appSearchTerm,
			getSortedAppData,
		],
	);

	// =============================================
	// ENHANCED AUDIENCE EXPORT - COMPREHENSIVE
	// =============================================
	const exportAudienceToCSV = useCallback(
		(exportType) => {
			try {
				setIsExporting(exportType);
				console.log("🎯 Starting comprehensive audience export:", exportType);

				// Get ALL audience data from combined hook
				let fullAudienceData = [];

				if (
					audienceSearchResults?.audiences &&
					audienceSearchResults.audiences.length > 0
				) {
					fullAudienceData = audienceSearchResults.audiences;
				} else {
					console.warn("⚠️ No audience data available for export");
					alert(
						"No audience data available for export. Please search for audiences first.",
					);
					setIsExporting(false);
					return;
				}

				// Apply filters (excluding excluded items)
				fullAudienceData = fullAudienceData.filter(
					(item) =>
						!excludedItems.includes(
							item.audienceVector?.audienceId || item.audienceId || item.id,
						),
				);

				console.log(
					`👥 Exporting audiences (${exportType}) - Total records: ${fullAudienceData.length}`,
				);

				const escapeCSV = (value) => {
					if (value === null || value === undefined) return "";
					const str = String(value);
					if (
						str.includes(",") ||
						str.includes('"') ||
						str.includes("\n") ||
						str.includes("\r")
					) {
						return `"${str.replace(/"/g, '""')}"`;
					}
					return str;
				};

				const formatNumber = (value) => {
					if (!value || value === "N/A") return "N/A";
					const num = typeof value === "string" ? parseFloat(value) : value;
					if (isNaN(num)) return "N/A";
					return num.toLocaleString();
				};

				const formatCurrency = (value) => {
					if (!value || value === "N/A") return "N/A";
					const num = typeof value === "string" ? parseFloat(value) : value;
					if (isNaN(num)) return "N/A";
					return num.toFixed(4);
				};

				const formatPercentage = (value) => {
					if (!value || value === "N/A") return "N/A";
					const num = typeof value === "string" ? parseFloat(value) : value;
					if (isNaN(num)) return "N/A";
					return `${num.toFixed(4)}%`;
				};

				let csvContent = "";
				let headers = [];
				let filename = "";

				if (exportType === "summarized") {
					headers = [
						"Audience Name",
						"Audience ID",
						"Account Name",
						"Region",
						"Status",
						"Composite Score",
						"Unique Users",
						"eCPM",
						"Sold Impressions",
						"Advertiser Spend",
						"Export Targets",
						"Audience Strategy",
					];
					filename = `audience-results-summarized-${new Date().toISOString().split("T")[0]}.csv`;
				} else {
					// Comprehensive detailed export with ALL available metrics
					headers = [
						// Basic Information
						"Audience Name",
						"Clean Audience Name",
						"Audience ID",
						"External ID",
						"Account Name",
						"Region",
						"Description",
						"Status",
						"Audience Strategy",
						"Type",
						"Data Source",
						"Export Targets",
						"Technical Attributes",
						"Created With",
						"Primary Persona",

						// Scores
						"Composite Score",
						"Search Score",

						// Core Metrics
						"Unique Users",
						"Requests (CPR Market Impressions)",
						"Sold Impressions",
						"Advertiser Spend",
						"Spend In USD",
						"Avg eCPM",
						"Avg Spend Per Unique User",
						"Impression Frequency",

						// Device Metrics
						"Cookies",
						"Mobile Devices",
						"Connected TV Devices",
						"Tablet Devices",
						"Desktops With Requests",

						// Engagement Metrics
						"Clicks",
						"Click Through Rate",
						"Viewability",
						"Fills",
						"Impressions",
						"Viewable Impressions (Viewables)",

						// Video Metrics
						"Video Starts (Start First Occurrence)",
						"Video Completions (Complete First Occurrence)",

						// Timestamps
						"Created Date",
						"Updated Date",
					];
					filename = `audience-results-comprehensive-${new Date().toISOString().split("T")[0]}.csv`;
				}

				csvContent = headers.join(",") + "\n";

				fullAudienceData.forEach((audience) => {
					const audienceVector = audience.audienceVector || {};
					const audienceMetrics = audience.audienceMetrics || {};

					if (exportType === "summarized") {
						const row = [
							escapeCSV(
								audienceVector.cleanAudienceName ||
									audienceVector.audienceName ||
									"Unknown",
							),
							escapeCSV(audienceVector.audienceId || "N/A"),
							escapeCSV(audienceVector.accountName || "N/A"),
							escapeCSV(audienceVector.region || "N/A"),
							escapeCSV(audienceVector.status || "N/A"),
							escapeCSV((audience.compositeScore || 0).toFixed(4)),
							escapeCSV(formatNumber(audienceMetrics.uniqueUsers)),
							escapeCSV(formatCurrency(audienceMetrics.avgEcpm)),
							escapeCSV(formatNumber(audienceMetrics.weightedImpressions)),
							escapeCSV(formatCurrency(audienceMetrics.advertiserSpend)),
							escapeCSV(
								(() => {
									const targets = audienceVector.exportTargets;
									if (!targets) return "N/A";
									if (Array.isArray(targets)) return targets.join("; ");
									if (typeof targets === "string") {
										try {
											const parsed = JSON.parse(targets);
											return Array.isArray(parsed)
												? parsed.join("; ")
												: targets;
										} catch (e) {
											return targets.replace(/[[\]"]/g, "").replace(/,/g, "; ");
										}
									}
									return String(targets);
								})(),
							),
							escapeCSV(audienceVector.audienceStrategy || "N/A"),
						];
						csvContent += row.join(",") + "\n";
					} else {
						// Comprehensive detailed export with ALL metrics
						const row = [
							// Basic Information
							escapeCSV(audienceVector.audienceName || "Unknown"),
							escapeCSV(audienceVector.cleanAudienceName || "N/A"),
							escapeCSV(audienceVector.audienceId || "N/A"),
							escapeCSV(audienceVector.externalId || "N/A"),
							escapeCSV(audienceVector.accountName || "N/A"),
							escapeCSV(audienceVector.region || "N/A"),
							escapeCSV(
								audienceVector.notesDescription ||
									audienceVector.description ||
									"N/A",
							),
							escapeCSV(audienceVector.status || "N/A"),
							escapeCSV(audienceVector.audienceStrategy || "N/A"),
							escapeCSV(audienceVector.type || "N/A"),
							escapeCSV(audienceVector.dataSource || "N/A"),
							escapeCSV(
								(() => {
									const targets = audienceVector.exportTargets;
									if (!targets) return "N/A";
									if (Array.isArray(targets)) return targets.join("; ");
									if (typeof targets === "string") {
										try {
											const parsed = JSON.parse(targets);
											return Array.isArray(parsed)
												? parsed.join("; ")
												: targets;
										} catch (e) {
											return targets.replace(/[[\]"]/g, "").replace(/,/g, "; ");
										}
									}
									return String(targets);
								})(),
							),
							escapeCSV(audienceVector.technicalAttributes || "N/A"),
							escapeCSV(audienceVector.createdWith || "N/A"),
							escapeCSV(
								audienceVector.primaryPersona ||
									audienceVector.audienceStrategy ||
									"N/A",
							),

							// Scores
							escapeCSV((audience.compositeScore || 0).toFixed(4)),
							escapeCSV(
								(() => {
									const score = audience.score;
									if (typeof score === "number") {
										return relevanceFromDistance(score, "AUDIENCE").toFixed(4);
									}
									return "N/A";
								})(),
							),

							// Core Metrics
							escapeCSV(formatNumber(audienceMetrics.uniqueUsers)),
							escapeCSV(formatNumber(audienceMetrics.cprmarketImpressions)),
							escapeCSV(formatNumber(audienceMetrics.weightedImpressions)),
							escapeCSV(formatCurrency(audienceMetrics.advertiserSpend)),
							escapeCSV(formatCurrency(audienceMetrics.spendInUsd)),
							escapeCSV(formatCurrency(audienceMetrics.avgEcpm)),
							escapeCSV(formatCurrency(audienceMetrics.avgSpendPerUniqueUser)),
							escapeCSV(formatCurrency(audienceMetrics.impressionFrequency)),

							// Device Metrics
							escapeCSV(formatNumber(audienceMetrics.cookies)),
							escapeCSV(formatNumber(audienceMetrics.mobileDevices)),
							escapeCSV(formatNumber(audienceMetrics.connectedTvDevices)),
							escapeCSV(formatNumber(audienceMetrics.tabletDevices)),
							escapeCSV(formatNumber(audienceMetrics.desktopsWithRequests)),

							// Engagement Metrics
							escapeCSV(formatNumber(audienceMetrics.clicks)),
							escapeCSV(formatPercentage(audienceMetrics.clickthroughrate)),
							escapeCSV(formatPercentage(audienceMetrics.viewability)),
							escapeCSV(formatNumber(audienceMetrics.fills)),
							escapeCSV(formatNumber(audienceMetrics.impressions)),
							escapeCSV(formatNumber(audienceMetrics.viewables)),

							// Video Metrics
							escapeCSV(formatNumber(audienceMetrics.startFirstOccurrence)),
							escapeCSV(formatNumber(audienceMetrics.completeFirstOccurrence)),

							// Timestamps
							escapeCSV(
								audienceVector.createdAt || audienceVector.createdDate || "N/A",
							),
							escapeCSV(
								audienceVector.updatedDate ||
									audienceVector.updated_date ||
									"N/A",
							),
						];
						csvContent += row.join(",") + "\n";
					}
				});

				const blob = new Blob([csvContent], {
					type: "text/csv;charset=utf-8;",
				});
				const link = document.createElement("a");
				const url = URL.createObjectURL(blob);
				link.setAttribute("href", url);
				link.setAttribute("download", filename);
				link.style.visibility = "hidden";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);

				console.log(
					`✅ Audience ${exportType} export completed: ${fullAudienceData.length} records`,
				);
				setTimeout(() => setIsExporting(false), 1000);
			} catch (error) {
				console.error("❌ Audience export failed:", error);
				setIsExporting(false);
				alert("Audience export failed. Please try again.");
			}
		},
		[audienceSearchResults, excludedItems],
	);

	// =============================================
	// ENHANCED SEGMENTS EXPORT - TAB-AWARE
	// =============================================
	const buildCategoryDataMap = (segmentsDataToUse) => {
		const categoryMap = {};
		Object.values(segmentsDataToUse).forEach((searchTermData) => {
			if (
				searchTermData &&
				searchTermData.categoryData &&
				Array.isArray(searchTermData.categoryData)
			) {
				searchTermData.categoryData.forEach((categoryItem) => {
					if (categoryItem.segmentIds && categoryItem.categoryType) {
						const segmentIds = categoryItem.segmentIds
							.split(",")
							.map((id) => id.trim())
							.filter((id) => id);
						segmentIds.forEach((segmentId) => {
							categoryMap[segmentId] = categoryItem.categoryType;
						});
					}
				});
			}
		});
		return categoryMap;
	};

	const exportSegmentsToCSV = useCallback(
		(exportType, exportScope = "current") => {
			try {
				setIsExporting(exportType);
				console.log("Starting comprehensive segments export:", {
					exportType,
					exportScope,
				});

				let segmentsToExport = [];
				let exportTitle = "";

				// Use the segments search results data from the segments hook
				const segmentsDataToUse = segmentsSearchResults?.data || {};

				if (exportScope === "current") {
					// Export only current tab data - use segmentsResults which is the current filtered data
					segmentsToExport = segmentsResults ? [...segmentsResults] : [];

					// Include demographics from the current tab if available
					Object.entries(segmentsDataToUse).forEach(([key, data]) => {
						if (
							key.startsWith("Demographic_") &&
							data &&
							Array.isArray(data.data)
						) {
							const demographicSegments = data.data.map((segment) => ({
								...segment,
								sourceTab: key,
							}));
							segmentsToExport.push(...demographicSegments);
						}
					});

					exportTitle = `current-segments`;
				} else if (exportScope === "all") {
					// Export ALL segments from ALL tabs INCLUDING demographic data
					Object.entries(segmentsDataToUse).forEach(([key, data]) => {
						if (data && Array.isArray(data.data)) {
							const tabSegments = data.data.map((segment) => ({
								...segment,
								sourceTab: key, // Add source tab info
							}));
							segmentsToExport.push(...tabSegments);
						}
					});
					exportTitle = "all-segments";
				} else if (exportScope === "selected") {
					// Export only selected segments INCLUDING demographic data if selected
					Object.entries(segmentsDataToUse).forEach(([key, data]) => {
						if (data && Array.isArray(data.data)) {
							const selectedFromTab = data.data
								.filter((segment) =>
									segmentsSelectedResults.includes(
										segment.segmentVector?.segmentId,
									),
								)
								.map((segment) => ({
									...segment,
									sourceTab: key,
								}));
							segmentsToExport.push(...selectedFromTab);

							// Explicitly include demographics (cannot be selected manually)
							if (key.startsWith("Demographic_")) {
								const demographicSegments = data.data.map((segment) => ({
									...segment,
									sourceTab: key,
								}));
								segmentsToExport.push(...demographicSegments);
							}
						}
					});
					exportTitle = "selected-segments";
				}

				if (segmentsToExport.length === 0) {
					console.warn("No segments data available for export");
					alert(`No segments data available for ${exportScope} export.`);
					setIsExporting(false);
					return;
				}

				console.log(
					`Exporting segments (${exportType}/${exportScope}) - Total records: ${segmentsToExport.length}`,
				);

				const escapeCSV = (value) => {
					if (value === null || value === undefined) return "";
					const str = String(value);
					if (
						str.includes(",") ||
						str.includes('"') ||
						str.includes("\n") ||
						str.includes("\r")
					) {
						return `"${str.replace(/"/g, '""')}"`;
					}
					return str;
				};

				const formatNumber = (value) => {
					if (!value || value === "N/A") return "N/A";
					const num = typeof value === "string" ? parseFloat(value) : value;
					if (isNaN(num)) return "N/A";
					return num.toLocaleString();
				};

				const formatCurrency = (value) => {
					if (!value || value === "N/A") return "N/A";
					const num = typeof value === "string" ? parseFloat(value) : value;
					if (isNaN(num)) return "N/A";
					return num.toFixed(4);
				};

				// Build category data map for OA/OAA detection - use the data that's available
				const categoryDataMap = buildCategoryDataMap(segmentsDataToUse);

				const getSegmentCategory = (segment) => {
					const segmentId = segment.segmentVector?.segmentId;
					if (segmentId && categoryDataMap[segmentId]) {
						return categoryDataMap[segmentId];
					}

					// Special handling for demographic segments
					const segmentName = segment.segmentVector?.segmentName || "";
					const sourceTab = segment.sourceTab || "";

					if (
						sourceTab.startsWith("Demographic_") ||
						segmentName.toLowerCase().includes("demographic")
					) {
						return "Demographic";
					}

					// Fallback logic for regular segments
					const category = segment.segmentVector?.category;
					const subCategory = segment.segmentVector?.sub_category;
					if (category === "predefined") {
						if (
							[
								"third_party_data",
								"past_browsing_interests",
								"partner_audiences",
							].includes(subCategory)
						) {
							return "OA";
						} else if (subCategory === "bulk") {
							return "OAA";
						}
					}
					return "OAA"; // Default
				};

				let csvContent = "";
				let headers = [];
				let filename = "";

				if (exportType === "summarized") {
					headers = [
						"Source Tab",
						"Segment Name",
						"Segment ID",
						"Category (OA/OAA/Demographic)",
						"Audience Type",
						"Provider",
						"Composite Score",
						"Search Score",
						"Reach",
						"eCPM",
						"Status",
						"Impressions",
					];
					filename = `${exportTitle}-summarized-${new Date().toISOString().split("T")[0]}.csv`;
				} else {
					// Comprehensive detailed export with ALL available metrics
					headers = [
						// Basic Information
						"Source Tab",
						"Segment Name",
						"Segment ID",
						"External ID",
						"Description",
						"Category L1",
						"Category L2",
						"Category (OA/OAA/Demographic)",
						"Audience Type",
						"Attribute Name",
						"Attribute Value",
						"Account Name",
						"Data Provider",
						"Source",
						"Provider ID",
						"Segment Type",
						"Segment Source",
						"Status",
						"Segment Category",

						// Scores
						"Composite Score",
						"Search Score",

						// Core Metrics
						"Unique Users",
						"Impressions",
						"Sold Impressions",
						"Requests",
						"Advertiser Spend",
						"Avg eCPM",
						"Avg Spend Per Unique User",
						"Impression Frequency",

						// Device Metrics
						"Cookies",
						"Mobile Devices",
						"Connected TV Devices",

						// Weighted Metrics (Performance Data)
						"Weighted Clicks",
						"Weighted CPR Market Impressions",
						"Weighted Fills",
						"Weighted Start First Occurrence",
						"Weighted Complete First Occurrence",
						"Weighted Viewables",
						"Weighted Spend In USD",

						// Additional Metrics
						"Media Types",
						"Allowed Countries",

						// Timestamps
						"Created Date",
						"Updated Date",
					];
					filename = `${exportTitle}-comprehensive-${new Date().toISOString().split("T")[0]}.csv`;
				}

				csvContent = headers.join(",") + "\n";

				segmentsToExport.forEach((segment) => {
					const segmentVector = segment.segmentVector || {};
					const segmentMetrics = segment.segmentMetrics || {};
					const segmentCategory = getSegmentCategory(segment);

					if (exportType === "summarized") {
						const row = [
							escapeCSV(segment.sourceTab || "Unknown"),
							escapeCSV(segmentVector.segmentName || "Unknown Segment"),
							escapeCSV(segmentVector.segmentId || "N/A"),
							escapeCSV(segmentCategory),
							escapeCSV(segmentVector.audienceType || "N/A"),
							escapeCSV(segmentVector.dataProvider || "N/A"),
							escapeCSV((segment.compositeScore || 0).toFixed(4)),
							escapeCSV((segment.score || 0).toFixed(4)),
							escapeCSV(
								formatNumber(segmentMetrics.calculatedReach) ||
									formatNumber(segmentMetrics.uniqueUsers),
							),
							escapeCSV(formatCurrency(segmentMetrics.avgEcpm)),
							escapeCSV(segmentVector.status || "Active"),
							escapeCSV(segmentMetrics.weightedImpressions || 0),
						];
						csvContent += row.join(",") + "\n";
					} else {
						// Comprehensive detailed export
						const row = [
							// Basic Information
							escapeCSV(segment.sourceTab || "Unknown"),
							escapeCSV(segmentVector.segmentName || "Unknown Segment"),
							escapeCSV(segmentVector.segmentId || "N/A"),
							escapeCSV(segmentVector.externalId || "N/A"),
							escapeCSV(segmentVector.description || "N/A"),
							escapeCSV(segmentVector.categoryL1 || "N/A"),
							escapeCSV(segmentVector.categoryL2 || "N/A"),
							escapeCSV(segmentCategory),
							escapeCSV(segmentVector.audienceType || "N/A"),
							escapeCSV(segmentVector.attributeName || "N/A"),
							escapeCSV(segmentVector.attributeValue || "N/A"),
							escapeCSV(segmentVector.accountName || "N/A"),
							escapeCSV(segmentVector.dataProvider || "N/A"),
							escapeCSV(segmentVector.source || "N/A"),
							escapeCSV(segmentVector.providerId || "N/A"),
							escapeCSV(segmentVector.segmentType || "N/A"),
							escapeCSV(segmentVector.segmentSource || "N/A"),
							escapeCSV(segmentVector.status || "Active"),
							escapeCSV(segmentVector.segmentCategory || "N/A"),

							// Scores
							escapeCSV((segment.compositeScore || 0).toFixed(4)),
							escapeCSV((segment.score || 0).toFixed(4)),

							// Core Metrics
							escapeCSV(formatNumber(segmentMetrics.calculatedReach)),
							escapeCSV(formatNumber(segmentMetrics.weightedImpressions)),
							escapeCSV(formatNumber(segmentMetrics.soldImpressions)),
							escapeCSV(formatNumber(segmentMetrics.requests)),
							escapeCSV(formatCurrency(segmentMetrics.advertiserSpend)),
							escapeCSV(formatCurrency(segmentMetrics.avgEcpm)),
							escapeCSV(formatCurrency(segmentMetrics.avgSpendPerUniqueUser)),
							escapeCSV(formatCurrency(segmentMetrics.impressionFrequency)),

							// Device Metrics
							escapeCSV(formatNumber(segmentMetrics.cookies)),
							escapeCSV(formatNumber(segmentMetrics.mobileDevices)),
							escapeCSV(formatNumber(segmentMetrics.connectedTvDevices)),

							// Weighted Metrics
							escapeCSV(formatNumber(segmentMetrics.weightedClicks)),
							escapeCSV(
								formatNumber(segmentMetrics.weightedCprMarketImpressions),
							),
							escapeCSV(formatNumber(segmentMetrics.weightedFills)),
							escapeCSV(
								formatNumber(segmentMetrics.weightedStartFirstOccurrence),
							),
							escapeCSV(
								formatNumber(segmentMetrics.weightedCompleteFirstOccurrence),
							),
							escapeCSV(formatNumber(segmentMetrics.weightedViewables)),
							escapeCSV(formatCurrency(segmentMetrics.weightedSpendInUsd)),

							// Additional Metrics
							escapeCSV(segmentVector.mediaTypes || "N/A"),
							escapeCSV(
								(() => {
									const countries = segmentVector.allowedCountries;
									if (Array.isArray(countries)) return countries.join("; ");
									return countries || "N/A";
								})(),
							),

							// Timestamps
							escapeCSV(segmentVector.createdDate || "N/A"),
							escapeCSV(segmentVector.updatedDate || "N/A"),
						];
						csvContent += row.join(",") + "\n";
					}
				});

				const blob = new Blob([csvContent], {
					type: "text/csv;charset=utf-8;",
				});
				const link = document.createElement("a");
				const url = URL.createObjectURL(blob);
				link.setAttribute("href", url);
				link.setAttribute("download", filename);
				link.style.visibility = "hidden";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);

				console.log(
					`Segments ${exportType}/${exportScope} export completed: ${segmentsToExport.length} records`,
				);
				setTimeout(() => setIsExporting(false), 1000);
			} catch (error) {
				console.error("Segments export failed:", error);
				setIsExporting(false);
				alert("Segments export failed. Please try again.");
			}
		},
		[segmentsSearchResults, segmentsResults, segmentsSelectedResults],
	);

	// =============================================
	// USE EFFECT HOOKS
	// =============================================

	//Authentication setup for segmentsApiService
	useEffect(() => {
		if (
			segmentsApiService &&
			typeof segmentsApiService.onAuthFailure === "function"
		) {
			segmentsApiService.onAuthFailure((error) => {
				if (window.location.pathname !== "/login") {
					window.location.href = "/login";
				}
			});
		}
	}, []);

	// Authentication setup for AppApiService
	useEffect(() => {
		if (appApiService && typeof appApiService.onAuthFailure === "function") {
			appApiService.onAuthFailure((error) => {
				if (window.location.pathname !== "/login") {
					window.location.href = "/login";
				}
			});
		}
	}, []);

	// Load App Prebuilt Sets
	useEffect(() => {
		if (appSearchHook?.loadPrebuiltAppSets) {
			console.log("🔄 Loading app prebuilt sets...");
			appSearchHook
				.loadPrebuiltAppSets()
				.then((result) => {
					const newSets = appSearchHook?.prebuiltAppSets;
					if (result.success && newSets && Object.keys(newSets).length > 0) {
						console.log("✅ App prebuilt sets loaded:", Object.keys(newSets));
						setPersistedPrebuiltAppSets(newSets);
					} else {
						setPersistedPrebuiltAppSets(null);
						console.log(
							"⚠️ Skipping overwrite of prebuilt sets (empty or missing), keeping existing ones",
						);
					}
				})
				.catch((error) => {
					console.error("❌ Failed to load app prebuilt sets:", error);
					setPersistedPrebuiltAppSets(null);
				});
		}
	}, [appSearchHook?.loadPrebuiltAppSets]);

	useEffect(() => {
		console.log("Hook prebuilt sets changed:", {
			hookSetsKeys: hookPrebuiltAppSets ? Object.keys(hookPrebuiltAppSets) : [],
			persistedSetsKeys: persistedPrebuiltAppSets
				? Object.keys(persistedPrebuiltAppSets)
				: [],
		});

		// If hook has new sets, update persisted and clear any cached sets
		if (hookPrebuiltAppSets && Object.keys(hookPrebuiltAppSets).length > 0) {
			console.log("✅ Syncing new prebuilt sets from hook to persisted state");
			setPersistedPrebuiltAppSets(hookPrebuiltAppSets);
		}
		// If hook sets are cleared/empty, also clear persisted sets
		else if (
			hookPrebuiltAppSets &&
			Object.keys(hookPrebuiltAppSets).length === 0 &&
			persistedPrebuiltAppSets
		) {
			console.log("🧹 Hook cleared sets, clearing persisted sets too");
			setPersistedPrebuiltAppSets(null);
		}
	}, [hookPrebuiltAppSets, persistedPrebuiltAppSets]);

	// Load Domain Prebuilt Sets
	useEffect(() => {
		if (hookPrebuiltDomainSets && !persistedPrebuiltDomainSets) {
			console.log(
				"📊 Persisting domain prebuilt sets:",
				Object.keys(hookPrebuiltDomainSets),
			);
			setPersistedPrebuiltDomainSets(hookPrebuiltDomainSets);
		}
	}, [hookPrebuiltDomainSets, persistedPrebuiltDomainSets]);

	useEffect(() => {
		console.log("🔄 SYNC EFFECT TRIGGERED:", {
			hookSetsKeys: hookPrebuiltAppSets ? Object.keys(hookPrebuiltAppSets) : [],
			persistedSetsKeys: persistedPrebuiltAppSets
				? Object.keys(persistedPrebuiltAppSets)
				: [],
			timestamp: new Date().toLocaleTimeString(),
		});

		if (hookPrebuiltAppSets && Object.keys(hookPrebuiltAppSets).length > 0) {
			console.log(
				"✅ UPDATING persistedPrebuiltAppSets with hookPrebuiltAppSets",
			);
			setPersistedPrebuiltAppSets(hookPrebuiltAppSets);
		} else {
			console.log("❌ hookPrebuiltAppSets is empty or null");
		}
	}, [hookPrebuiltAppSets]);

	// Sync Domain Sets
	useEffect(() => {
		if (
			hookPrebuiltDomainSets &&
			Object.keys(hookPrebuiltDomainSets).length > 0
		) {
			setPersistedPrebuiltDomainSets(hookPrebuiltDomainSets);
		}
	}, [hookPrebuiltDomainSets]);

	// Authentication error handling
	useEffect(() => {
		const handleAuthError = (error) => {
			if (
				error?.message?.includes("Authentication failed") ||
				error?.message?.includes("Token refresh failed") ||
				error?.message?.includes("unable to obtain valid token")
			) {
				// Let apiService handle redirect
			}
		};

		window.addEventListener("unhandledrejection", handleAuthError);

		return () => {
			window.removeEventListener("unhandledrejection", handleAuthError);
		};
	}, []);

	// App error handling
	useEffect(() => {
		const handleError = (error) => {
			setAppError(true);
		};

		window.addEventListener("error", handleError);
		window.addEventListener("unhandledrejection", handleError);

		return () => {
			window.removeEventListener("error", handleError);
			window.removeEventListener("unhandledrejection", handleError);
		};
	}, []);

	// Update search results when search data changes
	useEffect(() => {
		if (activeTab === "app" && searchResults?.success && searchResults?.apps) {
			setAppResults(searchResults.apps);
			setAppCurrentPage(1);
			setAppTotalItems(searchResults.totalRecords || searchResults.apps.length);
		}
	}, [searchResults, activeTab]);

	// Update domain/keyword results when search data changes
	useEffect(() => {
		if (
			searchResults?.success &&
			(searchResults?.domains || searchResults?.keywords)
		) {
			if (searchResults.domains && Array.isArray(searchResults.domains)) {
				const transformedDomains = transformApiDomains(searchResults.domains);
				setDomainResults(transformedDomains);
			}

			if (searchResults.keywords && Array.isArray(searchResults.keywords)) {
				const transformedKeywords = transformApiKeywords(
					searchResults.keywords,
				);
				setKeywordResults(transformedKeywords);
			}

			setCurrentPage(1);
		}
	}, [searchResults, transformApiDomains, transformApiKeywords]);

	useEffect(() => {
		if (activeTab === "segments" && segmentsSearchResults?.success) {
			console.log("✅ Processing segments search results from unified hook");

			// Extract all segments from the search results
			let allSegments = [];
			if (
				segmentsSearchResults.segments &&
				Array.isArray(segmentsSearchResults.segments)
			) {
				allSegments = segmentsSearchResults.segments;
			} else if (
				segmentsSearchResults.data &&
				Object.keys(segmentsSearchResults.data).length > 0
			) {
				allSegments = Object.values(segmentsSearchResults.data)
					.filter(
						(categoryData) =>
							categoryData?.data && Array.isArray(categoryData.data),
					)
					.flatMap((categoryData) => categoryData.data);
			}

			setSegmentsResults(allSegments);
			setSegmentsTotalItems(
				segmentsSearchResults.totalRecords || allSegments.length,
			);

			const summary = calculateSegmentsSummary(allSegments);
			setSegmentsSummary(summary);
			setSegmentsCurrentPage(1);
		} else if (
			activeTab === "segments" &&
			segmentsSearchResults &&
			!segmentsSearchResults.success
		) {
			console.error("❌ Segments search failed:", segmentsSearchResults.error);
			setSegmentsResults([]);
			setSegmentsTotalItems(0);
			setSegmentsSummary(null);
		}
	}, [segmentsSearchResults, activeTab]);

	// ✅ FIXED: Simplified audience results effect
	useEffect(() => {
		console.log("🎯 AUDIENCE RESULTS useEffect - Combined hook integration:", {
			activeTab,
			hasSearchResults: !!audienceSearchResults,
			searchResultsSuccess: audienceSearchResults?.success,
			audiencesLength: audienceSearchResults?.audiences?.length || 0,
		});

		if (
			activeTab === "audience" &&
			audienceSearchResults?.success &&
			audienceSearchResults?.audiences
		) {
			console.log(
				"✅ Setting audience results from combined hook:",
				audienceSearchResults.audiences.length,
				"items",
			);

			// Let the hook manage the data - just update summary for UI
			const summary = calculateAudienceSummary(audienceSearchResults.audiences);
			setAudienceSummary(summary);

			// Update pagination state if needed
			setAudienceCurrentPage(1);
			setAudienceTotalItems(
				audienceSearchResults.totalRecords ||
					audienceSearchResults.audiences.length,
			);
		} else if (
			activeTab === "audience" &&
			audienceSearchResults &&
			!audienceSearchResults.success
		) {
			console.error("❌ Audience search failed:", audienceSearchResults.error);
			setAudienceSummary(null);
			setAudienceTotalItems(0);
		}
	}, [audienceSearchResults, activeTab]);

	useEffect(() => {
		console.log("📄 Package hook effect running:", {
			activeTab,
			hookSuccess: packageSearchResults?.success,
			hookPackagesCount: packageSearchResults?.packages?.length || 0,
		});

		if (
			activeTab === "packages" &&
			packageSearchResults?.success &&
			packageSearchResults?.packages
		) {
			console.log("✅ Setting package results from hook");
			setPackageResults(packageSearchResults.packages);
			setPackageTotalItems(
				packageSearchResults.totalRecords ||
					packageSearchResults.packages.length,
			);
			setPackageCurrentPage(1);
		} else if (
			activeTab === "packages" &&
			packageSearchResults &&
			!packageSearchResults.success
		) {
			console.log("❌ Package search failed, clearing results");
			setPackageResults([]);
			setPackageTotalItems(0);
			setPackageSummary(null);
		}
	}, [packageSearchResults, activeTab]);

	// Update local search results
	useEffect(() => {
		setLocalSearchResults({
			domains: domainResults,
			keywords: keywordResults,
			totalRecords: domainResults.length + keywordResults.length,
			searchSummary: null,
		});
	}, [domainResults, keywordResults]);

	// Update pagination when data changes
	useEffect(() => {
		if (activeTab === "domains") {
			const total = getFilteredDataCount();
			setTotalItems(total);
			setTotalPages(Math.ceil(total / pageSize));
		} else if (activeTab === "keywords") {
			const total = getFilteredDataCount();
			setTotalItems(total);
			setTotalPages(Math.ceil(total / pageSize));
		}
	}, [
		domainResults,
		keywordResults,
		activeTab,
		pageSize,
		getFilteredDataCount,
	]);

	// Update select all state
	useEffect(() => {
		try {
			const currentPageData = getCurrentPageData();
			const currentPageIds = currentPageData
				.map((item) => item?.id)
				.filter(Boolean);
			const allCurrentPageSelected =
				currentPageIds.length > 0 &&
				currentPageIds.every((id) => selectedResults.includes(id));

			setIsAllSelected(allCurrentPageSelected);
		} catch (error) {
			// Silent error handling
		}
	}, [selectedResults, getCurrentPageData]);

	const mapLLMSortFieldToDomainField = (llmSortBy) => {
		const mapping = {
			// LLM field name -> Frontend field name
			avgClickThroughRate: "calculated_ctr",
			avgViewability: "avgViewability",
			totalImpressions: "totalImpressions",
			domainReach: "domainReach",
			totalCprMarketRequests: "totalCprMarketRequests",
			compositeScore: "compositeScore",
			domainName: "domainName",
			// Add more mappings as needed
		};

		const mapped = mapping[llmSortBy] || llmSortBy;
		console.log(`🔄 Mapping LLM sort field: ${llmSortBy} → ${mapped}`);
		return mapped;
	};

	const mapLLMSortFieldToAppField = (llmSortBy) => {
		const mapping = {
			// LLM field name -> Frontend field name
			avgClickThroughRate: "calculated_ctr",
			avgViewability: "avgViewability",
			totalImpressions: "totalImpressions",
			domainReach: "domainReach",
			totalCprMarketRequests: "totalCprMarketRequests",
			compositeScore: "compositeScore",
			domainName: "domainName",
			relevance: "score",
		};

		const mapped = mapping[llmSortBy] || llmSortBy;
		console.log(`🔄 Mapping LLM sort field: ${llmSortBy} → ${mapped}`);
		return mapped;
	};

	// ✅ Category AI search results (from the AI Search dropdown → search.jsx)
	useEffect(() => {
		const state = location.state;
		if (!state?.fromCategoryLLMSearch || state?.searchType !== "category") return;

		let payload = null;
		try {
			const stored = sessionStorage.getItem("llmCategorySearchResults");
			payload = stored ? JSON.parse(stored) : window.tempCategoryResults || null;
		} catch (e) {
			payload = window.tempCategoryResults || null;
		}

		if (payload) {
			setActiveTab("category");
			categorySearch.injectResults(payload);
			sessionStorage.removeItem("llmCategorySearchResults");
			delete window.tempCategoryResults;
			window.history.replaceState({}, document.title);
		}
	}, [location.state, categorySearch.injectResults]);

	// ✅ CORRECTED useEffect - Your implementation has some issues

	useEffect(() => {
		const state = location.state;
		const storedDomainResults = sessionStorage.getItem("llmSearchResults");
		const storedAppResults = sessionStorage.getItem("llmAppSearchResults");
		const storedAudienceResults = sessionStorage.getItem(
			"llmAudienceSearchResults",
		);
		const storedSegmentsResults = sessionStorage.getItem(
			"llmSegmentsSearchResults",
		);
		const memoryAudienceResults = window.tempAudienceResults;
		const memorySegmentsResults = window.tempSegmentsResults;

		// AUDIENCE LLM RESULTS PROCESSING - COMPLETE IMPLEMENTATION
		if (
			(state?.fromLLMSearch || state?.fromAudienceLLMSearch) &&
			state?.loadLLMResults &&
			state?.searchType === "audience" &&
			(storedAudienceResults ||
				memoryAudienceResults ||
				state?.audienceResults) &&
			!state?.switchToManual
		) {
			try {
				console.log("Loading Audience LLM Results...");

				// Try multiple sources in priority order
				let audienceLlmResults;
				if (state?.audienceResults) {
					audienceLlmResults = state.audienceResults;
					console.log("Loaded from navigation state");
				} else if (storedAudienceResults) {
					audienceLlmResults = JSON.parse(storedAudienceResults);
					console.log("Loaded from sessionStorage");
				} else if (memoryAudienceResults) {
					audienceLlmResults = memoryAudienceResults;
					console.log("Loaded from memory storage");
				} else {
					throw new Error("No audience results found in any storage");
				}

				console.log("Audience results loaded:", {
					success: audienceLlmResults.success,
					audienceCount: audienceLlmResults.audiences?.length,
					totalRecords: audienceLlmResults.totalRecords,
					truncated: audienceLlmResults.originalData?.metadata?.truncated,
				});

				// Extract search terms from LLM request payload structure
				const extractSearchTermsFromResponse = (llmResults) => {
					const searchTerms = [];

					// Debug: Log the entire structure with more detail
					console.log("Full LLM Results structure for debugging:", {
						llmResults,
						originalData: llmResults.originalData,
						metadata: llmResults.originalData?.metadata,
						// Check for LLM payload in various locations
						originalLlmResponse: llmResults.originalLlmResponse,
						originalAudienceResponse: llmResults.originalAudienceResponse,
					});

					// Try multiple possible paths where the LLM payload might be located
					const possiblePaths = [
						// Check if LLM payload is stored in originalLlmResponse
						llmResults.originalLlmResponse?.data,
						llmResults.originalAudienceResponse?.originalLlmResponse?.data,
						// Check metadata paths
						llmResults.originalData?.metadata?.llmPayload,
						llmResults.metadata?.llmPayload,
						llmResults.llmPayload,
						// Check if it's directly in originalData
						llmResults.originalData?.body?.metadata?.llmPayload,
						llmResults.originalData?.body?.originalLlmResponse?.data,
						// Check other possible locations
						llmResults.originalData?.metadata?.originalLLMPayload,
						llmResults.requestPayload,
						llmResults.searchRequest,
						llmResults.payload,
						llmResults.request,
						// Check if the LLM response is stored at top level somewhere
						llmResults.llmResponse,
						llmResults.originalLlmData,
					];

					let requestData = null;
					let foundPath = null;

					// Find the correct path that contains query and excludeQuery
					for (let i = 0; i < possiblePaths.length; i++) {
						const path = possiblePaths[i];
						if (path && (path.query || path.excludeQuery)) {
							requestData = path;
							foundPath = i;
							console.log(
								"Found query data in LLM payload at path index:",
								i,
								"Data:",
								path,
							);
							break;
						}
					}

					if (requestData) {
						console.log("LLM payload found at path index:", foundPath);
						console.log("LLM payload data:", requestData);

						// Add include terms from query array
						if (requestData.query && Array.isArray(requestData.query)) {
							console.log(
								"Processing include queries from LLM payload:",
								requestData.query,
							);
							requestData.query.forEach((queryItem) => {
								if (queryItem.query && queryItem.threshold) {
									searchTerms.push({
										text: queryItem.query,
										query: queryItem.query,
										type: "include",
										// Display % derived from the distance via the Audience curve
										score: Math.round(
											relevanceFromDistance(queryItem.threshold, "AUDIENCE"),
										),
										threshold: queryItem.threshold,
									});
								}
							});
						}

						// Add exclude terms from excludeQuery array
						if (
							requestData.excludeQuery &&
							Array.isArray(requestData.excludeQuery)
						) {
							console.log(
								"Processing exclude queries from LLM payload:",
								requestData.excludeQuery,
							);
							requestData.excludeQuery.forEach((excludeItem) => {
								if (excludeItem.query && excludeItem.threshold) {
									searchTerms.push({
										text: excludeItem.query,
										query: excludeItem.query,
										type: "exclude",
										// Display % derived from the distance via the Audience curve
										score: Math.round(
											relevanceFromDistance(excludeItem.threshold, "AUDIENCE"),
										),
										threshold: excludeItem.threshold,
									});
								}
							});
						}
					} else {
						console.warn(
							"Could not find LLM payload with query/excludeQuery structure",
						);
						console.log(
							"Available paths checked:",
							possiblePaths.map((path, index) => `${index}: ${!!path}`),
						);

						// Additional debugging - let's see what keys are available at different levels
						console.log("Debug - Available keys at different levels:");
						console.log("llmResults keys:", Object.keys(llmResults));
						console.log(
							"originalData keys:",
							llmResults.originalData
								? Object.keys(llmResults.originalData)
								: "none",
						);
						console.log(
							"metadata keys:",
							llmResults.originalData?.metadata
								? Object.keys(llmResults.originalData.metadata)
								: "none",
						);

						if (llmResults.originalLlmResponse) {
							console.log(
								"originalLlmResponse keys:",
								Object.keys(llmResults.originalLlmResponse),
							);
							if (llmResults.originalLlmResponse.data) {
								console.log(
									"originalLlmResponse.data keys:",
									Object.keys(llmResults.originalLlmResponse.data),
								);
							}
						}
					}

					// Fallback to original query if no terms found in request
					if (searchTerms.length === 0) {
						console.log("No terms extracted from LLM payload, using fallback");
						searchTerms.push({
							text:
								llmResults.originalData?.metadata?.originalQuery ||
								"Audience LLM Search",
							query:
								llmResults.originalData?.metadata?.originalQuery ||
								"Audience LLM Search",
							type: "include",
							score: AUDIENCE_DEFAULT_RELEVANCE,
							threshold: AUDIENCE_DEFAULT_THRESHOLD,
						});
					}

					console.log("Final extracted search terms:", searchTerms);
					return searchTerms;
				};

				// Use the extracted search terms from metadata, or extract them if missing
				let extractedSearchTerms =
					audienceLlmResults.originalData?.metadata?.extractedSearchTerms;

				console.log(
					"Extracted search terms from LLM response:",
					extractedSearchTerms,
				);

				// If not found in metadata, try to extract from stored LLM payload as fallback
				if (
					!extractedSearchTerms &&
					audienceLlmResults.originalData?.metadata?.llmPayload
				) {
					console.log(
						"Search terms not in metadata, extracting from stored LLM payload...",
					);
					const llmPayload =
						audienceLlmResults.originalData.metadata.llmPayload;
					extractedSearchTerms = [];

					// Add include terms from query array
					if (llmPayload.query && Array.isArray(llmPayload.query)) {
						llmPayload.query.forEach((queryItem) => {
							if (queryItem.query && queryItem.threshold) {
								extractedSearchTerms.push({
									text: queryItem.query,
									query: queryItem.query,
									type: "include",
									score: Math.round(
										relevanceFromDistance(queryItem.threshold, "AUDIENCE"),
									),
									threshold: queryItem.threshold,
								});
							}
						});
					}

					// Add exclude terms from excludeQuery array
					if (
						llmPayload.excludeQuery &&
						Array.isArray(llmPayload.excludeQuery)
					) {
						llmPayload.excludeQuery.forEach((excludeItem) => {
							if (excludeItem.query && excludeItem.threshold) {
								extractedSearchTerms.push({
									text: excludeItem.query,
									query: excludeItem.query,
									type: "exclude",
									score: Math.round(
										relevanceFromDistance(excludeItem.threshold, "AUDIENCE"),
									),
									threshold: excludeItem.threshold,
								});
							}
						});
					}

					console.log("Fallback extraction completed:", extractedSearchTerms);
				}

				// Final fallback if still no terms found
				if (!extractedSearchTerms || extractedSearchTerms.length === 0) {
					console.log("No terms extracted, using fallback");
					extractedSearchTerms = [
						{
							text:
								audienceLlmResults.originalData?.metadata?.originalQuery ||
								"Audience LLM Search",
							query:
								audienceLlmResults.originalData?.metadata?.originalQuery ||
								"Audience LLM Search",
							type: "include",
							score: AUDIENCE_DEFAULT_RELEVANCE,
							threshold: AUDIENCE_DEFAULT_THRESHOLD,
						},
					];
				}

				console.log(
					"Final extracted search terms to be used:",
					extractedSearchTerms,
				);
				// Separate include and exclude terms for proper UI handling
				const includeTerms = extractedSearchTerms.filter(
					(term) => term.type === "include",
				);
				const excludeTerms = extractedSearchTerms.filter(
					(term) => term.type === "exclude",
				);

				console.log("Separated terms:", { includeTerms, excludeTerms });

				const llmSortBy =
					audienceLlmResults.originalData?.metadata?.llmResponse?.sortBy ||
					audienceLlmResults.sortBy ||
					"compositeScore"; // default fallback

				const llmSortOrder =
					audienceLlmResults.originalData?.metadata?.llmResponse?.view ||
					audienceLlmResults.view ||
					"DESC"; // default fallback

				console.log("📊 Extracted sort settings from LLM:", {
					results: audienceLlmResults,
					sortBy: llmSortBy,
					sortOrder: llmSortOrder,
				});

				setAudienceSortBy(llmSortBy);
				setAudienceSortOrder(llmSortOrder);

				// Store LLM search data for persistence
				setLlmSearchData({
					type: "audience",
					searchTerms: includeTerms, // Only include terms
					excludeTerms: excludeTerms, // Only exclude terms
					filters: audienceLlmResults.appliedFilters,
					results: audienceLlmResults,
					searchOptions: {
						performanceMetric: "compositeScore",
						overrideResults: audienceLlmResults,
						sortBy: llmSortBy,
						sortOrder: llmSortOrder,
					},
				});
				setIsLlmResultsActive(true);

				// Switch to audience tab
				console.log("Switching to audience tab...");
				setActiveTab("audience");

				if (
					audienceLlmResults.appliedFilters &&
					typeof audienceLlmResults.appliedFilters === "object"
				) {
					console.log(
						"Applying Audience filters individually:",
						audienceLlmResults.appliedFilters,
					);

					// Apply filters sequentially with delays (same pattern as apps and segments)
					const applyAudienceFiltersInSequence = (appliedFilters) => {
						const filterEntries = Object.entries(appliedFilters);

						filterEntries.forEach(([filterKey, filterValue], index) => {
							setTimeout(() => {
								try {
									// Ensure filterKey is a string
									const key = String(filterKey);
									console.log(`Applying audience filter ${key}:`, filterValue);
									if (typeof audienceUpdateFilter === "function") {
										audienceUpdateFilter(key, filterValue);
									}
								} catch (filterError) {
									console.warn(
										"Failed to apply audience filter:",
										filterKey,
										filterError,
									);
								}
							}, index * 15);
						});
					};

					// Apply filters after tab switch with delay
					setTimeout(() => {
						applyAudienceFiltersInSequence(audienceLlmResults.appliedFilters);
					}, 300);
				}

				if (
					audienceLlmResults.audiences &&
					Array.isArray(audienceLlmResults.audiences)
				) {
					console.log(
						"Processing audience results:",
						audienceLlmResults.audiences.length,
						"audiences",
					);

					// Show warning if results were truncated due to storage limitations
					if (audienceLlmResults.originalData?.metadata?.truncated) {
						const originalCount =
							audienceLlmResults.originalData.metadata.originalCount;
						console.warn(
							`Results were truncated: showing ${audienceLlmResults.audiences.length} of ${originalCount} total results`,
						);
						setError(
							`Large result set detected: showing first ${audienceLlmResults.audiences.length} of ${originalCount} results`,
						);
					}

					// Use a delay to ensure the tab switch completes and hook is ready
					setTimeout(() => {
						console.log("Injecting audience results...");

						if (typeof audienceTriggerSearchWithOverride === "function") {
							console.log("Using audienceTriggerSearchWithOverride method...");
							audienceTriggerSearchWithOverride(
								extractedSearchTerms,
								audienceLlmResults,
							)
								.then(() => {
									console.log("Audience LLM results successfully injected");
									setAudienceCurrentPage(1);
									setAudienceTotalItems(
										audienceLlmResults.totalRecords ||
											audienceLlmResults.audiences.length,
									);
								})
								.catch((err) => {
									console.error("Failed to inject audience results:", err);
								});
						} else if (typeof audienceInjectSearchResults === "function") {
							console.log("Using audienceInjectSearchResults method...");

							// Add all search terms
							extractedSearchTerms.forEach((term) => {
								if (typeof audienceAddSearchTerm === "function") {
									audienceAddSearchTerm(term.text, {
										score: term.score,
										type: term.type,
										threshold: term.threshold,
									});
								}
							});

							// Then inject results
							audienceInjectSearchResults(audienceLlmResults);

							// Update page state
							setAudienceCurrentPage(1);
							setAudienceTotalItems(
								audienceLlmResults.totalRecords ||
									audienceLlmResults.audiences.length,
							);
						} else {
							console.log("Using fallback - normal audience search...");
							console.log("Available functions:", {
								audienceTriggerSearchWithOverride:
									typeof audienceTriggerSearchWithOverride,
								audienceInjectSearchResults: typeof audienceInjectSearchResults,
								audienceAddSearchTerm: typeof audienceAddSearchTerm,
								audienceTriggerSearch: typeof audienceTriggerSearch,
							});

							if (
								typeof audienceAddSearchTerm === "function" &&
								typeof audienceTriggerSearch === "function"
							) {
								// Add all extracted search terms
								extractedSearchTerms.forEach((term) => {
									audienceAddSearchTerm(term.text, {
										score: term.score,
										type: term.type,
										threshold: term.threshold,
									});
								});

								setTimeout(() => {
									audienceTriggerSearch();
								}, 100);
							} else {
								console.error("No audience search functions available");
								setError(
									"Failed to load audience results: Search functions not available",
								);
							}
						}
					}, 200);
				} else {
					console.warn("No audience data found in LLM results");
					setError("No audience data found in search results");
				}

				console.log("Audience LLM results processing completed");

				// Clean up all possible storage locations
				if (storedAudienceResults) {
					sessionStorage.removeItem("llmAudienceSearchResults");
				}
				if (memoryAudienceResults) {
					delete window.tempAudienceResults;
				}

				window.history.replaceState({}, document.title);
			} catch (e) {
				console.error("Audience LLM processing failed:", e);
				setError("Failed to load audience results: " + e.message);
			}
		}

		// SEGMENTS LLM RESULTS PROCESSING - ENHANCED WITH PROPER SEARCH TERM SYNC
		else if (
			(state?.fromLLMSearch || state?.fromSegmentsLLMSearch) &&
			state?.loadLLMResults &&
			state?.searchType === "segments" &&
			(storedSegmentsResults || window.tempSegmentsResults) &&
			!state?.switchToManual
		) {
			try {
				console.log("🧩 Loading Segments LLM Results...");

				// Try multiple sources in priority order
				let segmentsLlmResults;
				if (storedSegmentsResults) {
					segmentsLlmResults = JSON.parse(storedSegmentsResults);
					console.log("Loaded from sessionStorage");
				} else if (window.tempSegmentsResults) {
					segmentsLlmResults = window.tempSegmentsResults;
					console.log("Loaded from memory storage");
				} else {
					throw new Error("No segments results found in any storage");
				}

				console.log("Segments results loaded:", {
					success: segmentsLlmResults.success,
					hasData: !!segmentsLlmResults.data,
					dataType: typeof segmentsLlmResults.data,
					appliedFiltersKeys: segmentsLlmResults.appliedFilters
						? Object.keys(segmentsLlmResults.appliedFilters)
						: [],
					totalRecords: segmentsLlmResults.totalRecords,
					originalQuery:
						segmentsLlmResults.originalData?.metadata?.originalQuery,
				});

				// Extract the original search terms from the NLP payload
				const originalQuery =
					segmentsLlmResults.originalData?.metadata?.originalQuery ||
					segmentsLlmResults.metadata?.originalQuery ||
					"Segments LLM Search";

				const llmSortBy = segmentsLlmResults.sortBy || "compositeScore";
				const llmSortOrder = segmentsLlmResults.view || "DESC";
				// Store LLM search data for persistence
				setLlmSearchData({
					type: "segments",
					searchTerms: [
						{
							text: originalQuery,
							type: "include",
							score: 0.75,
							id: Date.now(),
						},
					],
					filters: segmentsLlmResults.appliedFilters,
					results: segmentsLlmResults,
					searchOptions: {
						performanceMetric: "compositeScore",
						overrideResults: segmentsLlmResults,
						preventAutoSearch: true,
						sortBy: llmSortBy,
						sortOrder: llmSortOrder,
					},
				});
				setIsLlmResultsActive(true);

				setSegmentsSortBy(llmSortBy);
				setSegmentsSortOrder(llmSortOrder);

				// Switch to segments tab
				console.log("Switching to segments tab...");
				setActiveTab("segments");

				if (
					segmentsLlmResults.appliedFilters &&
					typeof segmentsLlmResults.appliedFilters === "object"
				) {
					console.log(
						"🔧 Applying Segments filters individually:",
						segmentsLlmResults.appliedFilters,
					);

					// Apply filters sequentially with delays (same pattern as apps)
					const applySegmentsFiltersInSequence = (appliedFilters) => {
						const filterEntries = Object.entries(appliedFilters);

						filterEntries.forEach(([filterKey, filterValue], index) => {
							setTimeout(() => {
								try {
									// Ensure filterKey is a string
									const key = String(filterKey);
									console.log(`Applying segments filter ${key}:`, filterValue);
									if (typeof handleSegmentsUpdateFilter === "function") {
										// If the function expects key, value format
										handleSegmentsUpdateFilter(key, filterValue, {
											silent: true,
										});
									}
								} catch (filterError) {
									console.warn(
										"Failed to apply segments filter:",
										filterKey,
										filterError,
									);
								}
							}, index * 15);
						});
					};

					// Apply filters after tab switch with delay
					setTimeout(() => {
						applySegmentsFiltersInSequence(segmentsLlmResults.appliedFilters);
					}, 300);
				}

				// 🔑 CRITICAL: Clear any existing search terms first to prevent conflicts
				if (typeof handleSegmentsResetSearch === "function") {
					console.log("🧹 Clearing existing search state...");
					handleSegmentsResetSearch();
				}

				// Create proper search terms that match the NLP payload
				const nlpSearchTerms = [
					{
						text: originalQuery,
						type: "include",
						score: 75,
						id: Date.now(),
					},
				];

				// 🔑 CRITICAL: Use longer delay to ensure all hooks are ready and state is cleared
				setTimeout(() => {
					console.log(
						"🔧 Injecting NLP segments results with proper search terms...",
					);

					// Step 1: Clear any existing search terms
					if (
						typeof segmentsSearchTerms !== "undefined" &&
						segmentsSearchTerms.length > 0
					) {
						console.log(
							"⚠️ Warning: Existing search terms detected, clearing them first",
						);
						segmentsSearchTerms.forEach((term) => {
							if (typeof handleSegmentsRemoveSearchTerm === "function") {
								handleSegmentsRemoveSearchTerm(term.id);
							}
						});
					}

					// Step 2: Add the NLP search term
					if (typeof handleSegmentsAddSearchTerm === "function") {
						console.log("➕ Adding NLP search term:", originalQuery);
						handlePackageAddSearchTerm(originalQuery, {
							type: "include",
							score: 75,
						});
						setTimeout(() => {
							console.log(
								"DEBUG: Package search terms after adding:",
								packageSearchTerms,
							);
							console.log(
								"DEBUG: Package exclude terms after adding:",
								packageExcludeTerms,
							);
						}, 100);
					}

					setTimeout(() => {
						console.log("DEBUG: LLM data before processing:", {
							hasData: !!segmentsLlmResults.data,
							dataType: typeof segmentsLlmResults.data,
							dataKeys: segmentsLlmResults.data
								? Object.keys(segmentsLlmResults.data)
								: [],
							hasBody: !!segmentsLlmResults.data?.body,
							bodyKeys: segmentsLlmResults.data?.body
								? Object.keys(segmentsLlmResults.data.body)
								: [],
						});

						// FIX: Use the direct data - check if there's a body property, otherwise use data directly
						const directData =
							segmentsLlmResults.data?.body || segmentsLlmResults.data || {};

						console.log("DEBUG: Direct data to process:", {
							directDataKeys: Object.keys(directData),
							directDataType: typeof directData,
							firstKey: Object.keys(directData)[0],
							firstKeyStructure: directData[Object.keys(directData)[0]],
						});

						// Extract segments array from all categories
						const allSegments = [];
						if (typeof directData === "object") {
							Object.entries(directData).forEach(
								([categoryKey, categoryData]) => {
									console.log(`Processing category: ${categoryKey}`, {
										hasData: !!categoryData?.data,
										dataLength: categoryData?.data?.length,
										totalCount: categoryData?.totalCount,
									});

									if (categoryData?.data && Array.isArray(categoryData.data)) {
										allSegments.push(...categoryData.data);
									}
								},
							);
						}

						console.log("DEBUG: Extracted segments:", {
							segmentsCount: allSegments.length,
							directDataKeys: Object.keys(directData),
							sampleSegment: allSegments[0],
						});

						// Set data directly in the state variables
						setSegmentsResults(allSegments);
						setSegmentsTotalItems(allSegments.length);

						const summary = calculateSegmentsSummary(allSegments);
						setSegmentsSummary(summary);
						setSegmentsCurrentPage(1);

						// Create proper search results - PASS THE CORRECT STRUCTURE
						const mockSearchResults = {
							success: true,
							data: directData, // This should be the category object structure
							body: directData, // Also include as body for compatibility
							segments: allSegments,
							totalRecords: allSegments.length,
							appliedFilters: segmentsLlmResults.appliedFilters || {},
						};

						console.log("DEBUG: mockSearchResults structure:", {
							success: mockSearchResults.success,
							hasData: !!mockSearchResults.data,
							dataKeys: Object.keys(mockSearchResults.data),
							segmentsCount: mockSearchResults.segments.length,
							totalRecords: mockSearchResults.totalRecords,
						});

						// Clear and add search terms
						if (typeof handleSegmentsResetSearch === "function") {
							handleSegmentsResetSearch();
						}

						setTimeout(() => {
							let searchTermsFromPayload = [];

							// FIRST: Try to get from the original query array in metadata
							if (segmentsLlmResults.originalData?.query) {
								console.log("📋 Extracting from originalData.query");
								searchTermsFromPayload =
									segmentsLlmResults.originalData.query.map(
										(queryItem, index) => ({
											text: queryItem.query.trim(),
											type: "include",
											score: 75,
											id: `llm_term_${Date.now()}_${index}`,
										}),
									);
							}
							// SECOND: Try metadata.query (if stored differently)
							else if (segmentsLlmResults.metadata?.query) {
								console.log("📋 Extracting from metadata.query");
								searchTermsFromPayload = segmentsLlmResults.metadata.query.map(
									(queryItem, index) => ({
										text: queryItem.query.trim(),
										type: "include",
										score: 75,
										id: `llm_term_${Date.now()}_${index}`,
									}),
								);
							}
							// THIRD: Check appliedFilters for query
							else if (segmentsLlmResults.appliedFilters?.query) {
								console.log("📋 Extracting from appliedFilters.query");
								searchTermsFromPayload =
									segmentsLlmResults.appliedFilters.query.map(
										(queryItem, index) => ({
											text: queryItem.query.trim(),
											type: "include",
											score: 75,
											id: `llm_term_${Date.now()}_${index}`,
										}),
									);
							}
							// FOURTH: Extract from data keys (but filter out demographic keys properly)
							else if (directData && typeof directData === "object") {
								console.log("📋 Extracting from direct data object keys");
								const excludedPatterns = [
									"demographic",
									"default",
									"Demographic_Location",
								];
								const dataKeys = Object.keys(directData);

								searchTermsFromPayload = dataKeys
									.filter((key) => {
										const lowerKey = key.toLowerCase();
										return !excludedPatterns.some((pattern) =>
											lowerKey.includes(pattern.toLowerCase()),
										);
									})
									.filter((key) => key.trim().length > 0)
									.map((term, index) => ({
										text: term.trim(),
										type: "include",
										score: 75,
										id: `llm_term_${Date.now()}_${index}`,
									}));

								console.log(
									"Filtered data keys:",
									dataKeys,
									"→",
									searchTermsFromPayload.map((t) => t.text),
								);
							}
							// FINAL FALLBACK: Use original query string
							else {
								console.log("📋 Using fallback: original query string");
								const fallbackTerms = originalQuery
									.split(/[,\s]+/)
									.filter((term) => term.trim().length > 0);
								searchTermsFromPayload = fallbackTerms.map((term, index) => ({
									text: term.trim(),
									type: "include",
									score: 75,
									id: `llm_fallback_${Date.now()}_${index}`,
								}));
							}

							console.log(
								"✅ Final search terms to add:",
								searchTermsFromPayload,
							);

							// Add terms sequentially
							const addTermsSequentially = async (terms) => {
								for (const term of terms) {
									if (typeof handleSegmentsAddSearchTerm === "function") {
										console.log("Adding term:", term.text);
										handleSegmentsAddSearchTerm(
											"include",
											term.text,
											term.score,
										);
										await new Promise((resolve) => setTimeout(resolve, 50));
									}
								}
							};

							addTermsSequentially(searchTermsFromPayload).then(() => {
								console.log("All terms added, injecting results...");
								setTimeout(() => {
									if (typeof segmentsInjectSearchResults === "function") {
										console.log(
											"🎯 Injecting search results:",
											mockSearchResults,
										);
										segmentsInjectSearchResults(mockSearchResults);
									}
								}, 100);
							});
						}, 100);
					}, 200);
				}, 500); // Longer delay to ensure tab switch and hook initialization

				console.log("🎯 Segments LLM results processing setup completed");

				// Clean up storage
				if (storedSegmentsResults) {
					sessionStorage.removeItem("llmSegmentsSearchResults");
				}
				if (window.tempSegmentsResults) {
					delete window.tempSegmentsResults;
				}
				window.history.replaceState({}, document.title);
			} catch (e) {
				console.error("❌ Segments LLM processing failed:", e);
				setError("Failed to load segments results: " + e.message);
			}
		} else if (
			(state?.fromLLMSearch || state?.fromPackagesLLMSearch) &&
			state?.loadLLMResults &&
			state?.searchType === "packages" &&
			(sessionStorage.getItem("llmPackagesSearchResults") ||
				window.tempPackagesResults ||
				state?.packagesResults) &&
			!state?.switchToManual
		) {
			function extractAndNormalizePackageFilters(llmResults) {
				const normalizedFilters = {};

				// Get filters from the response
				const appliedFilters =
					llmResults?.data?.appliedFilters || llmResults?.appliedFilters || {};

				console.log("🔍 Raw applied filters:", appliedFilters);

				// Handle nested filter structure (packageVector, packageMetrics, etc.)
				if (appliedFilters.packageVector) {
					Object.entries(appliedFilters.packageVector).forEach(
						([key, value]) => {
							if (value !== null && value !== undefined) {
								normalizedFilters[key] = value;
							}
						},
					);
				}

				if (appliedFilters.packageMetrics) {
					Object.entries(appliedFilters.packageMetrics).forEach(
						([key, value]) => {
							if (value !== null && value !== undefined) {
								// Convert bidCpm back from micro units
								if (key === "bidCpm" && typeof value === "number") {
									normalizedFilters[key] = value / 1000000;
								} else {
									normalizedFilters[key] = value;
								}
							}
						},
					);
				}

				// Handle flat structure (fallback)
				Object.entries(appliedFilters).forEach(([key, value]) => {
					if (
						![
							"packageVector",
							"packageMetrics",
							"cumulativeDataFilters",
							"sortBy",
							"sortOrder",
						].includes(key)
					) {
						if (value !== null && value !== undefined) {
							normalizedFilters[key] = value;
						}
					}
				});

				// Clean up empty arrays
				Object.keys(normalizedFilters).forEach((key) => {
					if (
						Array.isArray(normalizedFilters[key]) &&
						normalizedFilters[key].length === 0
					) {
						delete normalizedFilters[key];
					}
				});

				console.log("✅ Normalized filters:", normalizedFilters);
				return normalizedFilters;
			}

			try {
				console.log("📦 Loading Package LLM Results...");

				// Try multiple sources in priority order
				let packagesLlmResults;
				const storedPackagesResults = sessionStorage.getItem(
					"llmPackagesSearchResults",
				);

				if (state?.packagesResults) {
					packagesLlmResults = state.packagesResults;
					console.log("Loaded packages from navigation state");
				} else if (storedPackagesResults) {
					packagesLlmResults = JSON.parse(storedPackagesResults);
					console.log("Loaded packages from sessionStorage");
				} else if (window.tempPackagesResults) {
					packagesLlmResults = window.tempPackagesResults;
					console.log("Loaded packages from memory storage");
				} else {
					throw new Error("No packages results found in any storage");
				}

				console.log(
					"🔍 FULL LLM STRUCTURE:",
					JSON.stringify(packagesLlmResults, null, 2),
				);

				// ✅ CRITICAL FIX 1: Set isLlmResultsActive IMMEDIATELY at the start
				console.log("🚩 Setting isLlmResultsActive to TRUE immediately");
				setIsLlmResultsActive(true);

				// ✅ DEFENSIVE: Override the clear function temporarily to prevent clearing
				const originalClearFunction = handlePackageClearSearchTerms;
				if (typeof handlePackageClearSearchTerms === "function") {
					window.handlePackageClearSearchTerms = () => {
						console.log(
							"🛑 BLOCKED: Attempted to clear search terms during LLM load",
						);
						console.trace("Call stack:");
						// Don't actually clear
					};
					console.log("🛡️ Temporarily disabled handlePackageClearSearchTerms");
				}

				// Extract sort configuration
				const llmSortBy =
					packagesLlmResults?.data?.appliedFilters?.sortBy || "relevanceScore";
				const llmSortOrder = (
					packagesLlmResults?.data?.appliedFilters?.sortOrder || "DESC"
				).toLowerCase();

				console.log("LLM PACKAGE SORT--", llmSortBy, llmSortOrder);

				setPackageSortColumn(llmSortBy);
				setPackageSortDirection(llmSortOrder);

				// Extract and normalize filters from LLM response
				const normalizedFilters =
					extractAndNormalizePackageFilters(packagesLlmResults);
				console.log("📋 Normalized filters from LLM:", normalizedFilters);

				// ✅ CRITICAL FIX 2: Extract BOTH include and exclude terms from normalizedFilters
				const llmSearchTerms = [];

				// Extract INCLUDE term from searchQuery
				let searchQueryText = "";
				if (
					normalizedFilters.searchQuery &&
					typeof normalizedFilters.searchQuery === "string"
				) {
					searchQueryText = normalizedFilters.searchQuery;
					console.log(
						"📝 Found searchQuery in normalizedFilters:",
						searchQueryText,
					);

					llmSearchTerms.push({
						text: searchQueryText,
						type: "include",
						score: Math.round(
							relevanceFromDistance(PACKAGE_INCLUDE_THRESHOLD, "PACKAGES"),
						),
						threshold: PACKAGE_INCLUDE_THRESHOLD,
					});
				}

				// ✅ NEW: Extract EXCLUDE term from excludeQuery
				let excludeQueryText = "";
				if (
					normalizedFilters.excludeQuery &&
					typeof normalizedFilters.excludeQuery === "string"
				) {
					excludeQueryText = normalizedFilters.excludeQuery;
					console.log(
						"📝 Found excludeQuery in normalizedFilters:",
						excludeQueryText,
					);

					llmSearchTerms.push({
						text: excludeQueryText,
						type: "exclude",
						score: Math.round(
							relevanceFromDistance(PACKAGE_EXCLUDE_THRESHOLD, "PACKAGES"),
						),
						threshold: PACKAGE_EXCLUDE_THRESHOLD,
					});
				}

				// Store search query in window for persistence
				window.llmPackageSearchQuery = searchQueryText;
				console.log(
					"💾 Stored search query in window.llmPackageSearchQuery:",
					window.llmPackageSearchQuery,
				);

				console.log("✅ Final search terms to use:", llmSearchTerms);
				console.log(
					"   - Include terms:",
					llmSearchTerms.filter((t) => t.type === "include").map((t) => t.text),
				);
				console.log(
					"   - Exclude terms:",
					llmSearchTerms.filter((t) => t.type === "exclude").map((t) => t.text),
				);

				if (llmSearchTerms.length === 0) {
					console.warn("⚠️ No search terms found in any location");
				}

				// Store LLM search data with normalized filters AND search terms
				setLlmSearchData({
					type: "packages",
					searchTerms: llmSearchTerms,
					filters: normalizedFilters,
					results: packagesLlmResults,
					searchOptions: {
						performanceMetric: "relevanceScore",
						overrideResults: packagesLlmResults,
						sortBy: llmSortBy,
						sortOrder: llmSortOrder,
					},
				});

				// Extract packages from LLM results
				let extractedPackages = [];

				if (
					packagesLlmResults.data?.packages &&
					Array.isArray(packagesLlmResults.data.packages)
				) {
					extractedPackages = packagesLlmResults.data.packages;
					console.log("✅ Using data.packages:", extractedPackages.length);
				} else if (
					packagesLlmResults.packages &&
					Array.isArray(packagesLlmResults.packages)
				) {
					extractedPackages = packagesLlmResults.packages;
					console.log("✅ Using direct packages:", extractedPackages.length);
				} else if (
					packagesLlmResults.data?.queryResults &&
					typeof packagesLlmResults.data.queryResults === "object"
				) {
					Object.values(packagesLlmResults.data.queryResults).forEach(
						(queryResult) => {
							if (queryResult?.data && Array.isArray(queryResult.data)) {
								extractedPackages.push(...queryResult.data);
							}
						},
					);
					console.log(
						"✅ Extracted from data.queryResults:",
						extractedPackages.length,
					);
				} else if (
					packagesLlmResults.queryResults &&
					typeof packagesLlmResults.queryResults === "object"
				) {
					Object.values(packagesLlmResults.queryResults).forEach(
						(queryResult) => {
							if (queryResult?.data && Array.isArray(queryResult.data)) {
								extractedPackages.push(...queryResult.data);
							}
						},
					);
					console.log(
						"✅ Extracted from queryResults:",
						extractedPackages.length,
					);
				}

				// Show warning if results were truncated
				if (packagesLlmResults.originalData?.metadata?.truncated) {
					const originalCount =
						packagesLlmResults.originalData.metadata.originalCount;
					console.warn(
						`Results truncated: showing ${extractedPackages.length} of ${originalCount}`,
					);
					setError(
						`Showing first ${extractedPackages.length} of ${originalCount} results`,
					);
				}

				if (extractedPackages.length === 0) {
					console.warn("No package data found in LLM results");
					setError("No package data found in search results");
				}

				console.log(
					"🔧 Setting package results in state:",
					extractedPackages.length,
				);

				// ✅ CRITICAL FIX 3: Set results immediately, don't wait
				setPackageResults(extractedPackages);
				setPackageTotalItems(
					packagesLlmResults.totalRecords || extractedPackages.length,
				);
				setPackageCurrentPage(1);
				setPackageLlmResults(packagesLlmResults);

				// ✅ FIX 4: Switch to packages tab
				console.log("Switching to packages tab...");
				setActiveTab("packages");

				// ✅ FIX 5: Apply filters after a small delay
				setTimeout(() => {
					console.log("🔧 Applying filters to sidebar...");

					Object.entries(normalizedFilters).forEach(
						([filterKey, filterValue]) => {
							try {
								if (filterValue !== null && filterValue !== undefined) {
									// Skip empty arrays and searchQuery
									if (Array.isArray(filterValue) && filterValue.length === 0) {
										return;
									}
									if (filterKey === "searchQuery") {
										return; // Skip searchQuery in filter application
									}

									console.log(`Applying filter ${filterKey}:`, filterValue);
									if (typeof handlePackageUpdateFilter === "function") {
										handlePackageUpdateFilter(filterKey, filterValue);
									}
								}
							} catch (filterError) {
								console.warn("Failed to apply filter:", filterKey, filterError);
							}
						},
					);
				}, 300);

				// ✅ CRITICAL FIX 6: Add search terms with increased delays and protection
				setTimeout(() => {
					console.log("🔧 NOW adding search terms...");
					console.log("🔍 Search terms to add:", llmSearchTerms);
					console.log("🔍 Current isLlmResultsActive:", isLlmResultsActive);

					if (llmSearchTerms.length === 0) {
						console.warn("⚠️ No search terms to add!");
						return;
					}

					// ✅ DEFENSIVE: Block any search triggers during term addition
					const originalTriggerSearch = window.packageTriggerSearch;
					if (typeof window.packageTriggerSearch === "function") {
						window.packageTriggerSearch = () => {
							console.log(
								"🛑 BLOCKED: Search trigger prevented during LLM term addition",
							);
							console.trace("Call stack:");
						};
					}

					// Add each search term
					llmSearchTerms.forEach((term, index) => {
						setTimeout(() => {
							console.log(
								`🔹 Adding search term ${index + 1}/${llmSearchTerms.length}:`,
								term.text,
							);

							if (typeof handlePackageAddSearchTerm === "function") {
								try {
									handlePackageAddSearchTerm(term.text, {
										originalScore:
											term.score ||
											Math.round(
												relevanceFromDistance(
													PACKAGE_INCLUDE_THRESHOLD,
													"PACKAGES",
												),
											),
										type: term.type || "include",
										threshold: term.threshold ?? PACKAGE_INCLUDE_THRESHOLD,
									});
									console.log(
										`✅ Successfully added search term: ${term.text}`,
									);
								} catch (err) {
									console.error(
										`❌ Failed to add search term: ${term.text}`,
										err,
									);
								}
							} else {
								console.error(
									"❌ handlePackageAddSearchTerm function not available",
								);
							}

							// Verify after adding
							setTimeout(() => {
								console.log(
									`🔍 Verification check after adding term ${index + 1}`,
								);
							}, 100);
						}, index * 100); // Increased delay between terms
					});

					// Final verification after all terms added
					setTimeout(
						() => {
							console.log(
								"🔍 FINAL VERIFICATION - All search terms should be added now",
							);
							console.log(
								"Expected terms:",
								llmSearchTerms.map((t) => t.text),
							);

							// ✅ Re-enable the clear function after search terms are added
							if (originalClearFunction) {
								window.handlePackageClearSearchTerms = originalClearFunction;
								console.log("🔓 Re-enabled handlePackageClearSearchTerms");
							}

							// ✅ Re-enable search trigger
							if (originalTriggerSearch) {
								window.packageTriggerSearch = originalTriggerSearch;
								console.log("🔓 Re-enabled packageTriggerSearch");
							}
						},
						llmSearchTerms.length * 100 + 300,
					);
				}, 800); // Increased initial delay

				// Clean up storage
				setTimeout(() => {
					if (storedPackagesResults) {
						sessionStorage.removeItem("llmPackagesSearchResults");
					}
					if (window.tempPackagesResults) {
						delete window.tempPackagesResults;
					}
					window.history.replaceState({}, document.title);
				}, 2000);

				console.log("✅ Package LLM results processing initiated");
			} catch (e) {
				console.error("❌ Package LLM processing failed:", e);
				setError("Failed to load package results: " + e.message);
			}
		}

		// DOMAIN LLM RESULTS PROCESSING (existing logic)
		else if (
			state?.fromLLMSearch &&
			state?.loadLLMResults &&
			state?.searchType === "domain" &&
			storedDomainResults &&
			!state?.switchToManual
		) {
			try {
				console.log("Loading Domain LLM Results...");
				const llmResults = JSON.parse(storedDomainResults);

				const llmSortBy =
					mapLLMSortFieldToDomainField(
						llmResults.originalData?.metadata?.originalLLMPayload?.sortBy,
					) || "viewability";
				console.log("LLM results", llmResults);
				console.log("llm sort by---", llmSortBy);
				const llmSortOrder = (
					llmResults.originalData?.metadata?.originalLLMPayload?.view || "DESC"
				).toLowerCase();
				console.log("llm sort order---", llmSortOrder);
				setDomainSortBy(llmSortBy);
				setDomainSortOrder(llmSortOrder);

				setLlmSearchData({
					type: "domain",
					searchTerms: [
						{
							text:
								llmResults.originalData?.metadata?.originalQuery ||
								"Domain LLM Search",
							type: "include",
							score: DEFAULT_QUERY_SCORE,
						},
					],
					filters: llmResults.appliedFilters,
					results: llmResults,
					searchOptions: {
						performanceMetric: "viewability",
						overrideResults: llmResults,
						sortBy: llmSortBy, // ✅ ADD THIS
						sortOrder: llmSortOrder,
					},
				});
				setIsLlmResultsActive(true);

				setActiveTab("domains");

				if (llmResults.appliedFilters) {
					console.log("Applying Domain filters:", llmResults.appliedFilters);
					// Merge LLM filters with current filters instead of replacing them
					setFilters((prev) => ({
						...prev,
						...llmResults.appliedFilters,
						// Preserve custom filters from current state if they exist
						customFilters:
							llmResults.appliedFilters.customFilters ||
							prev.customFilters ||
							[],
					}));
				}

				const mockSearchTerms = [
					{
						text:
							llmResults.originalData?.metadata?.originalQuery ||
							"Domain LLM Search",
						type: "include",
						score: 65,
					},
				];

				console.log("🔍 Triggering domain search with LLM results...");

				// Use domain search after a slight delay to ensure tab switch
				setTimeout(() => {
					onSearch(mockSearchTerms, {
						performanceMetric: "viewability",
						overrideResults: llmResults,
					});
				}, 100);

				sessionStorage.removeItem("llmSearchResults");
				window.history.replaceState({}, document.title);
			} catch (e) {
				console.error("Domain LLM processing failed:", e);
			}
		}

		// ✅ APP LLM RESULTS PROCESSING WITH PROPER PREBUILT SETS INTEGRATION
		else if (
			(state?.fromLLMSearch || state?.fromAppLLMSearch) &&
			state?.loadLLMResults &&
			state?.searchType === "app" &&
			storedAppResults &&
			!state?.switchToManual
		) {
			try {
				console.log(
					"📱 Loading App LLM Results with proper prebuilt sets integration...",
				);
				const appLlmResults = JSON.parse(storedAppResults);

				const extractSearchTerms = () => {
					// Priority 1: Check appliedFilters.searchTerms (where the actual data is)
					if (appLlmResults.appliedFilters?.searchTerms?.length > 0) {
						console.log(
							"✅ Using search terms from appliedFilters:",
							appLlmResults.appliedFilters.searchTerms,
						);
						return appLlmResults.appliedFilters.searchTerms;
					}

					// Priority 2: Check direct searchTerms (original logic)
					if (appLlmResults.searchTerms?.length > 0) {
						console.log(
							"✅ Using search terms from searchTerms:",
							appLlmResults.searchTerms,
						);
						return appLlmResults.searchTerms;
					}

					// Priority 3: Fallback to original query
					// const fallbackTerm = [{
					//     id: Date.now(),
					//     text: appLlmResults.originalData?.metadata?.originalQuery || 'App LLM Search',
					//     type: 'include',
					//     score: 75
					// }];
					// console.log('🔄 Using fallback search terms:', fallbackTerm);
					// return fallbackTerm;
				};

				const extractedSearchTerms = extractSearchTerms();

				// Use the extracted terms consistently
				setAppSearchTerms(extractedSearchTerms);

				const llmSortBy =
					mapLLMSortFieldToAppField(
						appLlmResults?.originalData?.metadata?.llmResponse?.sortBy,
					) || "relevance";
				const llmSortOrder = (
					appLlmResults?.originalData?.metadata?.llmResponse?.view || "DESC"
				).toLowerCase();

				console.log("APP LLM results---", appLlmResults);
				console.log("sort--", llmSortBy);
				console.log("view--", llmSortOrder);

				setAppSortBy(llmSortBy);
				setAppSortOrder(llmSortOrder);

				// ✅ STORE LLM SEARCH DATA FOR PERSISTENCE
				setLlmSearchData({
					type: "app",
					searchTerms: [
						{
							id: Date.now(),
							text: extractedSearchTerms,
							type: "include",
							score: DEFAULT_QUERY_SCORE,
						},
					],
					filters: appLlmResults.appliedFilters,
					results: appLlmResults,
					searchOptions: {
						performanceMetric: "viewability",
						overrideResults: appLlmResults,
						searchType: "app",
						sortBy: llmSortBy, // ✅ ADD THIS
						sortOrder: llmSortOrder,
					},
				});
				setIsLlmResultsActive(true);

				console.log("📱 Switching to app tab...");
				setActiveTab("app");

				// ✅ CRITICAL FIX: Set app results FIRST to enable prebuilt sets rendering
				if (appLlmResults.apps && Array.isArray(appLlmResults.apps)) {
					console.log(
						"📊 Setting app results FIRST for prebuilt sets component:",
						appLlmResults.apps.length,
						"app",
					);

					setAppData(appLlmResults.apps);
					setAppResults(appLlmResults.apps);
					setAppTotalItems(
						appLlmResults.totalRecords || appLlmResults.apps.length,
					);
					setAppCurrentPage(1);
				}

				// ✅ ENHANCED: Process and set prebuilt sets with proper timing
				const processPrebuiltSets = () => {
					console.log("🔧 Processing prebuilt sets from LLM results...");

					// Extract prebuilt sets from multiple possible locations
					let prebuiltSetsData = null;

					if (
						appLlmResults.prebuiltSets &&
						Object.keys(appLlmResults.prebuiltSets).length > 0
					) {
						prebuiltSetsData = appLlmResults.prebuiltSets;
						console.log(
							"✅ Found prebuilt sets in appLlmResults.prebuiltSets:",
							Object.keys(prebuiltSetsData),
						);
					} else if (
						appLlmResults.prebuiltAppSets &&
						Object.keys(appLlmResults.prebuiltAppSets).length > 0
					) {
						prebuiltSetsData = appLlmResults.prebuiltAppSets;
						console.log(
							"✅ Found prebuilt sets in appLlmResults.prebuiltAppSets:",
							Object.keys(prebuiltSetsData),
						);
					} else if (
						appLlmResults.appResponse?.prebuiltAppSets &&
						Object.keys(appLlmResults.appResponse.prebuiltAppSets).length > 0
					) {
						prebuiltSetsData = appLlmResults.appResponse.prebuiltAppSets;
						console.log(
							"✅ Found prebuilt sets in appResponse.prebuiltAppSets:",
							Object.keys(prebuiltSetsData),
						);
					}

					if (prebuiltSetsData) {
						console.log(
							"📊 CRITICAL FIX: Setting prebuilt sets in state to trigger component rendering",
						);

						// ✅ CRITICAL: Store in persistedPrebuiltAppSets to match manual search behavior
						setPersistedPrebuiltAppSets(prebuiltSetsData);

						// ✅ ALSO: Store in hook state if available
						if (appSearchHook?.storeLLMResults) {
							console.log("📊 Storing LLM results with prebuilt sets via hook");
							appSearchHook.storeLLMResults(appLlmResults, prebuiltSetsData);
						}

						// ✅ FORCE: Update global storage exactly like manual search does
						if (typeof window !== "undefined") {
							window.currentAppPrebuiltSets = prebuiltSetsData;
							window.lastAppLLMPrebuiltSets = prebuiltSetsData;

							window.globalAppLLMResults = {
								apps: appLlmResults.apps || [],
								prebuiltSets: prebuiltSetsData,
								prebuiltAppSets: prebuiltSetsData,
								timestamp: Date.now(),
								source: "frontend_cumulative", // Same source as manual search
							};
						}

						console.log(
							"✅ PREBUILT SETS STATE SYNCHRONIZED - Component should render now",
						);
						return prebuiltSetsData;
					} else {
						console.warn("⚠️ No prebuilt sets found in LLM results");
						return null;
					}
				};

				// ✅ TIMING FIX: Process prebuilt sets after a short delay to ensure app results are set
				setTimeout(() => {
					const processedSets = processPrebuiltSets();

					if (processedSets) {
						console.log("🎯 Prebuilt sets processed and state updated");

						// ✅ FORCE COMPONENT RE-RENDER: Update timestamp to trigger shouldShowPrebuiltAppSets
						setPrebuiltSetsTimestamp(Date.now());

						// ✅ DEBUGGING: Log the render condition state
						console.log("🔍 RENDER CONDITION CHECK:", {
							activeTab: "app", // We just set this
							persistedPrebuiltAppSetsKeys: Object.keys(processedSets),
							hookPrebuiltAppSetsKeys: Object.keys(
								appSearchHook?.prebuiltAppSets || {},
							),
							appResultsLength: appLlmResults.apps?.length || 0,
							shouldRender: true,
						});
					}
				}, 100);

				// ✅ APPLY FILTERS (existing logic remains the same)
				if (appLlmResults.appliedFilters) {
					console.log("🔧 Processing App LLM filters...");

					const mapLLMFiltersToAppFilters = (llmFilters) => {
						const fieldMapping = {
							appStores: "appStore",
							appCategories: "categories",
							deviceType: "deviceType",
							geography: "geography",
							searchTerms: "appNames",
							// ... rest of mapping
						};

						const mappedFilters = {};

						Object.entries(llmFilters).forEach(([llmField, value]) => {
							const appFilterField = fieldMapping[llmField] || llmField;

							if (Array.isArray(value) && value.length === 0) return;
							if (value === null || value === undefined) return;

							if (llmField === "searchTerms" && Array.isArray(value)) {
								const searchTermStrings = value
									.map((term) => (typeof term === "object" ? term.text : term))
									.filter(Boolean);

								if (searchTermStrings.length > 0) {
									mappedFilters["appNames"] = searchTermStrings;
								}
								return;
							}

							if (llmField === "customFilters" && Array.isArray(value)) {
								// Get current custom filters from appFilters
								const currentCustomFilters = appFilters?.customFilters || [];
								// Merge: use LLM filters if provided, otherwise keep current
								mappedFilters[appFilterField] =
									value.length > 0 ? value : currentCustomFilters;
								return;
							}

							mappedFilters[appFilterField] = value;
						});

						return mappedFilters;
					};

					const applyFiltersInSequence = (mappedFilters) => {
						Object.entries(mappedFilters).forEach(([key, value], index) => {
							setTimeout(() => {
								try {
									if (typeof handleAppUpdateFilter === "function") {
										handleAppUpdateFilter(key, value, { silent: true });
									}
								} catch (filterError) {
									console.warn("Failed to apply app filter:", key, filterError);
								}
							}, index * 15);
						});
					};

					setTimeout(() => {
						const filters = appLlmResults.appliedFilters;

						if (Array.isArray(filters)) {
							const filterObject = {};
							filters.forEach((filter) => {
								if (filter.key && filter.value !== undefined) {
									filterObject[filter.key] = filter.value;
								}
							});

							const mappedFilters = mapLLMFiltersToAppFilters(filterObject);
							applyFiltersInSequence(mappedFilters);
						} else if (typeof filters === "object") {
							const mappedFilters = mapLLMFiltersToAppFilters(filters);
							applyFiltersInSequence(mappedFilters);
						}

						console.log("✅ All app LLM filters queued for application");
					}, 200);
				}

				console.log(
					"✅ App LLM results processing completed WITH proper prebuilt sets state synchronization",
				);

				sessionStorage.removeItem("llmAppSearchResults");
				window.history.replaceState({}, document.title);
			} catch (e) {
				console.error("❌ App LLM processing failed:", e);
			}
		}

		// HANDLE MANUAL SWITCH - CLEANUP
		if (
			(state?.fromLLMSearch ||
				state?.fromAppLLMSearch ||
				state?.fromAudienceLLMSearch) &&
			state?.switchToManual
		) {
			console.log(
				"Switching to manual search - cleaning up all LLM results...",
			);

			setLlmSearchData(null);
			setIsLlmResultsActive(false);

			sessionStorage.removeItem("llmSearchResults");
			sessionStorage.removeItem("llmAppSearchResults");
			sessionStorage.removeItem("llmAudienceSearchResults"); // FIXED: Clean up audience results too
			sessionStorage.removeItem("llmSegmentsSearchResults");
			sessionStorage.removeItem("llmPackagesSearchResults");
			if (window.tempSegmentsResults) {
				delete window.tempSegmentsResults;
			}
			// Set the correct tab for manual search
			if (state.activeTab) {
				setActiveTab(state.activeTab);
			}

			window.history.replaceState({}, document.title);
		}
	}, [
		location.state,
		onSearch,
		setFilters,
		handleAppUpdateFilter,
		setActiveTab,
		setAppResults,
		setAppTotalItems,
		setAppCurrentPage,
		audienceTriggerSearchWithOverride,
		audienceInjectSearchResults,
		audienceAddSearchTerm,
		audienceTriggerSearch,
	]);

	// ✅ ADD THIS CRITICAL MISSING PIECE - THE SMART SEARCH HANDLER
	const handleSearchWithLlmPersistence = useCallback(
		(searchData, searchOptions) => {
			console.log("🔍 Search triggered with LLM persistence check:", {
				activeTab,
				isLlmResultsActive,
				llmSearchDataType: llmSearchData?.type,
				searchDataLength: searchData?.length,
				hasSearchTerms: searchData && searchData.length > 0,
			});

			// PACKAGE-SPECIFIC: Clear cached data for new searches
			if (activeTab === "packages" && searchData && searchData.length > 0) {
				console.log("🧹 NEW PACKAGE SEARCH - Clearing cached data completely");
				setPackageResults([]);
				setPackageTotalItems(0);
				setPackageSummary(null);
			}

			// APP-SPECIFIC: Clear prebuilt sets for new searches
			if (activeTab === "app" && searchData && searchData.length > 0) {
				console.log(
					"🧹 NEW APP SEARCH - Clearing cached prebuilt sets completely",
				);
				setPersistedPrebuiltAppSets(null);

				if (appSearchHook?.forceResetAppSearch) {
					appSearchHook.forceResetAppSearch(false);
				}
			}

			// CHECK: If LLM results are active and this looks like a "repeat search"
			if (isLlmResultsActive && llmSearchData) {
				const isRepeatSearch =
					!searchData ||
					searchData.length === 0 ||
					(searchData.length === 1 &&
						searchData[0]?.text === llmSearchData.searchTerms[0]?.text);

				if (isRepeatSearch) {
					console.log(
						"Repeating LLM search with stored data for type:",
						llmSearchData.type,
					);

					if (llmSearchData.type === "packages") {
						// PACKAGE: Preserve existing package LLM results
						console.log("Preserving existing package LLM results");
						if (llmSearchData.results.packages) {
							setPackageResults(llmSearchData.results.packages);
							setPackageTotalItems(
								llmSearchData.results.totalRecords ||
									llmSearchData.results.packages.length,
							);
							setPackageCurrentPage(1);
						}
						return;
					} else if (llmSearchData.type === "domain") {
						onSearch(llmSearchData.searchTerms, llmSearchData.searchOptions);
					} else if (llmSearchData.type === "app") {
						console.log("Preserving existing app LLM results");
						if (llmSearchData.results.apps) {
							setAppResults(llmSearchData.results.apps);
							setAppTotalItems(
								llmSearchData.results.totalRecords ||
									llmSearchData.results.apps.length,
							);
							setAppCurrentPage(1);
						}
						return;
					}
					return;
				}
			}

			// CHECK: If this is a genuinely new search, clear LLM state
			if (searchData && searchData.length > 0) {
				const isNewSearch =
					!llmSearchData ||
					searchData.length !== llmSearchData.searchTerms.length ||
					searchData[0]?.text !== llmSearchData.searchTerms[0]?.text;

				if (isNewSearch) {
					console.log("New search detected, clearing LLM state");
					setLlmSearchData(null);
					setIsLlmResultsActive(false);
				}
			}

			// PROCEED WITH NORMAL SEARCH LOGIC
			if (activeTab === "packages") {
				// PACKAGE: Use package search hook
				if (packageSearchHook?.triggerSearch) {
					console.log("Using package search hook for search");
					packageSearchHook.triggerSearch(searchOptions);
				}
			} else if (activeTab === "app") {
				if (appSearchHook?.onSearch) {
					const finalFilters = { ...appApiFilters, ...searchOptions?.filters };
					appSearchHook.onSearch(searchData, {
						...searchOptions,
						filters: finalFilters,
					});
				}
			} else {
				const baseFilters = { ...filters, uploadedDomains: uploadedDomains };
				const enhancedFilters = searchOptions?.filters || {};
				const finalFilters = {
					...baseFilters,
					...enhancedFilters,
					sortBy: enhancedFilters.sortBy || baseFilters.sortBy,
					sortOrder: enhancedFilters.sortOrder || baseFilters.sortOrder,
					view:
						enhancedFilters.view ||
						enhancedFilters.sortOrder ||
						baseFilters.view,
				};
				onSearch(searchData, { ...searchOptions, filters: finalFilters });
			}
		},
		[
			activeTab,
			isLlmResultsActive,
			llmSearchData,
			onSearch,
			appSearchHook,
			appApiFilters,
			filters,
			uploadedDomains,
			packageSearchHook,
			setPackageResults,
			setPackageTotalItems,
			setPackageCurrentPage,
			setPackageSummary,
			setAppResults,
			setAppTotalItems,
			setAppCurrentPage,
			setPersistedPrebuiltAppSets,
		],
	);

	// ✅ ENHANCED: Handle tab-specific result loading including segments
	useEffect(() => {
		console.log("🔄 Active tab changed to:", activeTab);

		// If we just switched to app tab and have pending app results, ensure they're loaded
		if (activeTab === "app") {
			const storedAppResults = sessionStorage.getItem("llmAppSearchResults");
			if (storedAppResults && (!appResults || appResults.length === 0)) {
				try {
					console.log("📱 Loading pending app results after tab switch...");
					const appLlmResults = JSON.parse(storedAppResults);

					if (appLlmResults.apps && Array.isArray(appLlmResults.apps)) {
						setAppData(appLlmResults.apps);
						setAppResults(appLlmResults.apps);
						setAppTotalItems(
							appLlmResults.totalRecords || appLlmResults.apps.length,
						);
						setAppCurrentPage(1);
						console.log("✅ App results loaded after tab switch");

						// Clean up after successful load
						sessionStorage.removeItem("llmAppSearchResults");
					}
				} catch (e) {
					console.error("❌ Failed to load pending app results:", e);
				}
			}
		}

		// ✅ NEW: If we just switched to segments tab and have pending segment results, ensure they're loaded
		else if (activeTab === "segments") {
			const storedSegmentResults = sessionStorage.getItem(
				"llmSegmentSearchResults",
			);
			// Check for segment results conditionally
			const hasSegmentResults =
				typeof window.segmentResults !== "undefined"
					? !window.segmentResults || window.segmentResults.length === 0
					: true;

			if (storedSegmentResults && hasSegmentResults) {
				try {
					console.log("🎯 Loading pending segment results after tab switch...");
					const segmentLlmResults = JSON.parse(storedSegmentResults);

					if (
						segmentLlmResults.segments &&
						Array.isArray(segmentLlmResults.segments)
					) {
						// Use conditional checks for segment state setters
						if (typeof window.setSegmentData === "function") {
							window.setSegmentData(segmentLlmResults.segments);
						}
						if (typeof window.setSegmentResults === "function") {
							window.setSegmentResults(segmentLlmResults.segments);
						}
						if (typeof window.setSegmentTotalItems === "function") {
							window.setSegmentTotalItems(
								segmentLlmResults.totalRecords ||
									segmentLlmResults.segments.length,
							);
						}
						if (typeof window.setSegmentCurrentPage === "function") {
							window.setSegmentCurrentPage(1);
						}

						// Store globally for segment component to pick up
						window.segmentLLMResultsLoaded = true;
						window.segmentLLMResultsData = segmentLlmResults.segments;
						window.segmentLLMTotalItems =
							segmentLlmResults.totalRecords ||
							segmentLlmResults.segments.length;

						console.log("✅ Segment results loaded after tab switch");

						// Clean up after successful load
						sessionStorage.removeItem("llmSegmentSearchResults");
					}
				} catch (e) {
					console.error("❌ Failed to load pending segment results:", e);
				}
			}
		}

		// Handle other tabs similarly...
	}, [
		activeTab,
		appResults,
		setAppResults,
		setAppTotalItems,
		setAppCurrentPage,
	]);

	// =============================================
	// ERROR BOUNDARY
	// =============================================

	if (appError) {
		return (
			<div className="app-error">
				<h1>Something went wrong</h1>
				<p>
					Please refresh the page or contact support if the problem persists.
				</p>
				<button
					onClick={() => {
						setAppError(false);
						window.location.reload();
					}}
				>
					Refresh Page
				</button>
			</div>
		);
	}
	// =============================================
	// MAIN JSX RENDER
	// =============================================

	return (
		<div className="app-container">
			<div className="main-layout">
				{/* Filters Sidebar - Conditional rendering based on active tab */}
				{activeTab === "domains" ? (
					<FiltersSidebar
						showFilters={showFilters}
						filters={filters}
						expandedSections={expandedSections}
						onToggleSection={toggleSection}
						onUpdateFilter={updateFilter}
						onToggleArrayFilter={toggleArrayFilter}
						onClearAllFilters={clearAllFilters}
						onClose={() => setShowFilters(false)}
						onUploadIncludeList={(domains) => {
							setUploadedDomains((prev) => ({
								...prev,
								includeList: domains,
							}));
						}}
						onUploadBlockedList={(domains) => {
							setUploadedDomains((prev) => ({
								...prev,
								excludeList: domains,
							}));
						}}
					/>
				) : activeTab === "app" ? (
					<AppFiltersSidebar
						showFilters={showFilters}
						filters={appFilters}
						expandedSections={appExpandedSections}
						onToggleSection={handleAppToggleSection}
						onUpdateFilter={handleAppUpdateFilter}
						onToggleArrayFilter={handleAppToggleArrayFilter}
						onClearAllFilters={handleAppClearAllFilters}
						onClose={handleAppFiltersClose}
						filterOptions={appFilterOptions}
						isLoadingFilterOptions={appFiltersLoading}
						hasError={appFiltersHasError}
						error={appFiltersError}
						onRetryFetchFilterOptions={handleAppRetryFilters}
					/>
				) : activeTab === "segments" ? (
					<SegmentsFiltersSidebar
						showFilters={showFilters}
						filters={segmentsFilters}
						expandedSections={segmentsExpandedSections}
						onToggleSection={handleSegmentsToggleSection}
						onUpdateFilter={handleSegmentsUpdateFilter}
						onToggleArrayFilter={handleSegmentsToggleArrayFilter}
						onClearAllFilters={handleSegmentsClearAllFilters}
						onClose={() => setShowFilters(false)}
						// ✅ UPDATED: Filter options props from new API integration
						filterOptions={segmentsFilterOptions}
						isLoadingFilterOptions={segmentsFiltersLoading}
						hasFilterError={segmentsFiltersHasError} // Updated prop name
						filterError={segmentsFiltersError} // Updated prop name
						retryFetchFilterOptions={handleSegmentsRetryFilters}
						fetchFilterOptions={fetchFilterOptions} // ✅ NEW: Add manual refresh function
						// Additional props
						activeSubTab={segmentsActiveSubTab}
						onSubTabChange={setSegmentsActiveSubTab}
						// ✅ NEW: Add validation and active filter props
						validation={segmentsFilterValidation}
						hasActiveFilters={segmentsHasActiveFilters}
						activeFilterCount={segmentsActiveFilterCount}
						filterSummary={segmentsFilterSummary}
					/>
				) : activeTab === "audience" ? (
					// ✅ FIXED: Simplified AudienceFiltersSidebar integration
					<AudienceFiltersSidebar
						showFilters={showFilters}
						onClose={() => setShowFilters(false)}
						// ✅ FIXED: All props from combined hook
						filters={audienceFilters}
						expandedSections={audienceExpandedCategories}
						expandedCategories={audienceExpandedCategories}
						availableOptions={audienceFilterOptions}
						filterValues={audienceFilterOptions}
						filterValuesLoading={audienceFilterValuesLoading}
						filterValuesError={audienceFilterValuesError}
						uploadStates={audienceUploadStates}
						showUploadTooltip={audienceShowUploadTooltip}
						hasActiveFilters={audienceHasActiveFilters}
						totalActiveFilters={audienceActiveFilterCount}
						filtersContentRef={audienceFiltersContentRef}
						// ✅ FIXED: All handlers from combined hook
						updateFilter={audienceUpdateFilter}
						toggleArrayFilter={audienceToggleArrayFilter}
						clearAllFilters={audienceClearAllFilters}
						toggleSection={audienceToggleCategory}
						toggleCategory={audienceToggleCategory}
						loadFilterValues={audienceLoadFilterValues}
						setShowUploadTooltip={audienceSetShowUploadTooltip}
						handleFileUpload={audienceHandleFileUpload}
						clearUploadedList={audienceClearUploadedList}
						handleSearchInputChange={audienceHandleSearchInputChange}
						handleSearchInputEnter={audienceHandleSearchInputEnter}
						handleRemoveSearchTag={audienceHandleRemoveSearchTag}
					/>
				) : activeTab === "packages" ? (
					<PackageFiltersSidebar
						showFilters={showFilters}
						filters={packageFilters}
						expandedSections={packageExpandedSections}
						filterOptions={packageFilterOptions}
						isLoadingFilters={packageIsLoadingFilters}
						filterError={packageFilterError}
						onToggleSection={handlePackageToggleSection}
						onUpdateFilter={handlePackageUpdateFilter}
						onToggleArrayFilter={handlePackageToggleArrayFilter}
						onClearAllFilters={handlePackageClearAllFilters}
						onClose={() => setShowFilters(false)}
						loadFilterOptions={handlePackageLoadFilterOptions}
					/>
				) : activeTab === "category" ? (
					<FiltersSidebar
						showFilters={showFilters}
						filters={categorySearch.filters}
						expandedSections={categorySearch.expandedSections}
						onToggleSection={categorySearch.toggleSection}
						onUpdateFilter={categorySearch.updateFilter}
						onToggleArrayFilter={categorySearch.toggleArrayFilter}
						onClearAllFilters={categorySearch.clearAllFilters}
						onClose={() => setShowFilters(false)}
						onUploadIncludeList={(domains) =>
							categorySearch.updateFilter("uploadedDomains", {
								...(categorySearch.filters.uploadedDomains || {}),
								includeList: domains,
							})
						}
						onUploadBlockedList={(domains) =>
							categorySearch.updateFilter("uploadedDomains", {
								...(categorySearch.filters.uploadedDomains || {}),
								excludeList: domains,
							})
						}
					/>
				) : null}

				{/* Main Content Area */}
				<div
					className={`main-content ${!shouldShowFilters() ? "full-width" : ""}`}
				>
					{/* Search Interfaces - Conditional rendering based on active tab */}
					{activeTab === "segments" && (
						<SegmentsSearchInterface
							// Props from unified hook
							searchQuery={segmentsSearchQuery}
							onSearchInputChange={handleSegmentsSearchInputChange}
							searchTerms={segmentsSearchTerms}
							addSearchTerm={handleSegmentsAddSearchTerm}
							removeSearchTerm={handleSegmentsRemoveSearchTerm}
							logicChain={segmentsLogicChain}
							onLogicChainChange={handleSegmentsLogicChainChange}
							onSearch={handleSegmentsSearch} // Main unified search function
							isSearching={segmentsIsSearching}
							searchResults={segmentsSearchResults}
							error={segmentsSearchError}
							filters={segmentsFilters}
							updateFilter={handleSegmentsUpdateFilter}
							// Additional props
							onExport={() => setShowUploadModal(true)}
							onRefresh={handleSegmentsResetSearch}
							resultsCount={segmentsResultsCount}
							sortBy={segmentsSortBy}
							setSortBy={setSegmentsSortBy}
							viewMode={segmentsViewMode}
							setViewMode={setSegmentsViewMode}
							isFilterSidebarOpen={showFilters}
							onToggleFilterSidebar={() => setShowFilters(!showFilters)}
							activeSubTab={segmentsActiveSubTab}
							onSubTabChange={setSegmentsActiveSubTab}
						/>
					)}
					{activeTab === "app" && (
						<AppSearchInterface
							searchQuery={searchQuery}
							refinedQuery={refinedQuery}
							isSearching={isSearching}
							performanceMetric={performanceMetric}
							minReachThreshold={minReachThreshold}
							onSearchInputChange={onSearchInputChange}
							onRefinedQueryChange={onRefinedQueryChange}
							onSearch={(searchData, searchOptions) => {
								// CRITICAL FIX: Clear prebuilt sets before new search
								console.log(
									"AppSearchInterface onSearch triggered - clearing prebuilt sets",
								);
								setPersistedPrebuiltAppSets(null);

								const finalFilters = {
									...appApiFilters,
									...searchOptions?.filters,
								};
								onSearch(searchData, {
									...searchOptions,
									filters: finalFilters,
								});
							}}
							initialSortBy={appSortBy}
							initialSortOrder={appSortOrder}
							onSortChange={(newSortBy, newSortOrder) => {
								console.log("📊 Sort changed in AppSearchInterface:", {
									newSortBy,
									newSortOrder,
								});
								setAppSortBy(newSortBy);
								setAppSortOrder(newSortOrder);
							}}
							onClearRefinement={onClearRefinement}
							onPerformanceMetricChange={onPerformanceMetricChange}
							onMinReachChange={onMinReachChange}
							filters={appApiFilters}
							className="main-search-interface"
							searchTerms={appSearchTerms}
							onSearchTermsChange={setAppSearchTerms}
						/>
					)}
					{activeTab === "domains" && (
						<SearchInterface
							searchQuery={searchQuery}
							refinedQuery={refinedQuery}
							isSearching={isSearching}
							showSuggestions={showSuggestions}
							searchSuggestions={searchSuggestions}
							performanceMetric={performanceMetric}
							minReachThreshold={minReachThreshold}
							onSearchInputChange={onSearchInputChange}
							onRefinedQueryChange={onRefinedQueryChange}
							onSearch={(searchData, searchOptions) => {
								const baseFilters = {
									...filters,
									uploadedDomains: uploadedDomains,
								};
								const enhancedFilters = searchOptions?.filters || {};

								const finalFilters = {
									...baseFilters,
									...enhancedFilters,
									sortBy: enhancedFilters.sortBy || baseFilters.sortBy,
									sortOrder: enhancedFilters.sortOrder || baseFilters.sortOrder,
									view:
										enhancedFilters.view ||
										enhancedFilters.sortOrder ||
										baseFilters.view,
								};
								onSearch(searchData, {
									...searchOptions,
									filters: finalFilters,
								});
							}}
							onSuggestionSelect={onSuggestionSelect}
							onClearRefinement={onClearRefinement}
							onHideSuggestions={onHideSuggestions}
							onPerformanceMetricChange={onPerformanceMetricChange}
							onMinReachChange={onMinReachChange}
							activeTab={activeTab}
							filters={filters}
							className="main-search-interface"
							searchTerms={domainSearchTerms}
							onSearchTermsChange={setDomainSearchTerms}
							initialSortBy={domainSortBy}
							initialSortOrder={domainSortOrder}
							onSortChange={(newSortBy, newSortOrder) => {
								console.log("📊 Sort changed in SearchInterface:", {
									newSortBy,
									newSortOrder,
								});
								setDomainSortBy(newSortBy);
								setDomainSortOrder(newSortOrder);
							}}
						/>
					)}
					{activeTab === "audience" && (
						// ✅ FIXED: Simplified AudienceSearchInterface integration
						<AudienceSearchInterface
							// ✅ FIXED: All props from combined hook
							searchQuery={audienceSearchQuery}
							searchTerms={audienceSearchTerms}
							excludeTerms={audienceExcludeTerms}
							isSearching={audienceIsSearching}
							error={audienceSearchError}
							filters={audienceFilters}
							// ✅ FIXED: All handlers from combined hook
							onSearchInputChange={audienceOnSearchInputChange}
							addSearchTerm={audienceAddSearchTerm}
							addExcludeTerm={audienceAddExcludeTerm}
							removeSearchTerm={audienceRemoveSearchTerm}
							clearSearchTerms={audienceClearSearchTerms}
							initialSortBy={audienceSortBy}
							initialSortOrder={audienceSortOrder}
							onSortChange={(newSortBy, newSortOrder) => {
								console.log("📊 Sort changed in AudienceSearchInterface:", {
									newSortBy,
									newSortOrder,
								});
								setAudienceSortBy(newSortBy);
								setAudienceSortOrder(newSortOrder);
							}}
							// ✅ CRITICAL: Single search trigger
							triggerSearch={audienceTriggerSearch}
							className="main-search-interface"
						/>
					)}
					{activeTab === "packages" && (
						<PackageSearchInterface
							// Search state props
							searchQuery={packageSearchQuery}
							searchTerms={packageSearchTerms}
							excludeTerms={packageExcludeTerms}
							isSearching={packageIsSearching}
							error={packageSearchError}
							filters={packageFilters}
							// Search handlers
							onSearchInputChange={handlePackageSearchInputChange}
							addSearchTerm={handlePackageAddSearchTerm}
							removeSearchTerm={handlePackageRemoveSearchTerm}
							clearSearchTerms={handlePackageClearSearchTerms}
							initialSortBy={packageSortColumn || "compositeScore"}
							initialView={packageSortDirection?.toUpperCase() || "DESC"}
							onSortChange={(newSortBy, newView) => {
								console.log("📊 Sort changed in PackageSearchInterface:", {
									newSortBy,
									newView,
								});
								setPackageSortColumn(newSortBy);
								setPackageSortDirection(newView.toLowerCase());
							}}
							// ✅ FIXED: Clear all LLM data on every search
							triggerSearch={(searchOptions) => {
								console.log(
									"📦 NEW PACKAGE SEARCH - Clearing ALL previous data",
								);

								// Clear all package state
								setPackageResults([]);
								setPackageTotalItems(0);
								setPackageSummary(null);
								setPackageLlmResults(null);

								// Clear LLM state
								setLlmSearchData(null);
								setIsLlmResultsActive(false);

								// Trigger the actual search
								handlePackageTriggerSearch(searchOptions);
							}}
							className="main-search-interface"
						/>
					)}
					{activeTab === "category" && (
						<CategorySearchInterface
							subTab={categorySearch.subTab}
							onSubTabChange={categorySearch.onSubTabChange}
							searchInput={categorySearch.searchInput}
							setSearchInput={categorySearch.setSearchInput}
							currentThreshold={categorySearch.currentThreshold}
							setCurrentThreshold={categorySearch.setCurrentThreshold}
							searchTerms={categorySearch.searchTerms}
							addSearchTerm={categorySearch.addSearchTerm}
							removeSearchTerm={categorySearch.removeSearchTerm}
							clearSearchTerms={categorySearch.clearSearchTerms}
							isSearching={categorySearch.isSearching}
							onSearch={categorySearch.search}
							onClear={categorySearch.clearAll}
							sortBy={categorySearch.sortBy}
							setSortBy={categorySearch.setSortBy}
							sortDirection={categorySearch.sortDirection}
							setSortDirection={categorySearch.setSortDirection}
							totalRecords={categorySearch.totalRecords}
							hasSearched={categorySearch.hasSearched}
						/>
					)}

					{/* Results Tabs */}
					{ResultsTabs && (
						<ResultsTabs
							activeTab={activeTab}
							onTabChange={handleTabChange}
							domainCount={
								searchResults?.domains?.length || domainResults?.length || 0
							}
							keywordCount={
								searchResults?.keywords?.length || keywordResults?.length || 0
							}
						/>
					)}

					{shouldShowPrebuiltDomainSets() && (
						<PrebuiltSets
							prebuiltDomainSets={
								persistedPrebuiltDomainSets || hookPrebuiltDomainSets
							}
							selectedDomainSet={selectedDomainSet}
							onSelectDomainSet={setSelectedDomainSet}
							onClearSelection={() => setSelectedDomainSet(null)}
						/>
					)}

					{shouldShowPrebuiltAppSets() && (
						<PrebuiltAppSets
							prebuiltAppSets={
								// FIXED: Always prioritize hook sets (latest from search) over persisted sets
								hookPrebuiltAppSets &&
								Object.keys(hookPrebuiltAppSets).length > 0
									? hookPrebuiltAppSets // Fresh sets from current search
									: persistedPrebuiltAppSets // Fallback to persisted sets
							}
							selectedAppSet={selectedAppSet}
							onSelectAppSet={setSelectedAppSet}
							performanceMetric={performanceMetric}
							onClearSelection={() => setSelectedAppSet(null)}
						/>
					)}

					{/* Summary Components - Conditional rendering based on active tab */}
					{activeTab === "domains" && DomainSummary && (
						<DomainSummary
							domainData={
								selectedDomainSet && prebuiltDomainSets[selectedDomainSet]
									? prebuiltDomainSets[selectedDomainSet].domains
									: domainResults
							}
							activeTab={activeTab}
							performanceMetric={performanceMetric}
							isHighPerformanceSet={selectedDomainSet === "high-performance"}
							excludedItems={excludedItems}
						/>
					)}

					{activeTab === "app" && (
						<AppSummary
							data={getCompleteAppData()}
							selectedResults={selectedResults}
							performanceMetric={performanceMetric}
							totalItems={getAppFilteredDataCount()}
						/>
					)}

					{/* View Controls - Conditional rendering for domains and apps */}
					{(activeTab === "domains" || activeTab === "app") && ViewControls && (
						<ViewControls
							activeTab={activeTab}
							activeView={activeView}
							metricsDisplayMode={metricsDisplayMode}
							sortBy={sortBy}
							sortOrder={sortOrder}
							onViewChange={setActiveView}
							onMetricsDisplayChange={setMetricsDisplayMode}
							onSortByChange={setSortBy}
							onSortOrderChange={setSortOrder}
						/>
					)}

					{/* Results Lists - Conditional rendering based on active tab */}
					{activeTab === "domains" && ResultsList && (
						<ResultsList
							activeTab={activeTab}
							data={getCurrentPageData()}
							fullData={domainResults}
							selectedResults={selectedResults}
							onResultSelect={handleResultSelect}
							currentPage={currentPage}
							totalPages={getTotalPages()}
							totalItems={getFilteredDataCount()}
							itemsPerPage={pageSize}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
							onSearchChange={handleDomainSearchChange}
							onShowUploadModal={() => setShowUploadModal(true)}
							searchTerm={
								activeTab === "domains" ? domainSearchTerm : keywordSearchTerm
							}
							onSelectAll={handleSelectAll}
							isAllSelected={isAllSelected}
							isExporting={isExporting}
							onExport={exportToCSV}
							isPaginatedData={true}
							onExcludeSelected={handleExcludeSelected}
							excludedItems={excludedItems}
							keywords={keywordResults} // Pass the keywords from your transformed results
							searchQuery={searchQuery || domainSearchTerm} // Pass the search query for context
							onAddKeywordsToSearch={(selectedKeywords) => {
								// Handle when keywords are added to search
								console.log("Adding keywords to search:", selectedKeywords);
								// You can add them to search terms or trigger a new search
								const keywordTexts = selectedKeywords.map((k) => k.keyword);
								// Add your logic here to add these keywords to the search interface
							}}
						/>
					)}

					{activeTab === "app" && (
						<AppResultsList
							data={getAppCurrentPageData()}
							selectedResults={appSelectedResults}
							onResultSelect={handleAppResultSelect}
							currentPage={appCurrentPage}
							totalPages={getAppTotalPages()}
							totalItems={getAppFilteredDataCount()}
							itemsPerPage={pageSize}
							onPageChange={handleAppPageChange}
							onSelectAll={handleAppSelectAll}
							isAllSelected={
								appSelectedResults.length === getAppCurrentPageData().length &&
								getAppCurrentPageData().length > 0
							}
							isExporting={isExporting}
							onExport={exportAppToCSV}
							searchTerm={appSearchTerm}
							onSearchChange={handleAppSearchChange}
							sortBy={sortBy}
							sortOrder={sortOrder}
							onSortByChange={setSortBy}
							onSortOrderChange={setSortOrder}
							// ADD: Full dataset for proper sorting
							fullDataset={appResults}
						/>
					)}

					{activeTab === "segments" && (
						<SegmentsResultsList
							segmentsData={filteredSegmentsDataForUI}
							segments={segmentsSearchResults?.segments || []}
							loading={segmentsIsSearching}
							error={segmentsSearchError}
							onSegmentSelect={handleSegmentsResultSelect}
							onSegmentView={(segment) => {
								console.log("View segment details:", segment);
							}}
							onSegmentAdd={(segment) => {
								console.log("Add segment to campaign:", segment);
							}}
							onSetSelect={handleSetSelect}
							onSetAdd={handleSetAdd}
							selectedSets={selectedSets}
							selectedSegments={segmentsSelectedResults}
							onSelectAll={handleSegmentsSelectAll}
							isAllSelected={
								segmentsSelectedResults.length ===
									(segmentsSearchResults?.segments?.length || 0) &&
								(segmentsSearchResults?.segments?.length || 0) > 0
							}
							isExporting={isExporting}
							onExport={exportSegmentsToCSV}
							activeTab={activeTab}
							// Unified hook integration
							currentFilters={segmentsFilters}
							hasActiveFilters={segmentsHasActiveFilters}
							activeFilterCount={segmentsActiveFilterCount}
							onSearch={handleSegmentsSearch}
							// Search integration - ADD THESE LINES
							searchTerms={segmentsSearchTerms}
							logicChain={segmentsLogicChain}
							// Pagination
							currentPage={segmentsCurrentPage}
							totalPages={getSegmentsTotalPages()}
							totalItems={getSegmentsFilteredDataCount()}
							itemsPerPage={pageSize}
							onPageChange={handleSegmentsPageChange}
							// Sorting
							sortBy={segmentsSortBy}
							sortOrder={segmentsSortOrder}
							onSortChange={handleSegmentsSortChange}
							// View mode
							viewMode={segmentsViewMode}
							onViewModeChange={handleSegmentsViewModeChange}
						/>
					)}

					{/* ✅ FIXED: Simplified AudienceResultsList integration */}
					{activeTab === "audience" && (
						<AudienceResultsList
							// ✅ FIXED: Data directly from combined hook
							data={audienceSearchResults?.audiences || []}
							selectedResults={audienceSelectedResults}
							onResultSelect={handleAudienceResultSelect}
							currentPage={audienceCurrentPage}
							totalPages={audienceTotalPages}
							totalItems={audienceTotalItems}
							itemsPerPage={pageSize}
							onPageChange={handleAudiencePageChange}
							onPageSizeChange={handlePageSizeChange}
							onSelectAll={handleAudienceSelectAll}
							isAllSelected={
								audienceSelectedResults.length ===
									getAudienceCurrentPageData().length &&
								getAudienceCurrentPageData().length > 0
							}
							onExport={exportAudienceToCSV}
							isExporting={isExporting}
							searchTerm={audienceSearchTerm}
							onSearchChange={handleAudienceSearchChange}
							// ✅ FIXED: State from combined hook
							isLoading={audienceIsSearching}
							error={audienceSearchError}
							isPaginatedData={false}
							onExcludeSelected={handleAudienceExcludeSelected}
							excludedItems={excludedItems}
							includeTerms={audienceSearchTerms}
							excludeTerms={audienceExcludeTerms}
							onOpenAudienceModal={handleOpenAudienceModal}
						/>
					)}

					{activeTab === "packages" && (
						<PackageResultsList
							results={getPackageCurrentPageData()}
							currentPage={packageCurrentPage}
							totalPages={getPackageTotalPages()}
							totalItems={getPackageFilteredDataCount()}
							itemsPerPage={pageSize}
							onPageChange={handlePackagePageChange}
							onPageSizeChange={handlePageSizeChange}
							selectedResults={packageSelectedResults}
							onResultSelect={handlePackageResultSelect}
							onSelectAll={handlePackageSelectAll}
							isAllSelected={
								packageSelectedResults.length ===
									getPackageCurrentPageData().length &&
								getPackageCurrentPageData().length > 0
							}
							searchTerm={packageSearchQuery}
							onSearchChange={handlePackageSearchInputChange}
							error={packageSearchError}
							hasResults={packageHasResults}
							resultsCount={packageResultsCount}
							isExporting={isExporting}
							onExport={exportPackagesToCSV}
							sortColumn={packageSortColumn}
							sortDirection={packageSortDirection}
							onSortChange={handlePackageSortChange}
							curationFilter={packageCurationFilter}
							onCurationFilterChange={handlePackageCurationFilterChange}
							packageCounts={getPackageCurationCounts()}
							excludedPackageIds={excludedPackageIds}
						/>
					)}
					{activeTab === "category" && (
						<CategoryResultsList
							results={categorySearch.paginatedResults}
							isLoading={categorySearch.isSearching}
							error={categorySearch.error}
							hasSearched={categorySearch.hasSearched}
							subTab={categorySearch.subTab}
							keywords={categorySearch.keywords}
							currentPage={categorySearch.currentPage}
							totalPages={categorySearch.totalPages}
							totalRecords={categorySearch.totalRecords}
							onPageChange={categorySearch.setCurrentPage}
						/>
					)}
				</div>
			</div>

			{/* Toggle Filters Button - Shows when filters are hidden */}
			{shouldShowFilters() && ToggleFiltersButton && (
				<ToggleFiltersButton
					showFilters={showFilters}
					onToggle={() => setShowFilters(true)}
				/>
			)}

			{/* Upload Modal */}
			{UploadModal && (
				<UploadModal
					show={showUploadModal}
					uploadedDomains={uploadedDomains}
					isUploading={isUploading}
					onClose={() => setShowUploadModal(false)}
					onFileUpload={handleFileUpload}
					onProcessUpload={processUploadedDomains}
				/>
			)}

			{/* ✅ ADD AUDIENCE DETAIL MODAL HERE */}
			{activeTab === "audience" && (
				<AudienceDetailModal
					audience={selectedAudienceForModal}
					isOpen={isAudienceModalOpen}
					onClose={handleCloseAudienceModal}
				/>
			)}

			{/* Inline Styles */}
			<style jsx>{`
                .app-error {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 100vh;
                    padding: 20px;
                    text-align: center;
                    background: #f8fafc;
                }

                .app-error h1 {
                    color: #dc2626;
                    margin-bottom: 16px;
                }

                .app-error p {
                    color: #64748b;
                    margin-bottom: 24px;
                    max-width: 400px;
                }

                .app-error button {
                    padding: 12px 24px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 16px;
                    font-weight: 500;
                    transition: background-color 0.2s;
                }

                .app-error button:hover {
                    background: #2563eb;
                }

                .search-summary-panel {
                    margin: 20px 0;
                    padding: 16px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                }
                
                .search-summary-panel h3 {
                    margin: 0 0 12px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #1a202c;
                }
                
                .summary-stats {
                    display: flex;
                    gap: 24px;
                    flex-wrap: wrap;
                }
                
                .stat {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                
                .stat .label {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 500;
                }
                
                .stat .value {
                    font-size: 18px;
                    color: #1a202c;
                    font-weight: 600;
                }
                
                .main-search-interface {
                    margin-bottom: 20px;
                }

                .main-layout {
                    display: flex;
                    min-height: calc(100vh - 60px);
                }

                .main-content {
                    flex: 1;
                    padding: 20px;
                    overflow-x: auto;
                }

                .main-content.full-width {
                    width: 100%;
                }

                .results-header-enhancements {
                    margin-bottom: 16px;
                    padding: 12px;
                    background: #f0f9ff;
                    border: 1px solid #0ea5e9;
                    border-radius: 8px;
                }

                .llm-enhancement-badge {
                    display: inline-block;
                    padding: 4px 8px;
                    background: #0ea5e9;
                    color: white;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                    margin-bottom: 8px;
                }

                .llm-query-info {
                    font-size: 14px;
                    color: #0369a1;
                }
                
                @media (max-width: 768px) {
                    .summary-stats {
                        flex-direction: column;
                        gap: 12px;
                    }

                    .main-layout {
                        flex-direction: column;
                    }

                    .main-content {
                        padding: 12px;
                    }
                }
            `}</style>
		</div>
	);
};

export default DomainSearchApp;
